"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AdaptableNoCodeWizard = exports.Adaptable = void 0;
const tslib_1 = require("tslib");
const core_1 = require("@ag-grid-community/core");
const merge_1 = tslib_1.__importDefault(require("lodash/merge"));
const debounce_1 = tslib_1.__importDefault(require("lodash/debounce"));
const isEqual_1 = tslib_1.__importDefault(require("lodash/isEqual"));
const throttle_1 = tslib_1.__importDefault(require("lodash/throttle"));
const uniqBy_1 = tslib_1.__importDefault(require("lodash/uniqBy"));
const React = tslib_1.__importStar(require("react"));
const clamp_1 = tslib_1.__importDefault(require("lodash/clamp"));
const AdaptableApiImpl_1 = require("../Api/Implementation/AdaptableApiImpl");
const Enums_1 = require("../PredefinedConfig/Common/Enums");
const Uuid_1 = require("../PredefinedConfig/Uuid");
const AdaptableStore_1 = require("../Redux/Store/AdaptableStore");
const GeneralConstants = tslib_1.__importStar(require("../Utilities/Constants/GeneralConstants"));
const GeneralConstants_1 = require("../Utilities/Constants/GeneralConstants");
const ModuleConstants = tslib_1.__importStar(require("../Utilities/Constants/ModuleConstants"));
const ModuleConstants_1 = require("../Utilities/Constants/ModuleConstants");
const DefaultAdaptableOptions_1 = require("../Utilities/Defaults/DefaultAdaptableOptions");
const Emitter_1 = tslib_1.__importDefault(require("../Utilities/Emitter"));
const ArrayExtensions_1 = require("../Utilities/Extensions/ArrayExtensions");
const StringExtensions_1 = require("../Utilities/Extensions/StringExtensions");
const getScrollbarSize_1 = tslib_1.__importDefault(require("../Utilities/getScrollbarSize"));
const AdaptableHelper_1 = tslib_1.__importDefault(require("../Utilities/Helpers/AdaptableHelper"));
const Helper_1 = require("../Utilities/Helpers/Helper");
const ObjectFactory_1 = require("../Utilities/ObjectFactory");
// services
const CalculatedColumnExpressionService_1 = require("../Utilities/Services/CalculatedColumnExpressionService");
const DataService_1 = require("../Utilities/Services/DataService");
const LicenseService_1 = require("../Utilities/Services/LicenseService");
const TeamSharingService_1 = require("../Utilities/Services/TeamSharingService");
const ReportService_1 = require("../Utilities/Services/ReportService");
const ModuleService_1 = require("../Utilities/Services/ModuleService");
const ValidationService_1 = require("../Utilities/Services/ValidationService");
const EntitlementService_1 = require("../Utilities/Services/EntitlementService");
const QueryLanguageService_1 = require("../Utilities/Services/QueryLanguageService");
const AlertService_1 = require("../Utilities/Services/AlertService");
const AdaptableView_1 = require("../View/AdaptableView");
const AdaptableWizardView_1 = tslib_1.__importDefault(require("../View/AdaptableWizardView"));
const AdaptableToolPanel_1 = require("../View/Components/ToolPanel/AdaptableToolPanel");
const UIHelper_1 = tslib_1.__importDefault(require("../View/UIHelper"));
const ActionColumnRenderer_1 = require("./ActionColumnRenderer");
const agGridHelper_1 = require("./agGridHelper");
const agGridMenuHelper_1 = require("./agGridMenuHelper");
// components
const FilterWrapper_1 = require("./FilterWrapper");
const FloatingFilterWrapper_1 = require("./FloatingFilterWrapper");
const StyleHelper_1 = require("../Utilities/Helpers/StyleHelper");
const AdaptableNumberEditor_1 = require("./editors/AdaptableNumberEditor");
const AdaptableDateEditor_1 = require("./editors/AdaptableDateEditor");
const waitForCondition_1 = require("../Utilities/waitForCondition");
const publishTimestamp_1 = tslib_1.__importDefault(require("../../publishTimestamp"));
const MetamodelService_1 = require("../Utilities/Services/MetamodelService");
const DocumentationLinkConstants_1 = require("../Utilities/Constants/DocumentationLinkConstants");
const DateHelper_1 = require("../Utilities/Helpers/DateHelper");
const StatusBarState_1 = require("../PredefinedConfig/StatusBarState");
const createAgStatusPanelComponent_1 = require("./createAgStatusPanelComponent");
const AdaptableStatusBar_1 = require("../View/StatusBar/AdaptableStatusBar");
const RowEditService_1 = require("../Utilities/Services/RowEditService");
const weightedAverage_1 = require("./weightedAverage");
const AggregationColumns_1 = require("../PredefinedConfig/Common/AggregationColumns");
const renderReactRoot_1 = require("../renderReactRoot");
const ChartingService_1 = require("../Utilities/Services/ChartingService");
const ThemeService_1 = require("../Utilities/Services/ThemeService");
const AdaptableLogger_1 = require("./AdaptableLogger");
const attachAddaptableColumnTypes_1 = require("./attachAddaptableColumnTypes");
const FormatHelper_1 = require("../Utilities/Helpers/FormatHelper");
const Fdc3Service_1 = require("../Utilities/Services/Fdc3Service");
const tinycolor = require('tinycolor2');
const GROUP_PATH_SEPARATOR = '/';
// IMPORTANT - we need colId to be set in order for safeSetColDefs to work correctly
const assignColId = (colDef, logger) => {
    if (!colDef) {
        return;
    }
    if (colDef.field && !colDef.colId) {
        colDef.colId = colDef.field;
    }
    if (colDef.children) {
        colDef.children.forEach((colDef) => assignColId(colDef, logger));
    }
    else {
        if (!colDef.colId) {
            logger.warn('A column is missing the colId - please check ', colDef, 'Either pass a "field" property or a "colId" property.');
        }
    }
};
const RowNodeProto = core_1.RowNode.prototype;
const RowNode_dispatchLocalEvent = RowNodeProto.dispatchLocalEvent;
const GridApi_setColumnDefs = core_1.GridApi.prototype.setColumnDefs;
['addCssClass', 'removeCssClass', 'containsCssClass', 'addOrRemoveCssClass'].forEach((methodName) => {
    // @ts-ignore
    const CssClassManager_originalMethod = core_1.CssClassManager.prototype[methodName];
    // @ts-ignore
    core_1.CssClassManager.prototype[methodName] = function () {
        return this.getGui() ? CssClassManager_originalMethod.apply(this, arguments) : undefined;
    };
});
/**
 * AgGrid does not expose Events.EVENT_ROW_DATA_CHANGED
 * so we have to override `dispatchLocalEvent`
 * and hook our own functionality into it
 */
RowNodeProto.dispatchLocalEvent = function (event) {
    const node = event.node;
    const result = RowNode_dispatchLocalEvent.apply(this, arguments);
    const extractGridApiFromRowNode = (rowNode) => {
        var _a;
        // starting with AG Grid 26.1.0 the gridApi is wrapped in a Beans property
        const rowNodeApi = (_a = rowNode === null || rowNode === void 0 ? void 0 : rowNode.beans) === null || _a === void 0 ? void 0 : _a.gridApi;
        if (!rowNodeApi) {
            AdaptableLogger_1.AdaptableLogger.consoleErrorBase(`No GridAPI found in passed RowNode, this should never happen!`, rowNode);
        }
        return rowNodeApi;
    };
    // we don't know from which instance of aggrid this is coming,
    // as this fn is shared by all instances
    if (node) {
        Adaptable.forEachAdaptable((adaptable) => {
            if (extractGridApiFromRowNode(node) !== adaptable.gridOptions.api) {
                // the event is coming from another aggrid instance
                // so IGNORE IT
                return;
            }
            // we're on the correct instance, so do this
            //@ts-ignore
            const fn = adaptable.rowListeners ? adaptable.rowListeners[event.type] : null;
            if (fn) {
                fn(event);
            }
        });
    }
    return result;
};
/**
 * Since column definitions can be nested and have groups
 * we use this forEachColumn function to call the passed-in `fn`
 * which will be called with just ColDef (not ColGroupDef), so just column definitions, not group definitions
 */
const forEachColumn = (cols, fn, parentColGroup) => {
    cols.forEach((col, i, arr) => {
        if (col.children) {
            forEachColumn(col.children, fn, col);
        }
        else {
            fn(col, i, arr, parentColGroup);
        }
    });
};
const adaptableInstances = {};
class Adaptable {
    get isLive() {
        return this.isInitialised && !this.isDestroyed;
    }
    /**
     * Initializer for Adaptable
     *
     * @param adaptableOptions the AdaptableOptions
     * @param agGridConfig AG Grid
     * @returns a Promise containing the AdaptableAPI object in order to enable run-time access to Adaptable's properties and functions
     */
    static init(adaptableOptions, agGridConfig) {
        var _a;
        if (!((_a = agGridConfig === null || agGridConfig === void 0 ? void 0 : agGridConfig.modules) === null || _a === void 0 ? void 0 : _a.length)) {
            AdaptableLogger_1.AdaptableLogger.consoleErrorBase(`No AG Grid modules were provided, thus limiting AdapTables features significantly!\n\nRead further info here:${DocumentationLinkConstants_1.AgGridModulesDocsLink}`);
        }
        return Adaptable.initInternal(adaptableOptions, Object.assign({}, agGridConfig));
    }
    /**
     * Lazy static constructor for Adaptable
     * Receives an AdaptableOptions object
     * Returns a Promise containing the AdaptableAPI object in order to enable run-time access to Adaptable's properties and functions
     * @param adaptableOptions an instance of AdaptableOptions
     */
    static async initInternal(adaptableOptions, runtimeConfig) {
        let promise = null;
        if (Array.isArray(adaptableOptions.plugins)) {
            const agGridOptions = {
                gridOptions: runtimeConfig.gridOptions,
                modules: runtimeConfig.modules,
            };
            // do this
            for (let plugin of adaptableOptions.plugins) {
                promise =
                    promise && promise.then
                        ? promise.then(() => {
                            return plugin.beforeInit(adaptableOptions, agGridOptions);
                        })
                        : plugin.beforeInit(adaptableOptions, agGridOptions);
            }
            // if gridOptions changed, we need to update the runtimeConfig
            if (agGridOptions.gridOptions !== runtimeConfig.gridOptions) {
                // This allows plugins to modify
                runtimeConfig.gridOptions = agGridOptions.gridOptions;
            }
            // instead of
            // if (Array.isArray(adaptableOptions.plugins)) {
            //   for await (let plugin of adaptableOptions.plugins) {
            //     await plugin.beforeInit(adaptableOptions);
            //   }
            // }
            // because this await for of introduces too much delay
        }
        const doInit = (ab) => {
            return ab.init(adaptableOptions, runtimeConfig, true).then((api) => {
                if (Array.isArray(adaptableOptions.plugins)) {
                    adaptableOptions.plugins.forEach((plugin) => {
                        plugin.afterInit(ab);
                    });
                }
                return api;
            });
        };
        if (promise && promise.then) {
            return promise.then(() => {
                const ab = new Adaptable();
                return doInit(ab);
            });
        }
        else {
            const ab = new Adaptable();
            return doInit(ab);
        }
    }
    static collectInstance(adaptable) {
        adaptable._id = adaptable.adaptableOptions.adaptableId || (0, Uuid_1.createUuid)();
        adaptableInstances[adaptable._id] = adaptable;
    }
    static forEachAdaptable(fn) {
        Object.keys(adaptableInstances).forEach((key) => {
            fn(adaptableInstances[key]);
        });
    }
    static dismissInstance(adaptable) {
        delete adaptableInstances[adaptable._id];
    }
    constructor() {
        this.variant = 'vanilla';
        this.colDefPropertyCache = new Map();
        this.gridOptionsPropertyCache = new Map();
        this.columnMinMaxValuesCache = {};
        this.isCheckedColumnDataType = false;
        // only for our private / internal events used within Adaptable
        // public events are emitted through the EventApi
        this._on = (eventName, callback) => {
            if (!this.emitter) {
                return () => { };
            }
            return this.emitter.on(eventName, callback);
        };
        this._onIncludeFired = (eventName, callback) => {
            if (!this.emitter) {
                return () => { };
            }
            return this.emitter.onIncludeFired(eventName, callback);
        };
        this._emit = (eventName, data) => {
            if (this.emitter) {
                return this.emitter.emit(eventName, data);
            }
        };
        this._emitSync = (eventName, data) => {
            if (this.emitter) {
                return this.emitter.emitSync(eventName, data);
            }
        };
        this._adaptableReady = false;
        this.isDestroyed = false;
        this.renderReactRoot = (node, container) => (0, renderReactRoot_1.renderReactRoot)(node, container);
        this.isPluginLoaded = (pluginId) => {
            const plugins = this.adaptableOptions.plugins || [];
            for (let i = 0, len = plugins.length; i < len; i++) {
                const plugin = plugins[i];
                if (plugin.pluginId === pluginId) {
                    return true;
                }
            }
            return false;
        };
        this.isAgGridReady = () => {
            var _a, _b;
            const waitTimeout = (_b = (_a = this.adaptableOptions.containerOptions) === null || _a === void 0 ? void 0 : _a.agGridContainerWaitTimeout) !== null && _b !== void 0 ? _b : 60000;
            return (0, waitForCondition_1.waitForCondition)(() => !!this.gridOptions.api, waitTimeout);
        };
        // debounced methods
        this.debouncedSetColumnIntoStore = (0, debounce_1.default)(() => {
            if (this.isDestroyed || !this.gridOptions.api) {
                return;
            }
            this.updateColumnsIntoStore();
        }, GeneralConstants_1.HALF_SECOND);
        this.debouncedSaveGridLayout = (0, debounce_1.default)(() => {
            if (this.isDestroyed || !this.gridOptions.api) {
                return;
            }
            this.updateLayoutFromGrid();
        }, GeneralConstants_1.HALF_SECOND);
        this.debouncedSetSelectedCells = (0, debounce_1.default)(() => {
            if (this.isDestroyed || !this.gridOptions.api) {
                return;
            }
            this.setSelectedCells();
        }, 250);
        this.debouncedSetSelectedRows = (0, debounce_1.default)(() => {
            if (this.isDestroyed || !this.gridOptions.api) {
                return;
            }
            this.setSelectedRows();
        }, GeneralConstants_1.HALF_SECOND);
        this.debouncedFilterGrid = (0, debounce_1.default)((callback = () => { }) => {
            if (this.isDestroyed || !this.gridOptions.api) {
                return;
            }
            this.applyColumnFiltering();
            if (callback) {
                callback();
            }
        }, GeneralConstants_1.HALF_SECOND);
        /**
         * This creates a clone of the current column definitions. If config.removeEmpty is true, will also remove empty column groups
         *
         */
        this.mapColumnDefs = (colDefs, fn, config) => {
            config = config || { removeEmpty: false };
            colDefs = colDefs || this.getGroupAndColumnDefs();
            forEachColumn(colDefs, (columnDef, i, colDefs, parentColGroup) => {
                const result = fn(columnDef, i, colDefs);
                const parentArray = parentColGroup ? parentColGroup.children : colDefs;
                parentArray[i] = result;
                if (i === colDefs.length - 1 && config.removeEmpty) {
                    if (parentColGroup) {
                        parentColGroup.children = parentArray.filter((x) => !!x);
                    }
                }
            });
            if (config.removeEmpty) {
                colDefs = colDefs.filter((x) => !!x);
            }
            return colDefs;
        };
        this.getGroupAndColumnDefs = () => {
            const colDefs = [].concat(this.currentColumnDefs || this.gridOptions.columnDefs);
            return colDefs;
        };
        this.getColumnDefsWithCorrectVisibility = () => {
            const visibleColIds = this.api.layoutApi.getCurrentVisibleColumnIdsMap();
            return this.gridOptions.columnApi.getColumns().map((c) => {
                const colDef = c.getColDef();
                const colId = c.getColId();
                colDef.colId = colId;
                colDef.hide = !visibleColIds[colId];
                return colDef;
            });
        };
        // (global as any).adaptable = this;
    }
    // the 'old' constructor which takes an Adaptable adaptable object
    // this is still used internally but should not be used externally as a preference
    async init(adaptableOptions, runtimeConfig, _staticInit) {
        this.logger = new AdaptableLogger_1.AdaptableLogger(adaptableOptions.adaptableId || String(Date.now()));
        const perfAdaptableInit = this.logger.beginPerf(`Adaptable init()`);
        if (runtimeConfig.variant) {
            this.variant = runtimeConfig.variant;
        }
        if (!_staticInit) {
            this.logger.warn(`
!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
!!!!!!! You should not use the "Adaptable" constructor directly, as it was deprecated in v6 and removed in v7!
!!!!!!!
!!!!!!! Use const api = await Adaptable.init(adaptableOptions) instead.
!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!`);
        }
        this.emitter = new Emitter_1.default();
        this.runtimeConfig = runtimeConfig;
        this.gridOptions = this.runtimeConfig.gridOptions;
        this._rawAdaptableOptions = adaptableOptions;
        // we create AdaptableOptions by merging the values provided by the user with the defaults (where no value has been set)
        this.adaptableOptions = AdaptableHelper_1.default.assignAdaptableOptions(adaptableOptions);
        if (this.hasAutogeneratedPrimaryKey()) {
            this.logger
                .warn(`Autogenerated primary key (adaptableOptions.autogeneratedPrimaryKey = TRUE) should be used only as a last resort,
      when no unique column is available, as it limits some Adaptable functionalities!

      For more details see: ${DocumentationLinkConstants_1.PrimaryKeyDocsLink}`);
            this.adaptableOptions.primaryKey = GeneralConstants_1.AUTOGENERATED_PK_COLUMN;
        }
        Adaptable.collectInstance(this);
        if (this.gridOptions.allowContextMenuWithControlKey === undefined) {
            this.gridOptions.allowContextMenuWithControlKey = true;
        }
        this.isInitialised = false;
        this.useRowNodeLookUp = false; // we will set later in instantiate if possible to be true
        this.forPlugins((plugin) => plugin.afterInitOptions(this, this.adaptableOptions));
        this.registerAdaptableAggFuncs();
        // get the api ready
        this.api = new AdaptableApiImpl_1.AdaptableApiImpl(this);
        this.forPlugins((plugin) => plugin.afterInitApi(this, this.api));
        // data source needs to be created early
        this.DataService = new DataService_1.DataService(this);
        // set up the ag Grid helper and menu helper
        this.agGridHelper = new agGridHelper_1.agGridHelper(this, this.gridOptions);
        this.agGridMenuHelper = new agGridMenuHelper_1.agGridMenuHelper(this, this.gridOptions);
        // Build the default group sort comparator - will get custom sort values (but not functions) in real time
        // TODO: if  a custom 'aggFunc' property is defined (see setupColumnAggFunc()), it won't be evaluated
        if (this.adaptableOptions.groupingOptions.autoOrderGroupedColumns) {
            this.setGridOptionsProperty('initialGroupOrderComparator', (userPropertyValue) => {
                if (userPropertyValue) {
                    return userPropertyValue;
                }
                return this.agGridHelper.runAdaptableGroupComparerFunction();
            });
        }
        // we prefer the grid to be NOT instantiated so that we can do it
        // perhaps in future we will force instantiation only?
        // create the store
        this.initStore();
        // create the services
        this.LicenseService = this.initLicenseService();
        this.ChartingService = new ChartingService_1.ChartingService(this.api);
        this.ThemeService = new ThemeService_1.ThemeService(this.api);
        this.ValidationService = new ValidationService_1.ValidationService(this.api);
        this.ReportService = new ReportService_1.ReportService(this.api);
        this.ModuleService = new ModuleService_1.ModuleService(this.api);
        this.CalculatedColumnExpressionService = new CalculatedColumnExpressionService_1.CalculatedColumnExpressionService(this.api);
        this.EntitlementService = new EntitlementService_1.EntitlementService(this.api);
        this.QueryLanguageService = new QueryLanguageService_1.QueryLanguageService(this.api);
        this.AlertService = new AlertService_1.AlertService(this.api);
        this.TeamSharingService = new TeamSharingService_1.TeamSharingService(this.api);
        this.Fdc3Service = new Fdc3Service_1.Fdc3Service(this.api);
        // @ts-ignore
        this.RowEditService = new RowEditService_1.RowEditService(this.api);
        this.MetamodelService = new MetamodelService_1.MetamodelService(() => this.api.optionsApi.getAdaptableOptions());
        // validate the provided AdaptableOptions
        this.MetamodelService.validateAdaptableOptionsValues();
        this.forPlugins((plugin) => plugin.afterInitServices(this));
        // Set up Modules - we set up all the Modules suitable for AG Grid
        // But users can make some hidden or readonly in their entitlements
        this.adaptableModules = this.agGridHelper.setUpModules();
        this.forPlugins((plugin) => plugin.afterInitModules(this, this.adaptableModules));
        // also try to set it early, here, if the grid is initialized - needed for AdapTable tool panel
        if (this.gridOptions.api) {
            this.gridOptions.api.__adaptable = this;
        }
        this.gridOptions.columnTypes = this.gridOptions.columnTypes || {};
        (0, attachAddaptableColumnTypes_1.attachColumnTypes)(this.gridOptions);
        if (this.gridOptions.columnTypes.abColDefNumber.cellEditor === undefined) {
            this.gridOptions.columnTypes.abColDefNumber.cellEditor =
                this.variant === 'react' ? AdaptableNumberEditor_1.ReactAdaptableNumberEditor : AdaptableNumberEditor_1.AdaptableNumberEditor;
        }
        if (this.gridOptions.columnTypes.abColDefDate.cellEditor === undefined) {
            this.gridOptions.columnTypes.abColDefDate.cellEditor =
                this.variant === 'react' ? AdaptableDateEditor_1.ReactAdaptableDateEditor : AdaptableDateEditor_1.AdaptableDateEditor;
        }
        return this.initializeAgGrid().then((initialized) => {
            if (!initialized) {
                // we have no grid, we can't do anything
                this.logger.error('Unable to set up AG Grid');
                return this.api;
            }
            this.logger.info(`Registered AG Grid modules: `, this.getAgGridRegisteredModuleNames().sort());
            let self = this;
            // add our adaptable object to the grid options api object
            // this is VERY useful for when we need to access Adaptable inside of agGrid only functions
            if (this.gridOptions.api) {
                this.gridOptions.api.__adaptable = this;
                // we need to monkey patch the grid api
                // we only do this for this instance, not on the prototype
                this.gridOptions.api.setColumnDefs = function (columnDefs) {
                    self.currentColumnDefs = [].concat(columnDefs);
                    const result = GridApi_setColumnDefs.apply(this, arguments);
                    self.setupColumns();
                    return result;
                };
            }
            // Load the store
            const perfAdaptableStoreLoad = this.logger.beginPerf('AdaptableStore.Load');
            this.adaptableStore.Load.then(() => {
                if (this.isDestroyed) {
                    return;
                }
                perfAdaptableStoreLoad.end();
                const perfInitInternalGridLogic = this.logger.beginPerf('InitInternalGridLogic');
                this.initInternalGridLogic();
                perfInitInternalGridLogic.end();
                AdaptableHelper_1.default.checkValidPrimaryKey(this);
            }, (e) => {
                this.logger.error('Failed to Init Modules : ', e);
                // for now we initialize the grid even if initialising Modules has failed (perhaps revisit this?)
                this.initInternalGridLogic();
                this.api.userInterfaceApi.hideLoadingScreen(); // doesnt really help but at least clears the screen
            }).then(async () => {
                this.api.userInterfaceApi.hideLoadingScreen();
                this.isInitialised = true;
                this._adaptableReady = true;
            });
            if (this.abContainerElement == null) {
                this.abContainerElement = this.getAdaptableContainerElement();
            }
            if (this.abContainerElement != null) {
                this.abContainerElement.innerHTML = '';
                this.unmountReactRoot = this.renderReactRoot((0, AdaptableView_1.AdaptableApp)({ Adaptable: this }), this.abContainerElement);
            }
            // create debounce methods that take a time based on user settings
            this.throttleFilterOnEditDataChange = (0, throttle_1.default)(
            // the extra function is to make sure we have a reference to ag-grid-api
            () => this.gridOptions.api.onFilterChanged(), this.adaptableOptions.columnFilterOptions.filterActionOnUserDataChange.throttleDelay, {
                trailing: true,
                leading: false,
            });
            this.throttleFilterOnTickingDataChange = (0, throttle_1.default)(() => this.gridOptions.api.onFilterChanged(), this.adaptableOptions.columnFilterOptions.filterActionOnExternalDataChange.throttleDelay, {
                trailing: true,
                leading: false,
            });
            perfAdaptableInit.end();
            return this.api;
        });
    }
    forPlugins(callback) {
        if (Array.isArray(this.adaptableOptions.plugins)) {
            this.adaptableOptions.plugins.forEach((plugin) => {
                callback(plugin);
            });
        }
    }
    getPlugin(pluginId) {
        const plugins = this.adaptableOptions.plugins || [];
        const thePlugin = plugins.filter((p) => p.pluginId === pluginId)[0];
        if (!thePlugin) {
            throw `Cannot find plugin "${pluginId}". Make sure you spelled it right!`;
        }
        return thePlugin;
    }
    getPluginProperty(pluginId, propertyName, ...args) {
        const plugins = this.adaptableOptions.plugins || [];
        const thePlugin = plugins.filter((p) => p.pluginId === pluginId)[0];
        if (!thePlugin) {
            throw `Cannot find plugin "${pluginId}". Make sure you spelled it right!`;
        }
        if (thePlugin && thePlugin.hasProperty(propertyName)) {
            return thePlugin.getProperty(propertyName)(...args);
        }
    }
    lookupPlugins(propertyName, ...args) {
        const plugins = this.adaptableOptions.plugins || [];
        for (let i = 0, len = plugins.length; i < len; i++) {
            const plugin = plugins[i];
            if (plugin.hasProperty(propertyName)) {
                return plugin.getProperty(propertyName)(...args);
            }
        }
        return undefined;
    }
    initStore() {
        const perfNewAdaptableStore = this.logger.beginPerf(`new AdaptableStore()`);
        this.adaptableStore = new AdaptableStore_1.AdaptableStore(this, this.isAgGridReady());
        perfNewAdaptableStore.end();
        this.adaptableStore.onAny((eventName, data) => {
            this.performAudit(data.action, data.state, data.newState);
            this.forPlugins((plugin) => plugin.onStoreEvent(eventName, data, this.adaptableStore));
            if (eventName == AdaptableStore_1.INIT_STATE) {
                const perfInitState = this.logger.beginPerf('InitState');
                const perfUpdateOldConfig = this.logger.beginPerf('updateOldConfig()');
                this.adaptableModules.forEach((m) => m.updateOldConfig());
                perfUpdateOldConfig.end();
                // call stuff here
                // and reset state also?
                this.forPlugins((plugin) => plugin.onAdaptableReady(this, this.adaptableOptions));
                this._adaptableReady = true;
                // setTimeout(() => {
                this.logger.info('emit AdaptableReady');
                this.api.eventApi.emit('AdaptableReady', {
                    adaptableApi: this.api,
                    gridOptions: this.gridOptions,
                });
                // }, 0);
                perfInitState.end();
            }
        });
    }
    performAudit(action, oldState, newState) {
        if (this.isLive) {
            const adaptableStateChangedInfo = {
                adaptableApi: this.api,
                actionName: action.type,
                clientTimestamp: new Date(),
                userName: this.adaptableOptions.userName,
                adaptableId: this.adaptableOptions.adaptableId,
                adaptableStateKey: this.adaptableOptions.adaptableStateKey,
                action: action,
                oldState: oldState,
                newState: newState,
            };
            this.api.eventApi.emit('AdaptableStateChanged', adaptableStateChangedInfo);
        }
    }
    initializeAgGrid() {
        var _a;
        const perfInitializeAgGrid = this.logger.beginPerf('initializeAgGrid()');
        this.setGridOptionsProperty('dataTypeDefinitions', () => {
            // temporary workaround until https://github.com/AdaptableTools/adaptable/issues/2230
            const revertedDateTypeDefinitions = {
                date: {
                    baseDataType: 'date',
                    extendsDataType: 'date',
                    valueParser: null,
                    valueFormatter: null,
                    suppressDefaultProperties: true,
                },
                dateString: {
                    baseDataType: 'dateString',
                    extendsDataType: 'dateString',
                    valueParser: null,
                    valueFormatter: null,
                    suppressDefaultProperties: true,
                },
            };
            return revertedDateTypeDefinitions;
        });
        // set up whether we use the getRowNode method or loop when finding a rowNode (former is preferable)
        // can only do that here as the gridOptions not yet set up
        this.useRowNodeLookUp = this.agGridHelper.TrySetUpNodeIds(this.hasAutogeneratedPrimaryKey());
        if (Array.isArray(this.gridOptions.columnDefs)) {
            this.gridOptions.columnDefs.forEach((colDef) => assignColId(colDef, this.logger));
        }
        const adaptableSideBarDef = this.createAdaptableSideBarDef();
        if (!!adaptableSideBarDef) {
            this.gridOptions.components = this.gridOptions.components || {};
            this.gridOptions.components.AdaptableToolPanel = (0, AdaptableToolPanel_1.getAdaptableToolPanelAgGridComponent)(this);
        }
        if (this.agGridHelper.isSideBarDefObject(adaptableSideBarDef)) {
            this.setGridOptionsProperty('sideBar', () => {
                return adaptableSideBarDef;
            });
            this.hasAdaptableToolPanel = true;
        }
        this.createStatusBars();
        const checkAgGridContainer = () => {
            let agGridContainer = this.getAgGridContainerElement();
            if (!agGridContainer) {
                agGridContainer = this.initAgGridContainerFromInitializedAgGrid();
                if (!agGridContainer) {
                    this.logger.consoleError('You must provide an element id in `containerOptions.agGridContainer`');
                    return false;
                }
            }
            return true;
        };
        const isGridInstantiated = this.gridOptions.api && typeof this.gridOptions.api.getValue === 'function';
        if (isGridInstantiated) {
            if (!checkAgGridContainer()) {
                perfInitializeAgGrid.end('isGridInstantiated=TRUE, checkAgGridContainer=FALSE');
                return Promise.resolve(false);
            }
            perfInitializeAgGrid.end('isGridInstantiated=TRUE, checkAgGridContainer=TRUE');
            return Promise.resolve(true);
        }
        if (this.runtimeConfig.waitForAgGrid) {
            return this.isAgGridReady().then(() => {
                const perfInitAgGridContainer = this.logger.beginPerf('initAgGridContainerFromInitializedAgGrid');
                this.initAgGridContainerFromInitializedAgGrid();
                perfInitAgGridContainer.end();
                perfInitializeAgGrid.end('waitForAgGrid=TRUE');
                return Promise.resolve(true);
            }, () => {
                this.logger.consoleError(`Could not find any AG Grid instance rendered. Could not find reference to AG Grid api. Make sure you pass the same gridOptions object reference both to Adaptable and AG Grid`);
                perfInitializeAgGrid.end('waitForAgGrid=TRUE, ERROR!');
                return Promise.reject(false);
            });
        }
        else {
            if (!checkAgGridContainer()) {
                perfInitializeAgGrid.end('waitForAgGrid=FALSE, checkAgGridContainer=FALSE!');
                return Promise.resolve(false);
            }
        }
        // now create the grid itself - it is done this way as previously when we instantiated the Grid 'properly' it got created as J.Grid
        let grid;
        const agGridContainer = this.getAgGridContainerElement();
        const perfAgGridInstance = this.logger.beginPerf('new AgGrid()');
        let gridParams;
        if ((_a = this.runtimeConfig.modules) === null || _a === void 0 ? void 0 : _a.length) {
            gridParams = { modules: this.runtimeConfig.modules };
        }
        grid = new core_1.Grid(agGridContainer, this.gridOptions, gridParams);
        perfAgGridInstance.end();
        // add our adaptable object to the grid options api object
        // this is VERY useful for when we need to access Adaptable inside of agGrid only functions
        if (this.gridOptions.api) {
            this.gridOptions.api.__adaptable = this;
        }
        perfInitializeAgGrid.end('AG Grid instantiation');
        return Promise.resolve(grid != null);
    }
    initAgGridContainerFromInitializedAgGrid() {
        if (this.getAgGridContainerElement()) {
            return;
        }
        const layoutElements = this.gridOptions.api
            ? this.gridOptions.api.gridOptionsWrapper
                ? this.gridOptions.api.gridOptionsWrapper.layoutElements || []
                : []
            : [];
        // aggrid 25.3.0 doesn't have layoutElements anymore, so we try to retrieve this element
        if (!layoutElements.length) {
            const getGridBodyElement = (gridApi) => {
                // @ts-ignore ctrlsService is private
                const gridBodyCtrl = gridApi.ctrlsService.getGridBodyCtrl();
                return gridBodyCtrl.getGridBodyElement();
            };
            layoutElements.push(getGridBodyElement(this.gridOptions.api));
        }
        let agGridContainer;
        for (let i = 0, len = layoutElements.length; i < len; i++) {
            const element = layoutElements[i];
            if (element && element.matches('.ag-root-wrapper, .ag-root')) {
                const gridContainer = element.closest('[class*="ag-theme"]');
                if (gridContainer) {
                    agGridContainer = gridContainer;
                    break;
                }
            }
        }
        if (!agGridContainer) {
            this.logger.consoleError(`Could not find the AG Grid Container. This will probably break some AdapTable functionality.`);
        }
        return (this.adaptableOptions.containerOptions.agGridContainer = agGridContainer);
    }
    createAdaptableSideBarDef() {
        var _a;
        if (!this.gridOptions.sideBar) {
            // no need to create the sidebar if it is not enabled
            return;
        }
        const adaptableToolBarHidden = this.EntitlementService.isModuleHiddenEntitlement('ToolPanel');
        if (this.gridOptions.sideBar === true) {
            // create all tool panels with default settings
            const toolPanels = [];
            toolPanels.push(GeneralConstants.AGGRID_TOOLPANEL_FILTERS);
            toolPanels.push(GeneralConstants.AGGRID_TOOLPANEL_COLUMNS);
            if (!adaptableToolBarHidden) {
                toolPanels.push(this.agGridHelper.buildAdaptableToolPanelDef());
            }
            return {
                toolPanels: toolPanels,
            };
        }
        else if (typeof this.gridOptions.sideBar === 'string') {
            // there is only one tool panel, and it's the adaptable one => we have to handle it
            if (this.gridOptions.sideBar === GeneralConstants.ADAPTABLE_TOOLPANEL_ID) {
                if (!adaptableToolBarHidden)
                    return {
                        toolPanels: [this.agGridHelper.buildAdaptableToolPanelDef()],
                    };
            }
        }
        else if (Array.isArray(this.gridOptions.sideBar)) {
            if (!this.gridOptions.sideBar.includes(GeneralConstants.ADAPTABLE_TOOLPANEL_ID) ||
                adaptableToolBarHidden) {
                return;
            }
            // if it's an array, process the tool panel definitions
            const sidebarDef = {};
            sidebarDef.toolPanels = this.agGridHelper.mapToolPanelDefs(this.gridOptions.sideBar);
            return sidebarDef;
        }
        else if (this.agGridHelper.isSideBarDefObject(this.gridOptions.sideBar)) {
            if ((_a = this.gridOptions.sideBar.toolPanels) === null || _a === void 0 ? void 0 : _a.some((toolpanelDef) => typeof toolpanelDef !== 'string' &&
                toolpanelDef.id === GeneralConstants.ADAPTABLE_TOOLPANEL_ID &&
                !adaptableToolBarHidden)) {
                // return the boolean flag so that we know that we need the custom AdaptableToolPanel component
                return true;
            }
            // if it's fully-fledged SideBarDef, process its tool panel definitions
            return Object.assign(Object.assign({}, this.gridOptions.sideBar), { toolPanels: this.agGridHelper.mapToolPanelDefs(this.gridOptions.sideBar.toolPanels) });
        }
    }
    createStatusBars() {
        const adaptableStatusPanelKeys = [];
        this.setGridOptionsProperty('statusBar', (originalStatusBar) => {
            var _a, _b;
            const statusPanels = (_b = ((_a = originalStatusBar === null || originalStatusBar === void 0 ? void 0 : originalStatusBar.statusPanels) !== null && _a !== void 0 ? _a : [])) === null || _b === void 0 ? void 0 : _b.map((statusPanel) => {
                if (statusPanel.statusPanel === StatusBarState_1.ADAPTABLE_STATUS_PANEL) {
                    adaptableStatusPanelKeys.push(statusPanel.key);
                    const context = {
                        Key: statusPanel.key,
                    };
                    return Object.assign(Object.assign({}, statusPanel), { statusPanel: (0, createAgStatusPanelComponent_1.createAgStatusPanelComponent)(AdaptableStatusBar_1.AdaptableStatusBar, this, context) });
                }
                return statusPanel;
            });
            return Object.assign(Object.assign({}, originalStatusBar), { statusPanels });
        });
        const statusBarModule = this.ModuleService.getModuleById(ModuleConstants.StatusBarModuleId);
        this.api.eventApi.on('AdaptableReady', () => {
            var _a, _b;
            const adaptableStatusPanels = (_b = (_a = this.gridOptions.statusBar) === null || _a === void 0 ? void 0 : _a.statusPanels) === null || _b === void 0 ? void 0 : _b.filter((statusPanel) => adaptableStatusPanelKeys.includes(statusPanel.key));
            // need to add only the adaptable panels
            statusBarModule.syncStateWithOptions(adaptableStatusPanels);
        });
    }
    getAgGridStatusPanels() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.gridOptions) === null || _a === void 0 ? void 0 : _a.statusBar) === null || _b === void 0 ? void 0 : _b.statusPanels) !== null && _c !== void 0 ? _c : [];
    }
    filterOnEditDataChange() {
        if (this.adaptableOptions.columnFilterOptions.filterActionOnUserDataChange.applyFilter ==
            Enums_1.FilterOnDataChangeOptions.Always) {
            this.gridOptions.api.onFilterChanged();
        }
        else if (this.adaptableOptions.columnFilterOptions.filterActionOnUserDataChange.applyFilter ==
            Enums_1.FilterOnDataChangeOptions.Throttle) {
            this.throttleFilterOnEditDataChange();
        }
    }
    filterOnTickingDataChange() {
        if (this.adaptableOptions.columnFilterOptions.filterActionOnExternalDataChange.applyFilter ==
            Enums_1.FilterOnDataChangeOptions.Always) {
            this.gridOptions.api.onFilterChanged();
        }
        else if (this.adaptableOptions.columnFilterOptions.filterActionOnExternalDataChange.applyFilter ==
            Enums_1.FilterOnDataChangeOptions.Throttle) {
            this.throttleFilterOnTickingDataChange();
        }
    }
    applyColumnFiltering() {
        if (this.api.columnFilterApi.isQuickFilterAvailable()) {
            if (this.api.columnFilterApi.isQuickFilterVisible()) {
                this.showQuickFilter();
            }
            else {
                this.hideQuickFilter();
            }
        }
        this.applyGridFiltering();
        this.updateColumnFilterActiveState();
    }
    applyGridFiltering() {
        this.gridOptions.api.onFilterChanged();
        this._emit('AdapTableFiltersApplied');
        this.setSelectedCells();
        this.setSelectedRows();
    }
    isGroupRowNode(rowNode) {
        if (!rowNode) {
            return false;
        }
        if (rowNode.isEmptyRowGroupNode()) {
            return true;
        }
        if (rowNode.group && rowNode.group === true) {
            return true;
        }
        if (rowNode.leafGroup && rowNode.leafGroup === true) {
            return true;
        }
        return false;
    }
    isVisibleNode(rowNode) {
        const foundNode = this.gridOptions.api
            .getRenderedNodes()
            .find((n) => n.id == rowNode.id);
        return foundNode != null;
    }
    isPinnedRowNode(rowNode) {
        if (!rowNode) {
            return false;
        }
        if (rowNode.isRowPinned()) {
            return true;
        }
        return false;
    }
    clearColumnFiltering() {
        this.gridOptions.columnApi.getColumns().forEach((c) => {
            this.gridOptions.api.destroyFilter(c);
        });
    }
    clearColumnFilteringForColumns(columnIds) {
        columnIds.forEach((c) => {
            const column = this.gridOptions.columnApi
                .getColumns()
                .find((col) => col.getColId() == c);
            if (column) {
                this.gridOptions.api.destroyFilter(column);
            }
        });
    }
    hideColumnFilterForm() {
        if (this.hideFilterFormPopup) {
            this.hideFilterFormPopup();
        }
    }
    updateColumnsIntoStore() {
        // if pivoting and we have 'special' columns as a result then do nothing ...
        // if (this.gridOptions.columnApi!.isPivotMode()) {
        //   if (ArrayExtensions.IsNotNullOrEmpty(this.gridOptions.columnApi!.getPivotColumns())) {
        //     return;
        //   }
        // }
        const allColumns = [];
        const agGridCols = this.gridOptions.columnApi.getColumns();
        const columnGroupChildren = this.gridOptions.columnApi.getAllDisplayedColumnGroups();
        const groupsCount = {};
        const colsToGroups = columnGroupChildren.reduce((acc, columnGroup) => {
            var _a, _b, _c;
            if (!((_b = (_a = columnGroup.getProvidedColumnGroup) === null || _a === void 0 ? void 0 : _a.call(columnGroup)) === null || _b === void 0 ? void 0 : _b.getColGroupDef())) {
                return acc;
            }
            const ColumnGroupId = columnGroup.getGroupId();
            const AllowGroupSplit = !columnGroup.getProvidedColumnGroup().getColGroupDef().marryChildren;
            const FriendlyName = (_c = columnGroup.getProvidedColumnGroup().getColGroupDef().headerName) !== null && _c !== void 0 ? _c : ColumnGroupId;
            const columnsInGroup = columnGroup.getLeafColumns();
            columnsInGroup.forEach((col) => {
                const group = {
                    columnGroupId: ColumnGroupId,
                    friendlyName: FriendlyName,
                    allowGroupSplit: AllowGroupSplit,
                    groupCount: 0,
                };
                groupsCount[group.columnGroupId] = groupsCount[group.columnGroupId] || 0;
                groupsCount[group.columnGroupId] += columnsInGroup.length;
                acc[col.getColId()] = group;
            });
            return acc;
        }, {});
        Object.keys(colsToGroups).forEach((colId) => {
            colsToGroups[colId].groupCount = groupsCount[colsToGroups[colId].columnGroupId];
        });
        // TODO sort the visible columns by layout order
        agGridCols.forEach((agGridColumn) => {
            const colId = agGridColumn.getColId();
            if (!this.api.columnApi.isAutoRowGroupColumn(colId)) {
                allColumns.push(this.createAdaptableColumn(agGridColumn, colsToGroups));
            }
        });
        this.api.gridApi.internalApi.setColumns(allColumns);
    }
    createAdaptableColumn(agGridColumn, colsToGroups) {
        const abColumn = this.agGridHelper.createAdaptableColumnFromAgGridColumn(agGridColumn, colsToGroups);
        return abColumn;
    }
    safeSetColDefs(colDefs) {
        // we need to do this because when doing gridOptions.api.setColumnDefs
        // internally aggrid checks for column equality, and uses colId for that
        // and since part of the column we set are basically the same old columns
        // those that dont have a colId are detected as new columns, but since the old colIds/field
        // are already in an aggrid cache, new ones with _1 are used, so basically all columns
        // end up as new columns - really weird
        colDefs = this.mapColumnDefs(colDefs, (colDef) => {
            if (colDef.field && colDef.colId == null) {
                colDef.colId = colDef.field;
            }
            return colDef;
        });
        this.gridOptions.api.setColumnDefs(colDefs);
        this.updateColumnsIntoStore(); // todo remove this from here!!!
    }
    /**
     * The combination of styled column and format cells
     * This functiond decides when the two can be merged.
     */
    getFormatColumnAndStyledColumnCellStyle(gridCell, params) {
        let styledColumn = this.api.styledColumnApi.getStyledColumnForColumnId(gridCell.column.columnId);
        let styledColumnStyle = {};
        if (styledColumn && !(styledColumn === null || styledColumn === void 0 ? void 0 : styledColumn.IsSuspended)) {
            const styledCellStyle = this.getStyledColumnStyle(styledColumn, gridCell.column, params);
            // for percentbar we want to merge
            if (styledColumn.PercentBarStyle || styledColumn.BadgeStyle) {
                styledColumnStyle = styledCellStyle;
            }
            else {
                // For other ones wo do not want to merge
                return styledCellStyle;
            }
        }
        const activeFormatColumnsWithStyle = this.api.formatColumnApi.internalApi.getFormatColumnsWithStyleForColumn(gridCell.column);
        return Object.assign(Object.assign({}, this.getFormatColumnCellStyle(gridCell.column, activeFormatColumnsWithStyle, params)), styledColumnStyle);
    }
    getStyledColumnStyle(styledColumn, abColumn, params) {
        let style = {};
        const gradientStyle = styledColumn === null || styledColumn === void 0 ? void 0 : styledColumn.GradientStyle;
        if (params.value === undefined) {
            return;
        }
        let colValue = params.value;
        if (this.api.gridApi.isGroupRowNode(params.node)) {
            if (styledColumn.IncludeGroupedRows) {
                const minColumnValue = this.api.styledColumnApi.internalApi.getMinValueForNumericColumn(abColumn);
                const maxColumnValue = this.api.styledColumnApi.internalApi.getMaxValueForNumericColumn(abColumn);
                /**
                 * Color should always be in bounds, it should not overflow.
                 * If the value is out of range, it shoul set the maximum/minimum color.
                 */
                colValue = (0, clamp_1.default)(params.value, minColumnValue, maxColumnValue);
            }
            else {
                return style;
            }
        }
        if (gradientStyle) {
            const min = this.api.styledColumnApi.internalApi.getNumericStyleMinValue(styledColumn, abColumn, params.node, colValue);
            const max = this.api.styledColumnApi.internalApi.getNumericStyleMaxValue(styledColumn, abColumn, params.node, colValue);
            let cellBackColor;
            let reverseGradient = false;
            if (gradientStyle.ColumnComparison) {
                cellBackColor = gradientStyle.ColumnComparison.Color;
            }
            else {
                const matchingRange = this.api.styledColumnApi.internalApi.findRangeForColumn(gradientStyle.CellRanges, abColumn, gradientStyle.RangeValueType, colValue);
                if (matchingRange) {
                    cellBackColor = matchingRange.Color;
                    reverseGradient = matchingRange.ReverseGradient;
                }
            }
            const increase = Math.abs(max - min);
            const percentage = ((colValue - min) / increase) * 100;
            let alpha = Number((percentage / 100).toPrecision(2));
            if (reverseGradient) {
                alpha = 1 - alpha;
            }
            const preparedColor = (0, StyleHelper_1.getVariableColor)(cellBackColor);
            // if no range match, do not apply color (black in this case)
            if (cellBackColor) {
                style.backgroundColor = tinycolor(preparedColor).setAlpha(alpha).toRgbString();
            }
        }
        if (styledColumn.PercentBarStyle && styledColumn.PercentBarStyle.CellText) {
            style.paddingTop = 0;
            style.paddingBottom = 0;
        }
        return style;
    }
    getFormatColumnAdaptableStyle(formatColumns) {
        // first has more precedence, then they need to be applied in reverse order
        return formatColumns.reduceRight((style, formatColumn) => {
            const formatColumnStyle = formatColumn.Style
                ? this.convertAdaptableStyleToCSS(formatColumn.Style)
                : {};
            if (formatColumn.CellAlignment) {
                switch (formatColumn.CellAlignment) {
                    case 'Left':
                        style.textAlign = 'left';
                        break;
                    case 'Right':
                        style.textAlign = 'right';
                        break;
                    case 'Center':
                        style.textAlign = 'center';
                        break;
                }
            }
            return Object.assign(Object.assign({}, style), formatColumnStyle);
        }, {});
    }
    getFormatColumnCellStyle(abColumn, activeFormatColumnsWithStyle, params) {
        if (!activeFormatColumnsWithStyle.length) {
            return {};
        }
        const relevantFormatColumnsWithStyle = activeFormatColumnsWithStyle.filter((formatColumn) => {
            return this.api.formatColumnApi.internalApi.isFormatColumnRelevantForColumn(formatColumn, abColumn, params);
        });
        return this.getFormatColumnAdaptableStyle(relevantFormatColumnsWithStyle);
    }
    getFormatColumnCellClass(formatColumns, abColumn, params) {
        const classNames = formatColumns
            .map((formatColumn) => {
            var _a, _b;
            if (((_a = formatColumn.Style) === null || _a === void 0 ? void 0 : _a.ClassName) &&
                this.api.formatColumnApi.internalApi.isFormatColumnRelevantForColumn(formatColumn, abColumn, params)) {
                return (_b = formatColumn.Style) === null || _b === void 0 ? void 0 : _b.ClassName;
            }
        })
            .filter((x) => !!x);
        return classNames;
    }
    getActiveAlertWithHighlightCell(col, params) {
        return this.api.alertApi.internalApi.getAdaptableAlertWithHighlightCell(col.columnId, params.node);
    }
    getActiveAlertWithHighlightRow(params) {
        return this.api.alertApi.internalApi.getAdaptableAlertWithHighlightRow(params.node);
    }
    getAlertCellStyle(gridCell, params) {
        const alert = this.getActiveAlertWithHighlightCell(gridCell.column, params);
        if (alert) {
            const highlightCell = alert.alertDefinition.AlertProperties.HighlightCell;
            if (typeof highlightCell === 'object') {
                return this.convertAdaptableStyleToCSS(highlightCell);
            }
            return {
                backgroundColor: UIHelper_1.default.getColorByMessageType(alert.alertDefinition.MessageType),
            };
        }
    }
    getAlertCellClass(gridCell, params) {
        var _a, _b;
        const alert = this.getActiveAlertWithHighlightCell(gridCell.column, params);
        const highlightCell = (_b = (_a = alert === null || alert === void 0 ? void 0 : alert.alertDefinition) === null || _a === void 0 ? void 0 : _a.AlertProperties) === null || _b === void 0 ? void 0 : _b.HighlightCell;
        return typeof highlightCell === 'object' && (highlightCell === null || highlightCell === void 0 ? void 0 : highlightCell.ClassName)
            ? highlightCell === null || highlightCell === void 0 ? void 0 : highlightCell.ClassName
            : null;
    }
    getAlertRowClass(params) {
        var _a, _b;
        const alert = this.getActiveAlertWithHighlightRow(params);
        const highlightRow = (_b = (_a = alert === null || alert === void 0 ? void 0 : alert.alertDefinition) === null || _a === void 0 ? void 0 : _a.AlertProperties) === null || _b === void 0 ? void 0 : _b.HighlightRow;
        return typeof highlightRow === 'object' && (highlightRow === null || highlightRow === void 0 ? void 0 : highlightRow.ClassName)
            ? highlightRow === null || highlightRow === void 0 ? void 0 : highlightRow.ClassName
            : null;
    }
    getAlertRowStyle(params) {
        var _a, _b;
        const alert = this.getActiveAlertWithHighlightRow(params);
        const highlightRow = (_b = (_a = alert === null || alert === void 0 ? void 0 : alert.alertDefinition) === null || _a === void 0 ? void 0 : _a.AlertProperties) === null || _b === void 0 ? void 0 : _b.HighlightRow;
        if (highlightRow) {
            return typeof highlightRow === 'object'
                ? this.convertAdaptableStyleToCSS(highlightRow)
                : {
                    backgroundColor: UIHelper_1.default.getColorByMessageType(alert.alertDefinition.MessageType),
                };
        }
        return null;
    }
    getFlashingCellStyle(gridCell, params) {
        var _a;
        const primaryKey = params.node.aggData ? params.node.id : gridCell.primaryKeyValue;
        const flashingCell = this.api.flashingCellApi.internalApi.getAdaptableFlashingCellFor(primaryKey, gridCell.column.columnId);
        if (!flashingCell) {
            return {};
        }
        return this.convertAdaptableStyleToCSS((_a = (flashingCell.direction === 'up'
            ? flashingCell.flashingCellDefinition.UpChangeStyle
            : flashingCell.direction === 'down'
                ? flashingCell.flashingCellDefinition.DownChangeStyle
                : flashingCell.flashingCellDefinition.NeutralChangeStyle)) !== null && _a !== void 0 ? _a : {});
    }
    getNoteCellClassName(gridCell, params) {
        if (!this.api.internalApi.getModuleService().isModuleAvailable('Notes')) {
            return;
        }
        const cellPosition = {
            PrimaryKeyValue: gridCell.primaryKeyValue,
            ColumnId: gridCell.column.columnId,
        };
        const cellNotes = this.api.notesApi.getCellNotes(cellPosition);
        if (!(cellNotes === null || cellNotes === void 0 ? void 0 : cellNotes.length)) {
            return undefined;
        }
        return 'ab-Cell-Note';
    }
    getFlashingCellClass(gridcell, params) {
        var _a, _b, _c;
        const primaryKey = params.node.aggData ? params.node.id : gridcell.primaryKeyValue;
        const flashingCell = this.api.flashingCellApi.internalApi.getAdaptableFlashingCellFor(primaryKey, gridcell.column.columnId);
        if (!flashingCell) {
            return;
        }
        return flashingCell.direction === 'up'
            ? (_a = flashingCell.flashingCellDefinition.UpChangeStyle) === null || _a === void 0 ? void 0 : _a.ClassName
            : flashingCell.direction === 'down'
                ? (_b = flashingCell.flashingCellDefinition.DownChangeStyle) === null || _b === void 0 ? void 0 : _b.ClassName
                : flashingCell.direction === 'neutral'
                    ? (_c = flashingCell.flashingCellDefinition.NeutralChangeStyle) === null || _c === void 0 ? void 0 : _c.ClassName
                    : undefined;
    }
    getCellHighlightStyle(gridCell, params) {
        const cellHightlight = this.api.internalApi
            .getSystemState()
            .HighlightedCells.find((cellHighlightInfo) => {
            return (gridCell.column.columnId === cellHighlightInfo.columnId &&
                cellHighlightInfo.primaryKeyValue === gridCell.primaryKeyValue);
        });
        if (cellHightlight) {
            return this.convertAdaptableStyleToCSS(cellHightlight.highlightStyle);
        }
    }
    getRowHighlightStyle(params) {
        var _a;
        const primaryKeyValue = this.getPrimaryKeyValueFromRowNode(params.node);
        const highlightRow = (_a = this.api.internalApi
            .getSystemState()
            .HighlightedRows) === null || _a === void 0 ? void 0 : _a.find((highlightRow) => {
            return highlightRow.primaryKeyValue === primaryKeyValue;
        });
        if (highlightRow) {
            return this.convertAdaptableStyleToCSS(highlightRow.highlightStyle);
        }
    }
    getRowHighlightClass(params) {
        var _a;
        const primaryKeyValue = this.getPrimaryKeyValueFromRowNode(params.node);
        const highlightRow = (_a = this.api.internalApi
            .getSystemState()
            .HighlightedRows) === null || _a === void 0 ? void 0 : _a.find((highlightRow) => {
            return highlightRow.primaryKeyValue === primaryKeyValue;
        });
        return typeof (highlightRow === null || highlightRow === void 0 ? void 0 : highlightRow.highlightStyle) === 'object'
            ? highlightRow.highlightStyle.ClassName
            : null;
    }
    getQuickSearchCellStyle() {
        const quickSearchStyle = this.api.quickSearchApi.getQuickSearchStyle();
        if (!quickSearchStyle || StringExtensions_1.StringExtensions.IsNotNullOrEmpty(quickSearchStyle.ClassName)) {
            return undefined;
        }
        return this.convertAdaptableStyleToCSS(quickSearchStyle);
    }
    getEditableCellStyle(gridCell, params) {
        const editableCellStyle = this.api.userInterfaceApi.getEditableCellStyle();
        if (!editableCellStyle) {
            return undefined;
        }
        if (gridCell) {
            if (this.api.gridApi.isCellEditable(gridCell)) {
                return this.convertAdaptableStyleToCSS(editableCellStyle);
            }
        }
        return undefined;
    }
    getReadOnlyCellStyle(gridCell, params) {
        const editableCellStyle = this.api.userInterfaceApi.getReadOnlyCellStyle();
        if (!editableCellStyle) {
            return undefined;
        }
        if (gridCell) {
            if (!this.api.gridApi.isCellEditable(gridCell)) {
                return this.convertAdaptableStyleToCSS(editableCellStyle);
            }
        }
        return undefined;
    }
    getEditableCellClass(gridCell, params) {
        const editableCellStyle = this.api.userInterfaceApi.getEditableCellStyle();
        if (!(editableCellStyle === null || editableCellStyle === void 0 ? void 0 : editableCellStyle.ClassName)) {
            return null;
        }
        const isCellEditable = this.api.gridApi.isCellEditable(gridCell);
        return isCellEditable ? editableCellStyle.ClassName : null;
    }
    getReadonlyCellClass(gridCell, params) {
        const readonlyCellStyle = this.api.userInterfaceApi.getReadOnlyCellStyle();
        if (!(readonlyCellStyle === null || readonlyCellStyle === void 0 ? void 0 : readonlyCellStyle.ClassName)) {
            return null;
        }
        const isCellReadonly = !this.api.gridApi.isCellEditable(gridCell);
        return isCellReadonly ? readonlyCellStyle.ClassName : null;
    }
    isQuickSearchActive(gridCell, params) {
        let quickSearchValue = this.api.quickSearchApi.getQuickSearchValue();
        if (StringExtensions_1.StringExtensions.IsNullOrEmpty(quickSearchValue)) {
            return false;
        }
        if (gridCell.column.isExcludedFromQuickSearch) {
            return false;
        }
        if (!params.node) {
            return false;
        }
        if (!gridCell.isPivotCell &&
            !this.api.optionsApi.getQuickSearchOptions().runQuickSearchOnRowGroups &&
            params.node.group) {
            return false;
        }
        if (!this.api.optionsApi.getQuickSearchOptions().runQuickSearchOnPivotColumns &&
            gridCell.isPivotCell) {
            return false;
        }
        if (this.api.optionsApi.getQuickSearchOptions().runQuickSearchOnPivotColumns &&
            gridCell.isPivotCell) {
            gridCell.displayValue = params.value;
        }
        const ignoreCase = !this.adaptableOptions.quickSearchOptions.runQuickSearchWithCaseSensitivity;
        const displayValue = ignoreCase
            ? String(gridCell.displayValue).toLocaleLowerCase()
            : String(gridCell.displayValue);
        const i = ignoreCase ? String(quickSearchValue).toLocaleLowerCase() : String(quickSearchValue);
        const applyQuickSearchFunction = this.adaptableOptions.quickSearchOptions.runBespokeQuickSearch;
        if (applyQuickSearchFunction) {
            const quickSearchContext = Object.assign(Object.assign({}, (0, ObjectFactory_1.createBaseContext)(this.api)), { gridCell, quickSearchValue: quickSearchValue });
            return applyQuickSearchFunction(quickSearchContext);
        }
        return displayValue.indexOf(i) !== -1;
    }
    getPrimaryKeyValueFromRowNode(rowNode) {
        var _a;
        if (!rowNode) {
            return null;
        }
        let gridApi = this.getGridOptionsApi();
        let result;
        if (gridApi && !this.hasAutogeneratedPrimaryKey()) {
            result = gridApi.getValue(this.adaptableOptions.primaryKey, rowNode);
        }
        if ((result === null || result == undefined) && rowNode.data) {
            result = (_a = rowNode.data[this.adaptableOptions.primaryKey]) !== null && _a !== void 0 ? _a : null;
        }
        return result;
    }
    getSortedColumnStateForVisibleColumns(visibleColumnList, columnState) {
        columnState = columnState || this.gridOptions.columnApi.getColumnState();
        const NewVisibleColumnIdsMap = visibleColumnList.reduce((acc, colId, index) => {
            acc[colId] = index;
            return acc;
        }, {});
        const columnsStateIndexes = columnState.reduce((acc, colState, index) => {
            acc[colState.colId] = index;
            return acc;
        }, {});
        const newVisibleColumnsMap = visibleColumnList.reduce((acc, colId, index) => {
            acc[colId] = index;
            return acc;
        }, {});
        const result = [...columnState]
            .sort((colState1, colState2) => {
            const colId1 = colState1.colId;
            const colId2 = colState2.colId;
            const originalIndex1 = columnsStateIndexes[colId1];
            const originalIndex2 = columnsStateIndexes[colId2];
            const isRowGroup1 = this.api.columnApi.isAutoRowGroupColumn(colId1);
            const isRowGroup2 = this.api.columnApi.isAutoRowGroupColumn(colId2);
            if (isRowGroup1 && isRowGroup2) {
                return 1;
            }
            if (isRowGroup1) {
                return -1;
            }
            if (isRowGroup2) {
                return 1;
            }
            if (newVisibleColumnsMap[colId1] != null && newVisibleColumnsMap[colId2] == null) {
                return -1;
            }
            if (newVisibleColumnsMap[colId1] == null && newVisibleColumnsMap[colId2] != null) {
                return 1;
            }
            if (newVisibleColumnsMap[colId1] == null && newVisibleColumnsMap[colId2] == null) {
                return originalIndex1 - originalIndex2;
            }
            return newVisibleColumnsMap[colState1.colId] - newVisibleColumnsMap[colState2.colId];
        })
            .map((colState) => (Object.assign(Object.assign({}, colState), { hide: NewVisibleColumnIdsMap[colState.colId] == null })));
        return result;
    }
    setColumnOrder(VisibleColumnList) {
        const newColumnState = this.getSortedColumnStateForVisibleColumns(VisibleColumnList);
        this.gridOptions.columnApi.applyColumnState({
            state: newColumnState,
            applyOrder: true,
        });
        this.updateColumnsIntoStore();
    }
    persistLayout(layout) {
        if (this.api.layoutApi.shouldAutoSaveLayouts()) {
            this.api.layoutApi.createOrUpdateLayout(layout);
        }
        else {
            this.api.layoutApi.internalApi.updateCurrentDraftLayout(layout);
        }
    }
    setLayout(layout) {
        var _a, _b, _c, _d, _e, _f, _g;
        if (!layout) {
            layout = this.api.layoutApi.getCurrentLayout();
        }
        const perfSetLayout = this.logger.beginPerf(`setLayout(${layout.Name})`);
        layout.Columns = layout.Columns || [];
        const actionRowColumn = this.getColDefsForActionRowColumns()[0];
        if (actionRowColumn) {
            layout.Columns.push(actionRowColumn.colId);
            layout.PinnedColumnsMap = layout.PinnedColumnsMap || {};
            layout.PinnedColumnsMap[actionRowColumn.colId] = actionRowColumn.pinned;
        }
        const layoutColumnsMap = layout.Columns.reduce((acc, colId) => {
            acc[colId] = true;
            return acc;
        }, {});
        const columnsState = this.gridOptions.columnApi.getColumnState();
        const columnsStateIndexes = {};
        const columnsStateMap = columnsState.reduce((acc, colState, index) => {
            columnsStateIndexes[colState.colId] = index;
            acc[colState.colId] = colState;
            return acc;
        }, {});
        let sortIndex = 0;
        const sortModelMap = ((_a = layout.ColumnSorts) !== null && _a !== void 0 ? _a : []).reduce((acc, customSort) => {
            const colId = customSort.ColumnId;
            acc[colId] = {
                colId,
                sort: customSort.SortOrder === 'Asc' ? 'asc' : 'desc',
                sortIndex,
            };
            sortIndex++;
            return acc;
        }, {});
        const groupedColumnsIndexesMap = (layout.RowGroupedColumns || []).reduce((acc, colId, index) => {
            acc[colId] = index;
            return acc;
        }, {});
        let pivotedColumnsIndexesMap = {};
        const aggregationFunctionsColumnsMap = layout.AggregationColumns || {};
        const columnsToShow = !layout.EnablePivot
            ? layout.Columns
            : ((_b = this.gridOptions.columnApi.getPivotResultColumns()) === null || _b === void 0 ? void 0 : _b.map((column) => column.getColId())) ||
                [];
        let isChanged = false;
        const colsToAutoSize = {};
        let newColumnsState = this.getSortedColumnStateForVisibleColumns(columnsToShow, columnsState);
        newColumnsState = newColumnsState
            .map((colState) => {
            var _a, _b, _c, _d, _e;
            const { colId } = colState;
            const oldColState = columnsStateMap[colId];
            const hide = this.api.columnApi.isAutoPivotColumn(colId)
                ? colState.hide
                : !layoutColumnsMap[colId];
            const newColState = Object.assign(Object.assign({}, oldColState), { hide });
            if (layout.ColumnWidthMap && layout.ColumnWidthMap[colId] != null) {
                newColState.width = layout.ColumnWidthMap[colId];
            }
            else if (!hide) {
                // autosize only the columns which are part of the selected layout
                colsToAutoSize[colId] = true;
            }
            if (actionRowColumn && actionRowColumn.colId === colId) {
                newColState.width = actionRowColumn.width;
            }
            newColState.rowGroupIndex =
                groupedColumnsIndexesMap[colId] != null ? groupedColumnsIndexesMap[colId] : null;
            const previousRowGroup = newColState.rowGroup;
            newColState.rowGroup = newColState.rowGroupIndex != null;
            if (!previousRowGroup && newColState.rowGroup) {
                this.triggerSetupColumnKeyCreator(colId);
            }
            const normalizePinned = (pinnedValue) => {
                if (typeof pinnedValue === 'string') {
                    return pinnedValue;
                }
                return pinnedValue == true ? 'left' : !!pinnedValue;
            };
            const newValuePinned = normalizePinned(layout.PinnedColumnsMap ? layout.PinnedColumnsMap[colId] : false);
            const stateValuePinned = normalizePinned(newColState.pinned);
            if (newValuePinned !== stateValuePinned) {
                newColState.pinned = newValuePinned;
            }
            newColState.pivotIndex = null;
            if (pivotedColumnsIndexesMap[colId] != null) {
                newColState.pivotIndex = pivotedColumnsIndexesMap[colId];
            }
            newColState.aggFunc = null;
            if (aggregationFunctionsColumnsMap[colId] != null) {
                const colDef = (_b = (_a = this.gridOptions) === null || _a === void 0 ? void 0 : _a.api) === null || _b === void 0 ? void 0 : _b.getColumnDef(colId);
                let aggFunc = null;
                const aggFuncFromLayout = aggregationFunctionsColumnsMap[colId];
                const adaptableAggFunc = this.getActiveAdaptableAggFuncForCol(colId);
                if (aggFuncFromLayout === true) {
                    // if we have true, it means - take the default aggFunc from colDef
                    // NOTE: colState gives us the current aggFunc, which can be null,
                    // while the colDef gives us the initially configured aggFunc for that column
                    aggFunc =
                        (_e = (_d = (_c = colState.aggFunc) !== null && _c !== void 0 ? _c : colDef === null || colDef === void 0 ? void 0 : colDef.aggFunc) !== null && _d !== void 0 ? _d : 
                        // @ts-ignore available only wth ag-Grid v27.3.x
                        colDef === null || colDef === void 0 ? void 0 : colDef.defaultAggFunc) !== null && _e !== void 0 ? _e : 'sum';
                }
                else if (adaptableAggFunc && adaptableAggFunc.type === 'weightedAverage') {
                    aggFunc = AggregationColumns_1.WEIGHTED_AVERAGE_AGG_FN_NAME;
                }
                else if (typeof aggFuncFromLayout === 'string') {
                    aggFunc = aggFuncFromLayout;
                }
                newColState.aggFunc = aggFunc;
            }
            if (sortModelMap[colId]) {
                newColState.sort = sortModelMap[colId].sort;
                newColState.sortIndex = sortModelMap[colId].sortIndex;
            }
            else {
                newColState.sort = null;
                newColState.sortIndex = null;
            }
            isChanged = isChanged || !(0, isEqual_1.default)(newColState, oldColState);
            return newColState;
        })
            .filter((x) => !!x);
        if (!isChanged) {
            // order changed
            const toString = (c) => `${c.colId}-${c.rowGroupIndex}-${c.pivotIndex}-${c.aggFunc}-${c.pinned}-${c.width}-${c.hide}`;
            const oldColStateString = columnsState.map(toString).join(',');
            const newColStateString = newColumnsState.map(toString).join(',');
            isChanged = newColStateString != oldColStateString;
        }
        const pivoted = !!layout.EnablePivot;
        const shouldUpdatePivoted = this.gridOptions.columnApi.isPivotMode() !== pivoted;
        /**
         * Pivot columns are secondary columns that are created on the fly and base
         * their configuration on the main columns by copying that configuration.
         * Because they copy the colDefs they might have an old copy of the colldefs.
         * This is why when the layout is pivoted we need to set columns before pivoting is applied.
         * e.g. {
         *  field: 'pivot-1',
         *  pivotValueColumn: {
         *   colDef: {
         *     field: 'price',
         *     // this is a copy of the original column
         *   }
         * }
         *
         */
        if (shouldUpdatePivoted) {
            this.setupColumns();
        }
        isChanged = isChanged || shouldUpdatePivoted;
        let shouldUpdateHeaders = false;
        shouldUpdateHeaders = this.setupColumnHeaderAggregations(layout);
        // update the header name for all columns
        // there should be a simpler solution for this, once the Layout Management is refactored
        this.gridOptions.columnApi.getColumns().forEach((col) => {
            const colDef = col.getColDef();
            const colId = col.getColId();
            const abColumn = this.api.columnApi.getColumnWithColumnId(colId);
            const colSetupInfo = {
                col,
                colDef,
                colId,
                abColumn,
            };
            shouldUpdateHeaders = this.setupColumnHeader(colSetupInfo) || shouldUpdateHeaders;
        });
        isChanged = isChanged || shouldUpdateHeaders;
        if (isChanged) {
            // it's important we set pivot mode
            // before we set column state
            // as otherwise column order is not preserved properly when
            // going from pivoted to unpivoted layout
            if (shouldUpdatePivoted) {
                this.gridOptions.columnApi.setPivotMode(pivoted);
            }
            const perfApplyColumnState = this.logger.beginPerf('applyColumnState (layout.isChanged)');
            this.gridOptions.columnApi.applyColumnState({
                state: newColumnsState,
                applyOrder: true,
            });
            this.applyGroupColumnWidth(layout);
            perfApplyColumnState.end();
            this.api.gridApi.setColumnSorts(layout.ColumnSorts);
            this.gridOptions.columnApi.setPivotColumns(layout.PivotColumns || []);
            // aggrid 25.1.0 introduced a bug such that a layout that has a grouped column, if the column has enableRowGroup: true but not rowGroup: true
            // the group column is not possitioned correctly at the start of the layout
            // see the test in layout/layout-switch/"should be able to switch from grouped to non-grouped and back"
            // so we figured out the following line fixes the issue
            // this.gridOptions.api.setColumnDefs(this.gridOptions.api.getColumnDefs()); //TODO find a solution for weighted averages - column floating filters are not showing
            const perfSetColumnDefs = this.logger.beginPerf('GridApi_setColumnDefs');
            GridApi_setColumnDefs.call(this.gridOptions.api, this.gridOptions.api.getColumnDefs());
            perfSetColumnDefs.end();
            // these updates need to be at the end, the methods are based on the grid state/col defs
            // the layout needs to be applied for them to work
            this.updateRowGroupsExpandedState(layout);
        }
        const colsToAutoSizeArray = Object.keys(colsToAutoSize);
        if (pivoted && ((_d = (_c = this.adaptableOptions) === null || _c === void 0 ? void 0 : _c.layoutOptions) === null || _d === void 0 ? void 0 : _d.autoSizeColumnsInPivotLayout)) {
            // when a pivoted layout loads, autosize all cols
            requestAnimationFrame(() => {
                this.gridOptions.columnApi.autoSizeAllColumns();
            });
            //but if it's also the first time the grid is loading
            //it's not timely enough the above call, so we keep trying... I know it's ugly, we need to find a better way
            setTimeout(() => {
                var _a;
                (_a = this.gridOptions.columnApi) === null || _a === void 0 ? void 0 : _a.autoSizeAllColumns();
                setTimeout(() => {
                    var _a;
                    (_a = this.gridOptions.columnApi) === null || _a === void 0 ? void 0 : _a.autoSizeAllColumns();
                }, 200);
            }, 100);
        }
        else if (((_f = (_e = this.adaptableOptions) === null || _e === void 0 ? void 0 : _e.layoutOptions) === null || _f === void 0 ? void 0 : _f.autoSizeColumnsInLayout) &&
            colsToAutoSizeArray.length) {
            (_g = this.gridOptions.columnApi) === null || _g === void 0 ? void 0 : _g.autoSizeColumns(colsToAutoSizeArray);
        }
        this.forPlugins((plugin) => {
            if (plugin.afterSetLayout) {
                plugin.afterSetLayout(this, layout);
            }
        });
        perfSetLayout.end();
    }
    /**
     * Setting layout works by modifing the column state.
     * The column state is based on the current existing columns.
     * At this point the column groups do not exist, and the widths from layout are not applied.
     *
     * After the col sate is applied and the column groups are created we need
     * to apply the state again to update the widths of the group columns.
     */
    applyGroupColumnWidth(layout) {
        if (!layout.RowGroupedColumns || !layout.RowGroupedColumns.length) {
            return;
        }
        const groupColumnWithDifferentWidths = this.gridOptions
            .columnApi.getColumnState()
            .reduce((acc, col) => {
            var _a;
            if (this.api.columnApi.isAutoRowGroupColumn(col.colId)) {
                const widthInLayout = (_a = layout.ColumnWidthMap) === null || _a === void 0 ? void 0 : _a[col.colId];
                if (widthInLayout && widthInLayout !== col.width) {
                    acc.push(Object.assign(Object.assign({}, col), { width: widthInLayout }));
                }
            }
            return acc;
        }, []);
        if (groupColumnWithDifferentWidths.length === 0) {
            return;
        }
        this.gridOptions.columnApi.applyColumnState({
            state: groupColumnWithDifferentWidths,
        });
    }
    updateRowGroupsExpandedState(layout) {
        if (!layout) {
            layout = this.api.layoutApi.getCurrentLayout();
        }
        if (this.api.layoutApi.internalApi.areExpandedRowGroupsSavedInLayouts() &&
            ArrayExtensions_1.ArrayExtensions.IsNotNullOrEmpty(layout.ExpandedRowGroupValues)) {
            this.expandRowGroupsForValues(layout.ExpandedRowGroupValues);
        }
        if (this.adaptableOptions.layoutOptions.displayRowGroups === 'expanded') {
            this.expandAllRowGroups();
        }
    }
    /*
     * This is the opposite of setLayout
     */
    updateLayoutFromGrid() {
        var _a, _b;
        const currentLayout = this.api.layoutApi.getCurrentLayout();
        if (currentLayout.IsReadOnly) {
            // reaply the layout so the grid is reverted
            this.setLayout();
            return;
        }
        const layout = Object.assign({}, currentLayout);
        let columnOrder = [];
        const columnFlexes = {};
        const pinnedColumns = {};
        const columnSorts = [];
        const columnState = this.gridOptions.columnApi.getColumnState();
        let groupedColumns = [...new Array(columnState.length)];
        let pivotedColumns = [...new Array(columnState.length)];
        const pivotColumns = [];
        const aggregatedColumns = {};
        const columnWidths = columnState.reduce((acc, colState) => {
            const { colId } = colState;
            if (colState.sort && colState.sortIndex != null) {
                columnSorts.push({
                    ColumnId: colId,
                    SortOrder: colState.sort === 'asc' ? 'Asc' : 'Desc',
                    SortIndex: colState.sortIndex,
                });
            }
            if (colState.width != null) {
                acc[colId] = colState.width;
            }
            if (colState.flex != null) {
                columnFlexes[colId] = colState.flex;
            }
            if (colState.pinned === 'left') {
                pinnedColumns[colId] = 'left';
            }
            if (colState.pinned === 'right') {
                pinnedColumns[colId] = 'right';
            }
            if (!colState.hide) {
                columnOrder.push(colId);
            }
            if (colState.rowGroupIndex != null) {
                groupedColumns[colState.rowGroupIndex] = colId;
            }
            if (colState.pivotIndex != null) {
                pivotedColumns[colState.pivotIndex] = colId;
            }
            if (colState.aggFunc && typeof colState.aggFunc === 'string') {
                aggregatedColumns[colId] = colState.aggFunc;
            }
            return acc;
        }, {});
        columnSorts.sort((a, b) => a.SortIndex - b.SortIndex);
        this.gridOptions.columnApi.getPivotColumns().forEach((col) => {
            pivotColumns.push(col.getColId());
        });
        groupedColumns = groupedColumns.filter((x) => !!x);
        pivotedColumns = pivotedColumns.filter((x) => !!x);
        if ((_b = (_a = this.adaptableOptions) === null || _a === void 0 ? void 0 : _a.groupingOptions) === null || _b === void 0 ? void 0 : _b.restoreUngroupedColumns) {
            columnOrder = this.restoreUnGroupColumnOrder({
                columnOrder,
                newGroupColumns: groupedColumns,
            });
        }
        layout.ColumnWidthMap = columnWidths;
        //  layout.ColumnFlexMap = columnFlexes;
        layout.PinnedColumnsMap = pinnedColumns;
        layout.Columns = columnOrder;
        layout.ColumnSorts = columnSorts;
        layout.RowGroupedColumns = groupedColumns;
        if (Object.keys(aggregatedColumns).length) {
            // AG Grid aggregations are not 1-1 with adaptable column-aggregations
            layout.AggregationColumns = this.mapAggregationColumnsFromGrid(aggregatedColumns, currentLayout);
        }
        else {
            layout.AggregationColumns = {};
        }
        layout.EnablePivot = this.gridOptions.columnApi.isPivotMode();
        layout.PivotColumns = pivotColumns;
        if (
        // check first row node for presence of data without iterating over whole grid
        this.api.layoutApi.internalApi.areExpandedRowGroupsSavedInLayouts() &&
            this.api.gridApi.getFirstDisplayedRowNode()) {
            layout.ExpandedRowGroupValues = this.getExpandRowGroupsKeys();
        }
        const previousVisibleColumns = currentLayout.Columns;
        const newVisibleColumns = layout.Columns;
        // check if all new columns were previously visible
        const someColumnsAreNew = newVisibleColumns.some((colId) => !previousVisibleColumns.includes(colId));
        if (someColumnsAreNew) {
            this.setupColumns();
        }
        this.persistLayout(layout);
    }
    // This method returns selected cells ONLY (if selection mode is cells or multiple cells).
    // If the selection mode is row it will returns nothing - use the setSelectedRows() method
    setSelectedCells() {
        if (!this.isGridRangeSelectable()) {
            return undefined;
        }
        const selected = this.gridOptions.api.getCellRanges();
        const columns = [];
        const selectedCells = [];
        if (this.api.internalApi.isGridInPivotMode()) {
            return undefined;
        }
        if (selected) {
            // we iterate for each ranges
            selected.forEach((rangeSelection) => {
                let shouldIncludeRange = true;
                if (rangeSelection.startRow && rangeSelection.endRow) {
                    let isStartRowPin = rangeSelection.startRow.rowPinned != null;
                    let isEndRowPin = rangeSelection.endRow.rowPinned != null;
                    // Warn user if trying to select pinned rows
                    // If only selecting pinned rows then stop
                    if (isStartRowPin) {
                        if (isEndRowPin) {
                            shouldIncludeRange = false;
                        }
                        this.logger.consoleWarn('Cannot select pinned rows in AG Grid.');
                    }
                    if (shouldIncludeRange) {
                        const y1 = Math.min(rangeSelection.startRow.rowIndex, rangeSelection.endRow.rowIndex);
                        const y2 = Math.max(rangeSelection.startRow.rowIndex, rangeSelection.endRow.rowIndex);
                        for (const column of rangeSelection.columns) {
                            if (column != null) {
                                const colId = column.getColId();
                                const selectedColumn = this.api.columnApi.getColumnWithColumnId(colId);
                                if (selectedColumn &&
                                    columns.find((c) => c.columnId == selectedColumn.columnId) == null) {
                                    columns.push(selectedColumn);
                                }
                                for (let rowIndex = y1; rowIndex <= y2; rowIndex++) {
                                    const rowNode = this.gridOptions.api.getModel().getRow(rowIndex);
                                    // we used NOT to return grouped rows but I think that was wrong - if someone wants to return them then that is up to them...
                                    // we definitely dont return pinned rows as they cannot be selected
                                    if (rowNode && !this.isPinnedRowNode(rowNode)) {
                                        const selectedCell = this.getGridCellFromRowNode(rowNode, colId);
                                        selectedCells.push(selectedCell);
                                    }
                                }
                            }
                        }
                    }
                }
            });
        }
        const selectedCellInfo = {
            columns: columns,
            gridCells: selectedCells,
        };
        this.api.gridApi.internalApi.setSelectedCells(selectedCellInfo);
        this._emit('CellsSelected');
        this.agGridHelper.fireCellSelectionChangedEvent();
        return selectedCellInfo;
    }
    setSelectedRows() {
        if (!this.isGridSelectable()) {
            return undefined;
        }
        const nodes = this.gridOptions.api.getSelectedNodes();
        const selectedRows = [];
        if (this.gridOptions.columnApi.isPivotMode()) {
            //  dont perform row selection in pivot mode
            return undefined;
        }
        if (ArrayExtensions_1.ArrayExtensions.IsNotNullOrEmpty(nodes)) {
            nodes.forEach((node) => {
                const rowInfo = {
                    isMaster: !!(node.master != null && node.master == true),
                    isExpanded: !!(node.expanded != null && node.expanded == true),
                    isGroup: !!(node.group != null && node.group == true),
                    isSelected: true,
                    isDisplayed: node.displayed == true,
                    rowGroupLevel: node.level,
                };
                const gridRow = {
                    primaryKeyValue: this.getPrimaryKeyValueFromRowNode(node),
                    rowData: node.data,
                    rowNode: node,
                    rowInfo,
                };
                selectedRows.push(gridRow);
            });
        }
        const selectedRowInfo = { gridRows: selectedRows };
        this.api.gridApi.internalApi.setSelectedRows(selectedRowInfo);
        this.agGridHelper.fireRowSelectionChangedEvent();
        return selectedRowInfo;
    }
    setDataValue(value, column, primaryKeyValue, rowNode) {
        // note: because we use RowNode.setDataValue() this will cause Validation to fire
        // see https://www.ag-grid.com/javascript-data-grid/change-detection/#triggering-value-change-detection
        let newValue;
        let dataType = column.dataType;
        newValue = dataType == 'Number' ? Number(value) : value;
        if (dataType == undefined) {
            return; // no point continuing as probably a wrong  column
        }
        if (rowNode) {
            rowNode.setDataValue(column.columnId, newValue);
        }
        else {
            if (this.useRowNodeLookUp) {
                const rowNode = this.gridOptions.api.getRowNode(primaryKeyValue);
                if (rowNode != null) {
                    rowNode.setDataValue(column.columnId, newValue);
                }
            }
            else {
                let isUpdated = false;
                // prefer not to use this method but if we do then at least we can prevent further lookups once we find
                this.gridOptions.api.getModel().forEachNode((rowNode) => {
                    if (!isUpdated) {
                        if (primaryKeyValue == this.getPrimaryKeyValueFromRowNode(rowNode)) {
                            rowNode.setDataValue(column.columnId, newValue);
                            isUpdated = true;
                        }
                    }
                });
            }
        }
    }
    cancelEdit() {
        this.gridOptions.api.stopEditing(true);
    }
    isCellEditable(rowNode, column) {
        // it's safe to rely on the AG Grid implementation because we override the colDef.editable property, which is in this case the single source of truth
        return column === null || column === void 0 ? void 0 : column.isCellEditable(rowNode);
    }
    getGridCellsForColumn(columnId) {
        let returnValues = [];
        this.gridOptions.api.forEachNode((rowNode) => {
            const gridCell = this.getGridCellFromRowNode(rowNode, columnId);
            if (gridCell && gridCell.rawValue != undefined) {
                returnValues.push(gridCell);
            }
        });
        return returnValues;
    }
    getDistinctGridCellsForColumn(column, distinctValuesParams) {
        let gridCells = [];
        if (distinctValuesParams.visibleRowsOnly) {
            this.gridOptions.api.forEachNodeAfterFilter((rowNode) => {
                const gridCell = this.addDistinctColumnValue(rowNode, column.columnId);
                if (gridCell &&
                    gridCell.rawValue != undefined &&
                    gridCell.rowNode !== distinctValuesParams.skipRowNode) {
                    gridCells.push(gridCell);
                }
            });
        }
        else {
            this.gridOptions.api.forEachNode((rowNode) => {
                const gridCell = this.addDistinctColumnValue(rowNode, column.columnId);
                if (gridCell && gridCell.rowNode !== distinctValuesParams.skipRowNode) {
                    if (gridCell.rawValue == undefined &&
                        this.adaptableOptions.columnFilterOptions.valuesFilterOptions.includeBlankFilterValues) {
                        gridCell.rawValue = GeneralConstants_1.BLANK_DISTINCT_COLUMN_VALUE;
                        gridCell.displayValue = GeneralConstants_1.BLANK_DISTINCT_COLUMN_VALUE;
                        gridCell.normalisedValue = GeneralConstants_1.BLANK_DISTINCT_COLUMN_VALUE;
                    }
                    gridCells.push(gridCell);
                }
            });
        }
        return gridCells;
    }
    getUniqueGridCells(column, gridCells) {
        let uniqueVals = (0, uniqBy_1.default)(gridCells, (dataItem) => {
            const value = dataItem.rawValue;
            if (value instanceof Date) {
                return value.toISOString();
            }
            return value;
        });
        if (column.dataType == 'String' && this.api.predicateApi.useCaseSensitivity()) {
            uniqueVals = (0, uniqBy_1.default)(uniqueVals, (d) => d.displayValue.toLowerCase());
        }
        return uniqueVals.slice(0, this.adaptableOptions.columnFilterOptions.valuesFilterOptions.maxFilterValuesToDisplay);
    }
    getGridCellsForPermittedValues(column, distinctValuesParams) {
        var _a;
        let gridCells = [];
        const permittedValues = (_a = distinctValuesParams.permittedValues) !== null && _a !== void 0 ? _a : this.api.userInterfaceApi.getPermittedValuesForColumn(column);
        if (ArrayExtensions_1.ArrayExtensions.IsNotNullOrEmpty(permittedValues)) {
            gridCells = permittedValues.map((pv) => {
                return {
                    rawValue: pv,
                    displayValue: pv,
                    normalisedValue: pv,
                    columnId: column.columnId,
                    column: column,
                    rowNode: undefined,
                    isPivotCell: false,
                    isRowGroupCell: false,
                };
            });
            return gridCells;
        }
    }
    getDistinctValuesForColumn(column, distinctValuesParams) {
        let gridCells = this.getGridCellsForPermittedValues(column, distinctValuesParams);
        if (ArrayExtensions_1.ArrayExtensions.IsNullOrEmpty(gridCells)) {
            gridCells = this.getDistinctGridCellsForColumn(column, distinctValuesParams);
        }
        return this.getUniqueGridCells(column, gridCells);
    }
    async getDistinctFilterListValuesForColumn(column, filter, distinctValuesParams) {
        var _a;
        const filterPermittedValues = (_a = this.api.userInterfaceApi.getFilterPermittedValuesForColumn(column)) !== null && _a !== void 0 ? _a : {
            suppressFilterSearchBar: false,
            values: () => undefined,
        };
        let preparedPermittedValues = undefined;
        try {
            const filterPermittedValuesParams = {
                adaptableApi: this.api,
                userName: this.adaptableOptions.userName,
                adaptableId: this.adaptableOptions.adaptableId,
                column: column,
                searchFilter: filter,
            };
            preparedPermittedValues = await (filterPermittedValues === null || filterPermittedValues === void 0 ? void 0 : filterPermittedValues.values(filterPermittedValuesParams));
        }
        catch (error) {
            this.logger.consoleError(`Failed to load filter permitted values`, column, filter, error);
        }
        distinctValuesParams.permittedValues = preparedPermittedValues;
        return {
            suppressClientSideFilter: filterPermittedValues.suppressFilterSearchBar,
            gridCells: this.getDistinctValuesForColumn(column, distinctValuesParams),
        };
    }
    async getDistinctCustomSortValuesForColumn(column, distinctValuesParams) {
        const customSortPermittedValues = await this.api.userInterfaceApi.getCustomSortPermittedValuesForColumn(column);
        let preparedPermittedValues = undefined;
        try {
            const permittedValuesParams = {
                adaptableApi: this.api,
                userName: this.adaptableOptions.userName,
                adaptableId: this.adaptableOptions.adaptableId,
                column: column,
            };
            preparedPermittedValues = await (customSortPermittedValues === null || customSortPermittedValues === void 0 ? void 0 : customSortPermittedValues.values(permittedValuesParams));
        }
        catch (error) {
            this.logger.consoleError(`Failed to load custom sort permitted values`, column, error);
        }
        distinctValuesParams.permittedValues = preparedPermittedValues;
        return this.getDistinctValuesForColumn(column, distinctValuesParams);
    }
    async getDistinctBulkUpdateValuesForColumn(column, selectedGridCells, distinctValuesParams) {
        const bulkUpdatePermittedValues = await this.api.userInterfaceApi.getBulkUpdatePermittedValuesForColumn(column);
        let preparedPermittedValues = undefined;
        try {
            const bulkUpdatePermittedValuesParams = {
                adaptableApi: this.api,
                column: column,
                gridCells: selectedGridCells,
                userName: this.adaptableOptions.userName,
                adaptableId: this.adaptableOptions.adaptableId,
            };
            preparedPermittedValues = await (bulkUpdatePermittedValues === null || bulkUpdatePermittedValues === void 0 ? void 0 : bulkUpdatePermittedValues.values(bulkUpdatePermittedValuesParams));
        }
        catch (error) {
            this.logger.consoleError(`Failed to load bulk permitted values`, error, column, selectedGridCells);
        }
        distinctValuesParams.permittedValues = preparedPermittedValues;
        return this.getDistinctValuesForColumn(column, distinctValuesParams);
    }
    getColumnValueDisplayValuePairList(columnId, visibleRowsOnly, onlyIncludeIds) {
        const returnArray = [];
        let permittedMap = new Map();
        // check if there are permitted column values for that column
        // NB.  this currently contains a small bug as we dont check for visibility so if using permitted values then ALL are returned :(
        const abColumn = this.api.columnApi.getColumnWithColumnId(columnId);
        const permittedValuesForColumn = this.api.userInterfaceApi.getPermittedValuesForColumn(abColumn);
        if (ArrayExtensions_1.ArrayExtensions.IsNotNullOrEmpty(permittedValuesForColumn)) {
            permittedValuesForColumn.forEach((pv) => {
                permittedMap.set(pv, {
                    rawValue: pv,
                    displayValue: pv,
                    normalisedValue: pv,
                    column: abColumn,
                    rowNode: undefined,
                    isPivotCell: false,
                    isRowGroupCell: false,
                });
            });
        }
        else {
            permittedMap = null;
        }
        const eachFn = (rowNode, columnId) => {
            if (rowNode && !this.isGroupRowNode(rowNode)) {
                const gridCell = this.getGridCellFromRowNode(rowNode, columnId);
                let allowed = !permittedMap || permittedMap.has(gridCell.displayValue);
                if (allowed && onlyIncludeIds) {
                    let id = this.getPrimaryKeyValueFromRowNode(rowNode);
                    allowed = !!onlyIncludeIds[id];
                }
                if (allowed) {
                    returnArray.push(gridCell);
                }
            }
        };
        if (visibleRowsOnly) {
            this.gridOptions.api.forEachNodeAfterFilter((rowNode) => {
                eachFn(rowNode, columnId);
            });
        }
        else {
            this.gridOptions.api.forEachNode((rowNode) => {
                eachFn(rowNode, columnId);
            });
        }
        return returnArray.slice(0, this.adaptableOptions.columnFilterOptions.valuesFilterOptions.maxFilterValuesToDisplay);
    }
    addDistinctColumnValue(rowNode, columnId) {
        // we do not return the values of the aggregates when in grouping mode
        // otherwise they would appear in the filter dropdown etc....
        if (rowNode && !this.isGroupRowNode(rowNode)) {
            const returnValue = this.getGridCellFromRowNode(rowNode, columnId);
            if (Helper_1.Helper.objectExists(returnValue)) {
                return returnValue;
            }
        }
        else {
            return undefined;
        }
    }
    /**
     * Don't think that this is used - a good thing as its quite verbose
     */
    getDisplayValue(primaryKey, columnId) {
        let returnValue;
        if (this.useRowNodeLookUp) {
            const rowNode = this.getRowNodeForPrimaryKey(primaryKey);
            return this.getDisplayValueFromRowNode(rowNode, columnId);
        }
        else {
            let foundRow = false;
            this.gridOptions.api.getModel().forEachNode((rowNode) => {
                if (!foundRow) {
                    const primaryKeyValue = this.getPrimaryKeyValueFromRowNode(rowNode);
                    if (primaryKey == primaryKeyValue) {
                        returnValue = this.getDisplayValueFromRowNode(rowNode, columnId);
                    }
                    foundRow = true;
                }
            });
        }
        return returnValue;
    }
    getGridCellFromRowNode(rowNode, columnId) {
        if (rowNode == null) {
            return undefined;
        }
        const abColumn = this.api.columnApi.getColumnWithColumnId(columnId);
        const pkValue = this.getPrimaryKeyValueFromRowNode(rowNode);
        const rawValue = this.getRawValueFromRowNode(rowNode, columnId);
        const displayValue = this.getDisplayValueFromRawValue(rowNode, columnId, rawValue);
        const normalisedValue = this.getNormalisedValueFromRawValue(rawValue, abColumn);
        const isPivotCell = this.api.columnApi.isAutoPivotColumn(columnId);
        const isRowGroupCell = rowNode.group;
        return {
            rawValue: rawValue,
            displayValue: displayValue,
            normalisedValue: normalisedValue,
            column: abColumn,
            primaryKeyValue: pkValue,
            rowNode: rowNode,
            isPivotCell,
            isRowGroupCell,
        };
    }
    getDisplayValueFromRowNode(rowNode, columnId) {
        if (rowNode == null) {
            return undefined;
        }
        const rawValue = this.getRawValueFromRowNode(rowNode, columnId);
        return this.getDisplayValueFromRawValue(rowNode, columnId, rawValue);
    }
    getDisplayValueFromRawValue(rowNode, columnId, rawValue) {
        // no need for this case, it's handled below after the ActionColumn logic
        // const abColumn: AdaptableColumn = this.api.columnApi.getColumnWithColumnId(columnId);
        // const isCustomRenderedColumn = this.api.styledColumnApi.hasPercentBarStyle(columnId);
        // if (isCustomRenderedColumn) {
        //   const colDef = this.gridOptions.api!.getColumnDef(columnId);
        //   if (typeof colDef.valueFormatter == 'function') {
        //     return this.getFormattedValue(rowNode, rawValue, columnId, colDef, colDef.valueFormatter);
        //   }
        //   return this.agGridHelper.getCleanValue(rawValue);
        // }
        const isActionColumn = this.api.columnApi.isActionColumn(columnId);
        if (isActionColumn) {
            return this.agGridHelper.getCleanValue(rawValue);
        }
        const colDef = this.gridOptions.api.getColumnDef(columnId);
        if (colDef) {
            if (typeof colDef.valueFormatter == 'function') {
                const formattedValue = this.getFormattedValue(rowNode, rawValue, columnId, colDef, colDef.valueFormatter);
                // Never use cellRenderer:
                // - when a string, you never know if it is HTML or a normal string
                // - when an object, cannot be used
                // if (colDef.cellRenderer && !skipCellRenderers) {
                //   return this.agGridHelper.getRenderedValue(colDef, formattedValue);
                // }
                return formattedValue || '';
            }
            // if (colDef.cellRenderer && !skipCellRenderers) {
            // return this.agGridHelper.getRenderedValue(colDef, rawValue);
            // }
        }
        return this.agGridHelper.getCleanValue(rawValue);
    }
    getFormattedValue(rowNode, rawValue, columnId, colDef, formatterFn) {
        const column = this.gridOptions.columnApi
            .getColumns()
            .find((c) => c.getColId() == columnId);
        const params = {
            value: rawValue,
            node: rowNode,
            data: rowNode.data,
            colDef,
            column,
            api: this.gridOptions.api,
            columnApi: this.gridOptions.columnApi,
            context: this.gridOptions.context,
        };
        return formatterFn(params);
    }
    getNormalisedValueFromRawValue(rawValue, column) {
        if (!column) {
            return rawValue;
        }
        // prevents from null
        if (rawValue === undefined || rawValue === null) {
            return rawValue;
        }
        const dataType = column.dataType;
        if (dataType === 'String') {
            return typeof rawValue !== 'string' ? String(rawValue) : rawValue;
        }
        if (dataType === 'Number') {
            // empty string or space should not be converted to 0
            return typeof rawValue !== 'number' && StringExtensions_1.StringExtensions.IsNumeric(rawValue)
                ? Number(rawValue)
                : rawValue;
        }
        if (dataType === 'Boolean') {
            return typeof rawValue !== 'boolean' ? Boolean(rawValue) : rawValue;
        }
        if (dataType === 'Date') {
            return rawValue instanceof Date ? rawValue : (0, DateHelper_1.parseDateValue)(rawValue);
        }
        return rawValue;
    }
    getRawValueFromRowNode(rowNode, columnId) {
        if (rowNode == null) {
            return undefined;
        }
        return this.gridOptions.api.getValue(columnId, rowNode);
    }
    getDataRowFromRowNode(rowNode) {
        return rowNode != null && rowNode != undefined ? rowNode.data : undefined;
    }
    getRowNodeForPrimaryKey(primaryKeyValue) {
        if (this.useRowNodeLookUp) {
            return this.gridOptions.api.getRowNode(primaryKeyValue);
        }
        else {
            this.gridOptions.api.getModel().forEachNode((rowNode) => {
                if (primaryKeyValue == this.getPrimaryKeyValueFromRowNode(rowNode)) {
                    return rowNode;
                }
            });
        }
    }
    getRowNodeByIndex(index) {
        return this.gridOptions.api.getDisplayedRowAtIndex(index);
    }
    getRowNodesForPrimaryKeys(primaryKeyValues) {
        let rowNodes = [];
        if (this.useRowNodeLookUp) {
            primaryKeyValues.forEach((pkValue) => {
                const rowNode = this.gridOptions.api.getRowNode(pkValue);
                if (rowNode) {
                    rowNodes.push(rowNode);
                }
            });
        }
        else {
            primaryKeyValues.forEach((pkValue) => {
                let foundRow = false;
                this.gridOptions.api.getModel().forEachNode((rowNode) => {
                    if (!foundRow && pkValue == this.getPrimaryKeyValueFromRowNode(rowNode)) {
                        rowNodes.push(rowNode);
                        foundRow = true;
                    }
                });
            });
        }
        return rowNodes;
    }
    forAllRowNodesDo(func, config) {
        this.gridOptions.api.forEachNode((rowNode, rowIndex) => {
            const includeGroupRows = (config === null || config === void 0 ? void 0 : config.includeGroupRows) || !this.isGroupRowNode(rowNode);
            const filterFnFulfilled = !(config === null || config === void 0 ? void 0 : config.filterFn) || (config === null || config === void 0 ? void 0 : config.filterFn(rowNode));
            if (includeGroupRows && filterFnFulfilled) {
                func(rowNode, rowIndex);
            }
        });
    }
    forAllVisibleRowNodesDo(func, config) {
        if (this.getAgGridRowModelType() !== 'clientSide') {
            // only in client-side row model can we loop through filtered&sorted rows
            // see https://www.ag-grid.com/javascript-data-grid/accessing-data/#iterating-rows
            this.logger.warn('`forAllVisibleRowNodesDo()` is only supported in client-side row model. `forAllRowNodesDo` will be used instead.');
            return this.forAllRowNodesDo(func, config);
        }
        this.gridOptions.api.forEachNodeAfterFilterAndSort((rowNode, rowIndex) => {
            const includeGroupRows = (config === null || config === void 0 ? void 0 : config.includeGroupRows) || !this.isGroupRowNode(rowNode);
            const filterFnFulfilled = !(config === null || config === void 0 ? void 0 : config.filterFn) || (config === null || config === void 0 ? void 0 : config.filterFn(rowNode));
            if (includeGroupRows && filterFnFulfilled) {
                func(rowNode, rowIndex);
            }
        });
    }
    getVisibleRowNodes(config) {
        let rowNodes = [];
        this.forAllVisibleRowNodesDo((rowNode) => rowNodes.push(rowNode), config);
        return rowNodes;
    }
    getAllRowNodes(config) {
        let rowNodes = [];
        this.forAllRowNodesDo((rowNode) => rowNodes.push(rowNode), config);
        return rowNodes;
    }
    getGroupRowNodes(config) {
        return this.getAllRowNodes({
            includeGroupRows: true,
            filterFn: (rowNode) => this.isGroupRowNode(rowNode),
        });
    }
    getRowsInViewport() {
        return this.gridOptions.api.getRenderedNodes();
    }
    selectNodes(rowNodes, clearSelection) {
        if (ArrayExtensions_1.ArrayExtensions.IsNotNullOrEmpty(rowNodes)) {
            rowNodes.forEach((node) => this.selectNode(node, clearSelection));
        }
    }
    deSelectNodes(rowNodes, clearSelection) {
        if (ArrayExtensions_1.ArrayExtensions.IsNotNullOrEmpty(rowNodes)) {
            rowNodes.forEach((node) => this.deSelectNode(node, clearSelection));
        }
    }
    selectNode(rowNode, clearSelection) {
        if (!rowNode) {
            this.logger.error('No node to select');
            return;
        }
        rowNode.setSelected(true, clearSelection);
    }
    deSelectNode(rowNode, clearSelection) {
        if (!rowNode) {
            this.logger.error('No node to deselect');
            return;
        }
        rowNode.setSelected(false, clearSelection);
    }
    selectCells(columnIds, startNode, endNode, clearSelection) {
        if (clearSelection) {
            this.gridOptions.api.clearRangeSelection();
        }
        const cellRangeParams = {
            rowStartIndex: startNode.rowIndex,
            rowEndIndex: endNode.rowIndex,
            columns: columnIds,
        };
        this.gridOptions.api.addCellRange(cellRangeParams);
    }
    isRowNodeVisible(rowNode) {
        // sure this can be done better!
        let isVisible = false;
        this.gridOptions.api.forEachNodeAfterFilter((rd) => {
            if (rd == rowNode) {
                isVisible = true;
            }
        });
        return isVisible;
    }
    redrawBody() {
        this.gridOptions.api.redrawRows();
        this._emit('GridRefreshed');
    }
    redrawRenderedRows() {
        this.redrawRows(this.gridOptions.api.getRenderedNodes());
    }
    redrawHeader() {
        this.gridOptions.api.refreshHeader();
    }
    redrawRow(rowNode) {
        this.redrawRows([rowNode]);
    }
    redrawRows(rowNodes) {
        const redrawRowsParams = {
            rowNodes,
        };
        try {
            this.gridOptions.api.redrawRows(redrawRowsParams);
        }
        catch (ex) {
            this.logger.consoleError('AG Grid redrawRows was unable to find some row nodes. Tried to redraw row nodes: ', rowNodes, ex);
        }
    }
    refreshCells(rowNodes, columns, forceUpdate, suppressFlash = false) {
        const api = this.gridOptions.api;
        // in order to get a fresh reference to rowNodes
        // rowNodes = this.getRowNodesForPrimaryKeys(
        //   rowNodes.map((rowNode) => this.getPrimaryKeyValueFromRowNode(rowNode))
        // );
        const refreshCellParams = {
            rowNodes,
            columns: columns,
            force: forceUpdate,
            suppressFlash,
        };
        api.refreshCells(refreshCellParams);
    }
    refreshColumns(columns, forceUpdate, suppressFlash) {
        this.refreshCells(null, columns, forceUpdate, suppressFlash);
    }
    jumpToRow(rowNode) {
        this.gridOptions.api.ensureNodeVisible(rowNode, 'middle');
    }
    jumpToColumn(columnId) {
        this.gridOptions.api.ensureColumnVisible(columnId);
    }
    jumpToCell(columnId, rowNode) {
        this.jumpToRow(rowNode);
        this.jumpToColumn(columnId);
    }
    getColDefsForSpecialColumns() {
        return [
            ...this.getColDefsForCalculatedColumns(),
            ...this.getColDefsForActionColumns(),
            ...this.getColDefsForFreeTextColumns(),
            ...this.getColDefsForActionRowColumns(),
            ...this.api.fdc3Api.internalApi.getFdc3ActionColDefs(),
        ];
    }
    getColDefsForFreeTextColumns() {
        const defaultFreeTextColumnSettings = (0, ObjectFactory_1.CreateEmptyFreeTextColumn)().FreeTextColumnSettings;
        return this.api.freeTextColumnApi.getFreeTextColumns().map((freeTextColumn) => {
            var _a;
            const freeTextColumnSettings = Object.assign(Object.assign({}, defaultFreeTextColumnSettings), freeTextColumn.FreeTextColumnSettings);
            const dataTypeEditor = freeTextColumnSettings.DataType === 'Number'
                ? AdaptableNumberEditor_1.AdaptableNumberEditor
                : freeTextColumnSettings.DataType === 'Date'
                    ? AdaptableDateEditor_1.AdaptableDateEditor
                    : null;
            const columnTypes = [
                GeneralConstants_1.AB_SPECIAL_COLUMN,
                this.agGridHelper.getAgGridDataType(freeTextColumnSettings.DataType),
            ];
            if (ArrayExtensions_1.ArrayExtensions.IsNotNullOrEmpty((_a = freeTextColumn === null || freeTextColumn === void 0 ? void 0 : freeTextColumn.FreeTextColumnSettings) === null || _a === void 0 ? void 0 : _a.ColumnTypes)) {
                columnTypes.push(...freeTextColumn.FreeTextColumnSettings.ColumnTypes);
            }
            const newColDef = {
                headerName: freeTextColumn.FriendlyName || freeTextColumn.ColumnId,
                colId: freeTextColumn.ColumnId,
                editable: true,
                hide: true,
                width: freeTextColumnSettings.Width,
                enableValue: freeTextColumnSettings.Aggregatable,
                filter: freeTextColumnSettings.Filterable,
                floatingFilter: freeTextColumnSettings.Filterable,
                resizable: freeTextColumnSettings.Resizable,
                enableRowGroup: freeTextColumnSettings.Groupable,
                sortable: freeTextColumnSettings.Sortable,
                enablePivot: freeTextColumnSettings.Pivotable,
                suppressMenu: freeTextColumnSettings.SuppressMenu,
                suppressMovable: freeTextColumnSettings.SuppressMovable,
                cellEditor: dataTypeEditor !== null && dataTypeEditor !== void 0 ? dataTypeEditor : (freeTextColumn.TextEditor && freeTextColumn.TextEditor == 'Large'
                    ? 'agLargeTextCellEditor'
                    : 'agTextCellEditor'),
                type: columnTypes,
                valueSetter: (params) => {
                    return (params.data[freeTextColumn.ColumnId] = params.newValue);
                },
                // use a Boolean Cell Data Type if the Free Text Column is boolean
                cellDataType: freeTextColumnSettings.DataType && freeTextColumnSettings.DataType == 'Boolean'
                    ? 'boolean'
                    : undefined,
                valueGetter: (params) => this.api.freeTextColumnApi.getFreeTextColumnValueForRowNode(freeTextColumn, params.node),
            };
            if (freeTextColumnSettings.HeaderToolTip) {
                newColDef.headerTooltip = freeTextColumnSettings.HeaderToolTip;
            }
            this.logger.info('Setting up FreeText Column: ' + freeTextColumn.ColumnId);
            return newColDef;
        });
    }
    setupColumnValueGetter({ col }) {
        // need this here if we want plugins to intercept
        this.setColDefProperty(col, 'valueGetter', (userValue) => {
            return userValue;
        });
    }
    getActionRowButtonDefs() {
        const actionRowButtons = this.api.actionRowApi.getActionRowButtons();
        if (!(actionRowButtons === null || actionRowButtons === void 0 ? void 0 : actionRowButtons.length)) {
            return [];
        }
        const defaultActionRowButtonConfiguration = {
            create: {
                icon: {
                    name: 'add',
                },
                tooltip: 'Create',
                onClick: () => {
                    this.api.actionRowApi.displayCreateActionRow();
                },
            },
            edit: {
                icon: {
                    name: 'edit',
                },
                tooltip: 'Edit',
                onClick: (button, context) => {
                    this.api.actionRowApi.displayEditActionRow(context.primaryKeyValue);
                },
            },
            clone: {
                icon: {
                    name: 'clone',
                },
                tooltip: 'Clone',
                onClick: (button, context) => {
                    this.api.actionRowApi.displayCloneActionRow(context.primaryKeyValue);
                },
            },
            delete: {
                icon: {
                    name: 'delete',
                },
                tooltip: 'Delete',
                onClick: (button, context) => {
                    var _a, _b;
                    const eventInfo = {
                        type: 'rowDeleted',
                        rowNode: context.rowNode,
                        adaptableApi: context.adaptableApi,
                        userName: context.userName,
                        adaptableId: context.adaptableId,
                    };
                    this.api.eventApi.emit('ActionRowSubmitted', eventInfo);
                    (_b = (_a = this.adaptableOptions.actionRowOptions.actionRowFormOptions).onFormSubmit) === null || _b === void 0 ? void 0 : _b.call(_a, eventInfo);
                },
            },
        };
        const actionButtons = actionRowButtons
            .map((actionRowButtonType) => {
            var _a, _b;
            const defaultButtonConfig = defaultActionRowButtonConfiguration[actionRowButtonType];
            if (!defaultButtonConfig) {
                // should NOT happen unless the user sent some invalid button type in the predefined config
                return;
            }
            const customButtonConfig = (_b = (_a = this.adaptableOptions.actionRowOptions.actionRowButtonOptions).customConfiguration) === null || _b === void 0 ? void 0 : _b.call(_a, {
                adaptableApi: this.api,
                actionRowButtonType,
                userName: this.adaptableOptions.userName,
                adaptableId: this.adaptableOptions.adaptableId,
            });
            if (!customButtonConfig) {
                return defaultButtonConfig;
            }
            return Object.assign(Object.assign({}, defaultButtonConfig), customButtonConfig);
        })
            .filter(Boolean);
        return actionButtons;
    }
    getColDefsForActionRowColumns() {
        const actionRowButtons = this.api.actionRowApi.getActionRowButtons();
        if (!(actionRowButtons === null || actionRowButtons === void 0 ? void 0 : actionRowButtons.length)) {
            return [];
        }
        const actionButtons = this.getActionRowButtonDefs();
        if (!actionButtons.length) {
            return [];
        }
        return [
            {
                headerName: '',
                colId: GeneralConstants_1.ADAPTABLE_ROW_ACTION_BUTTONS,
                hide: false,
                editable: false,
                // 15px is the icon width + left&right padding + left&right border
                width: Math.max(actionButtons.length * (UIHelper_1.default.getSimpleButtonPaddingWidth() * 2 + 15) + 2, 
                // If the width of the column is to small, the width will not be applied
                // this triggers the setLayout an infinite loop because the width read from the grid is always different
                40),
                resizable: false,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                suppressMenu: true,
                suppressMovable: true,
                filter: false,
                sortable: false,
                enableRowGroup: false,
                pinned: this.adaptableOptions.actionRowOptions.actionRowButtonOptions.position === 'pinnedRight'
                    ? 'right'
                    : 'left',
                cellRenderer: this.variant === 'react' ? ActionColumnRenderer_1.ReactActionColumnRenderer : ActionColumnRenderer_1.ActionColumnRenderer,
                cellClass: GeneralConstants_1.ADAPTABLE_ROW_ACTION_BUTTONS,
                type: [GeneralConstants_1.AB_SPECIAL_COLUMN, 'abColDefObject'],
            },
        ];
    }
    getColDefsForActionColumns() {
        const defaultActionColumnSettings = {
            resizable: true,
            suppressMenu: false,
            suppressMovable: false,
        };
        return this.api.actionColumnApi.getActionColumns().map((actionColumn) => {
            const actionColumnSettings = Object.assign(Object.assign({}, defaultActionColumnSettings), actionColumn.actionColumnSettings);
            const newColDef = {
                headerName: actionColumn.friendlyName ? actionColumn.friendlyName : actionColumn.columnId,
                colId: actionColumn.columnId,
                hide: false,
                editable: false,
                width: actionColumnSettings.width,
                resizable: actionColumnSettings.resizable,
                suppressMenu: actionColumnSettings.suppressMenu,
                suppressMovable: actionColumnSettings.suppressMovable,
                filter: false,
                sortable: false,
                enableRowGroup: false,
                cellRenderer: this.variant === 'react' ? ActionColumnRenderer_1.ReactActionColumnRenderer : ActionColumnRenderer_1.ActionColumnRenderer,
                type: [GeneralConstants_1.AB_SPECIAL_COLUMN, 'abColDefObject'],
            };
            this.logger.info('Setting up Action Column: ' + actionColumn.columnId);
            return newColDef;
        });
    }
    updateColDefsForSpecialColumns() {
        const allColDefs = this.getGroupAndColumnDefs();
        const specialColDefs = this.getColDefsForSpecialColumns();
        const isSpecialColDef = (colDef) => {
            const { type } = colDef;
            if (type === GeneralConstants_1.AB_SPECIAL_COLUMN || (Array.isArray(type) && type.includes(GeneralConstants_1.AB_SPECIAL_COLUMN))) {
                return true;
            }
            return false;
        };
        const isColGroupDef = (columnDefinition) => {
            // @ts-ignore
            return columnDefinition['children'] != null;
        };
        const processedSpecialColDefIds = [];
        const mapColDefs = (colDefs) => {
            return colDefs.map((colDef) => {
                if (isColGroupDef(colDef)) {
                    // if it's a group column, recursively map its children
                    colDef.children = mapColDefs(colDef.children);
                    return colDef;
                }
                else {
                    if (!isSpecialColDef(colDef)) {
                        // if it's not a special column, return it as is
                        return colDef;
                    }
                    const newlyCreatedSpecialColDef = specialColDefs.find((specialColDef) => specialColDef.colId === colDef.colId);
                    if (newlyCreatedSpecialColDef) {
                        // if it's a special column and we have a special col def for it, return the special col def
                        processedSpecialColDefIds.push(colDef.colId);
                        // merge the user defined colDef with the special col def
                        // this way the user may provide some custom settings for the special col def (tooltip, etc)
                        const mergedColDef = Object.assign(Object.assign({}, colDef), newlyCreatedSpecialColDef);
                        return mergedColDef;
                    }
                    else {
                        // otherwise, return the original col def
                        return colDef;
                    }
                }
            });
        };
        let resultColDefs = mapColDefs(allColDefs);
        // check if there are any special colDefs that were not processed
        // in that case, add them to the end of the colDefs
        specialColDefs.forEach((specialColDef) => {
            if (!processedSpecialColDefIds.includes(specialColDef.colId)) {
                resultColDefs.push(specialColDef);
            }
        });
        // remove special column that are no longer defined
        resultColDefs = resultColDefs.filter((colDef) => {
            if (isSpecialColDef(colDef)) {
                // must be in specialColDefs
                return specialColDefs.some((specialColDef) => specialColDef.colId === colDef.colId);
            }
            return true;
        });
        this.safeSetColDefs(resultColDefs);
        this.setLayout();
    }
    getColDefsForCalculatedColumns() {
        const defaultCalculatedColumnSettings = (0, ObjectFactory_1.CreateEmptyCalculatedColumn)().CalculatedColumnSettings;
        return this.api.calculatedColumnApi.getCalculatedColumns().map((calculatedColumn) => {
            const calculatedColumnSettings = Object.assign(Object.assign({}, defaultCalculatedColumnSettings), calculatedColumn.CalculatedColumnSettings);
            if (!calculatedColumnSettings.DataType) {
                calculatedColumnSettings.DataType =
                    this.CalculatedColumnExpressionService.getCalculatedColumnDataType(calculatedColumn.Query);
            }
            const columnTypes = [
                GeneralConstants_1.AB_SPECIAL_COLUMN,
                this.agGridHelper.getAgGridDataType(calculatedColumnSettings.DataType),
            ];
            if (ArrayExtensions_1.ArrayExtensions.IsNotNullOrEmpty(calculatedColumnSettings.ColumnTypes)) {
                columnTypes.push(...calculatedColumnSettings.ColumnTypes);
            }
            const isExternalEvaluation = !this.api.expressionApi.internalApi.evaluateExpressionInAdaptableQL('CalculatedColumn', calculatedColumn, this.api.expressionApi.getAdaptableQueryExpression(calculatedColumn.Query));
            const newColDef = {
                headerName: calculatedColumn.FriendlyName
                    ? calculatedColumn.FriendlyName
                    : calculatedColumn.ColumnId,
                colId: calculatedColumn.ColumnId,
                hide: true,
                editable: false,
                width: calculatedColumnSettings.Width,
                enableValue: calculatedColumnSettings.Aggregatable,
                filter: calculatedColumnSettings.Filterable,
                floatingFilter: calculatedColumnSettings.Filterable,
                resizable: calculatedColumnSettings.Resizable,
                enableRowGroup: calculatedColumnSettings.Groupable,
                sortable: calculatedColumnSettings.Sortable,
                enablePivot: calculatedColumnSettings.Pivotable,
                suppressMenu: calculatedColumnSettings.SuppressMenu,
                suppressMovable: calculatedColumnSettings.SuppressMovable,
                type: columnTypes,
                valueGetter: (params) => {
                    var _a;
                    if (isExternalEvaluation) {
                        return (_a = params.data) === null || _a === void 0 ? void 0 : _a[calculatedColumn.ColumnId];
                    }
                    return this.CalculatedColumnExpressionService.evaluateCalculatedColumnQuery(calculatedColumn, params.node);
                },
            };
            if (calculatedColumnSettings.ShowToolTip != null &&
                calculatedColumnSettings.ShowToolTip == true) {
                newColDef.tooltipValueGetter = () => {
                    return this.api.internalApi.getAdaptableQueryExpressionText(calculatedColumn.Query);
                };
            }
            if (calculatedColumnSettings.HeaderToolTip) {
                newColDef.headerTooltip = calculatedColumnSettings.HeaderToolTip;
            }
            this.logger.info('Setting up Calculated Column: ' + calculatedColumn.ColumnId);
            return newColDef;
        });
    }
    isRegularColDef(colDef) {
        return colDef.children === undefined;
    }
    isGroupColDef(colDef) {
        return colDef.children !== undefined;
    }
    getColDefs() {
        let allColDefs = this.getGroupAndColumnDefs();
        let justColDefs = [];
        allColDefs.forEach((cd) => {
            if (this.isRegularColDef(cd)) {
                justColDefs.push(cd);
            }
        });
        return justColDefs;
    }
    getGroupedColDefs() {
        let allColDefs = this.getGroupAndColumnDefs();
        let justGroupColDefs = [];
        allColDefs.forEach((cd) => {
            if (this.isGroupColDef(cd)) {
                justGroupColDefs.push(cd);
            }
        });
        return justGroupColDefs;
    }
    getFirstRowNode() {
        let startIndex = 0;
        let firstRowNode = this.gridOptions.api.getDisplayedRowAtIndex(startIndex);
        if (firstRowNode === null || firstRowNode === void 0 ? void 0 : firstRowNode.group) {
            // all groups may be closed so it is safer to get first leaf node
            // all groups must have at least one leafe node
            firstRowNode = firstRowNode.allLeafChildren[0];
        }
        if (!firstRowNode) {
            firstRowNode = this.gridOptions.api.getDisplayedRowAtIndex(0);
        }
        return firstRowNode;
    }
    getFirstDisplayedRowNode() {
        const firstDisplayedRowIndex = this.gridOptions.api.getFirstDisplayedRow();
        return this.gridOptions.api.getDisplayedRowAtIndex(firstDisplayedRowIndex);
    }
    destroy(config) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3;
        if (this.gridOptions && this.gridOptions.api) {
            this.gridOptions.api.removeEventListener(core_1.Events.EVENT_FIRST_DATA_RENDERED, this.listenerFirstDataRendered);
            this.gridOptions.api.removeEventListener(core_1.Events.EVENT_COLUMN_PIVOT_MODE_CHANGED, this.listenerPivotModeChanged);
            this.gridOptions.api.removeEventListener(core_1.Events.EVENT_COLUMN_PIVOT_CHANGED, this.listenerPivotChanged);
            this.gridOptions.api.removeEventListener(core_1.Events.EVENT_CELL_EDITING_STARTED, this.listenerCellEditingStarted);
            this.gridOptions.api.removeEventListener(core_1.Events.EVENT_COLUMN_ROW_GROUP_CHANGED, this.listenerColumnRowGroupChanged);
            this.gridOptions.api.removeEventListener(core_1.Events.EVENT_RANGE_SELECTION_CHANGED, this.listenerRangeSelectionChanged);
            this.gridOptions.api.removeEventListener(core_1.Events.EVENT_COLUMN_RESIZED, this.listenerColumnResized);
            this.gridOptions.api.removeEventListener(core_1.Events.EVENT_SORT_CHANGED, this.listenerSortChanged);
            this.gridOptions.api.removeEventListener(core_1.Events.EVENT_MODEL_UPDATED, this.listenerModelUpdated);
            this.gridOptions.api.removeGlobalListener(this.listenerGlobalSetRowSelection);
            this.gridOptions.api.removeGlobalListener(this.listenerGlobalColumnEventsThatTriggerStateChange);
            this.gridOptions.api.removeGlobalListener(this.listenerGlobalColumnEventsThatTriggerAutoLayoutSave);
            this.gridOptions.api.removeGlobalListener(this.listenerGlobalRowGroupEventsThatTriggerAutoLayoutSave);
            this.listenerFirstDataRendered = null;
            this.listenerPivotModeChanged = null;
            this.listenerPivotChanged = null;
            this.listenerCellEditingStarted = null;
            this.listenerColumnRowGroupChanged = null;
            this.listenerRangeSelectionChanged = null;
            this.listenerColumnResized = null;
            this.listenerGlobalSetRowSelection = null;
            this.listenerSortChanged = null;
            this.listenerModelUpdated = null;
            this.listenerGlobalColumnEventsThatTriggerStateChange = null;
            this.listenerGlobalColumnEventsThatTriggerAutoLayoutSave = null;
            this.listenerGlobalRowGroupEventsThatTriggerAutoLayoutSave = null;
            this.throttleFilterOnEditDataChange = null;
            this.throttleFilterOnTickingDataChange = null;
            this.gridOptions.api.__adaptable = null;
            this.gridOptions.api.setColumnDefs = GridApi_setColumnDefs;
            this.revertGridOptionsPropertiesToUserValue([
                'getRowStyle',
                'getRowClass',
                'excelStyles',
                'suppressAggFuncInHeader',
                'initialGroupOrderComparator',
                'getContextMenuItems',
                'getMainMenuItems',
                'isExternalFilterPresent',
                'doesExternalFilterPass',
                'statusBar',
                'sideBar',
                'aggFuncs',
            ]);
            if (this.gridOptions.components) {
                this.gridOptions.components.AdaptableToolPanel = null;
            }
            if (config && config.destroyApi === false) {
            }
            else {
                this.gridOptions.api.destroy();
            }
        }
        const gridContainerElement = this.getAgGridContainerElement();
        if (gridContainerElement) {
            gridContainerElement.removeEventListener('keydown', this.listenerKeydown);
            gridContainerElement.removeEventListener('mouseenter', this.listenerMouseEnter);
            gridContainerElement.removeEventListener('mouseleave', this.listenerMouseLeave);
            this.listenerKeydown = null;
            this.listenerMouseEnter = null;
            this.listenerMouseLeave = null;
        }
        this.api.internalDestroySelf();
        this.colDefPropertyCache = null;
        this.gridOptionsPropertyCache = null;
        this.currentColumnDefs = null;
        this.rowListeners = null;
        this.emitter.destroy();
        this.emitter = null;
        this._adaptableReady = false;
        this.agGridHelper.destroy();
        this.agGridMenuHelper.destroy();
        Adaptable.dismissInstance(this);
        const abContainerElement = this.getAdaptableContainerElement();
        if (config && !config.unmount) {
            return;
        }
        if (abContainerElement != null) {
            (_a = this.unmountReactRoot) === null || _a === void 0 ? void 0 : _a.call(this);
        }
        this.gridContainerElement = null;
        this.abContainerElement = null;
        (_b = this.adaptableStore) === null || _b === void 0 ? void 0 : _b.destroy();
        this.adaptableStore = null;
        this.gridOptions = null;
        this.adaptableOptions = null;
        (_d = (_c = this.CalculatedColumnExpressionService) === null || _c === void 0 ? void 0 : _c.destroy) === null || _d === void 0 ? void 0 : _d.call(_c);
        this.CalculatedColumnExpressionService = null;
        (_f = (_e = this.DataService) === null || _e === void 0 ? void 0 : _e.destroy) === null || _f === void 0 ? void 0 : _f.call(_e);
        this.DataService = null;
        (_h = (_g = this.EntitlementService) === null || _g === void 0 ? void 0 : _g.destroy) === null || _h === void 0 ? void 0 : _h.call(_g);
        this.EntitlementService = null;
        (_k = (_j = this.Fdc3Service) === null || _j === void 0 ? void 0 : _j.destroy) === null || _k === void 0 ? void 0 : _k.call(_j);
        this.Fdc3Service = null;
        (_m = (_l = this.ReportService) === null || _l === void 0 ? void 0 : _l.destroy) === null || _m === void 0 ? void 0 : _m.call(_l);
        this.ReportService = null;
        (_p = (_o = this.ModuleService) === null || _o === void 0 ? void 0 : _o.destroy) === null || _p === void 0 ? void 0 : _p.call(_o);
        this.ModuleService = null;
        (_r = (_q = this.ValidationService) === null || _q === void 0 ? void 0 : _q.destroy) === null || _r === void 0 ? void 0 : _r.call(_q);
        this.ValidationService = null;
        (_t = (_s = this.QueryLanguageService) === null || _s === void 0 ? void 0 : _s.destroy) === null || _t === void 0 ? void 0 : _t.call(_s);
        this.QueryLanguageService = null;
        (_v = (_u = this.AlertService) === null || _u === void 0 ? void 0 : _u.destroy) === null || _v === void 0 ? void 0 : _v.call(_u);
        this.AlertService = null;
        (_x = (_w = this.TeamSharingService) === null || _w === void 0 ? void 0 : _w.destroy) === null || _x === void 0 ? void 0 : _x.call(_w);
        this.TeamSharingService = null;
        (_z = (_y = this.RowEditService) === null || _y === void 0 ? void 0 : _y.destroy) === null || _z === void 0 ? void 0 : _z.call(_y);
        this.RowEditService = null;
        (_1 = (_0 = this.MetamodelService) === null || _0 === void 0 ? void 0 : _0.destroy) === null || _1 === void 0 ? void 0 : _1.call(_0);
        this.MetamodelService = null;
        (_3 = (_2 = this.LicenseService) === null || _2 === void 0 ? void 0 : _2.destroy) === null || _3 === void 0 ? void 0 : _3.call(_2);
        this.LicenseService = null;
        this.isDestroyed = true;
    }
    // really really need to do this properly but as a temp fix lets create a default style for when no data
    getDefaultIPPStyle() {
        return {
            Header: {
                headerColor: '#000000',
                headerBackColor: '#f5f7f7',
                headerFontFamily: 'sans-serif',
                headerFontSize: '12px',
                headerFontStyle: 'normal',
                headerFontWeight: '400',
                height: 65,
                Columns: this.api.columnApi.getColumns().map((col) => {
                    return {
                        columnFriendlyName: col.friendlyName,
                        width: 200,
                        textAlign: 'start',
                    };
                }),
            },
            Row: {
                color: '#000000',
                backColor: '#ffffff',
                altBackColor: '#fcfdfe',
                fontFamily: 'sans-serif',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: '400',
                height: 30,
                Columns: this.api.columnApi.getColumns().map((col) => {
                    return {
                        columnFriendlyName: col.friendlyName,
                        width: 200,
                        textAlign: 'start',
                    };
                }),
            },
        };
    }
    // This horrible method is used to get the grid style for when we export to ipushpull
    // We need to find a better implementation
    getCurrentIPPStyle() {
        return this.agGridHelper.getCurrentIPPStyle();
    }
    getAdaptableContainerElement() {
        if (!this.abContainerElement) {
            let containerOptionsContainer = this.adaptableOptions.containerOptions.adaptableContainer;
            this.abContainerElement =
                typeof containerOptionsContainer === 'string'
                    ? document.getElementById(containerOptionsContainer)
                    : containerOptionsContainer;
            if (!this.abContainerElement) {
                let oldContainer = document.getElementById('adaptableBlotter');
                if (oldContainer) {
                    this.logger.warn(`
!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
!!!!! The old default container element for Adaptable was "#adaptableBlotter", configured via 'containerOptions.adaptableContainer="adaptableBlotter"'.
!!!!! Seems like you haven't updated your html container selector, so we're falling back to using that one.
!!!!!
!!!!! To make this warning go away, update your html structure and make sure you have an element with id="adaptable" in your app!
!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
`);
                    this.abContainerElement = oldContainer;
                }
            }
        }
        return this.abContainerElement;
    }
    getAgGridContainerElement() {
        if (!this.gridContainerElement) {
            this.gridContainerElement =
                typeof this.adaptableOptions.containerOptions.agGridContainer === 'string'
                    ? document.getElementById(this.adaptableOptions.containerOptions.agGridContainer)
                    : this.adaptableOptions.containerOptions.agGridContainer;
        }
        return this.gridContainerElement;
    }
    initInternalGridLogic() {
        if (this.abContainerElement == null) {
            this.abContainerElement = this.getAdaptableContainerElement();
        }
        if (this.abContainerElement == null) {
            if (typeof this.adaptableOptions.containerOptions.adaptableContainer === 'string') {
                this.logger.consoleError(`There is no DIV with id="${this.adaptableOptions.containerOptions.adaptableContainer}" so cannot render Adaptable`);
            }
            else {
                this.logger.consoleError(`No valid adaptableContainer found to render Adaptable`, this.adaptableOptions.containerOptions.adaptableContainer);
            }
            return;
        }
        const gridContainerElement = this.getAgGridContainerElement();
        if (gridContainerElement) {
            gridContainerElement.addEventListener('keydown', (this.listenerKeydown = (event) => this._emit('KeyDown', event)), 
            // This is needed to be able to prevent the editor to be opened
            // in bubling phase the opening is not prevented with ag-grid v30
            true);
            gridContainerElement.addEventListener('mouseenter', (this.listenerMouseEnter = (event) => {
                this._emit('MouseEnter', event);
            }), true);
            gridContainerElement.addEventListener('mouseleave', (this.listenerMouseLeave = (event) => this._emit('MouseLeave', event)));
        }
        /*********************
         * AG Grid EVENTS
         * AG Grid provide a numer of events - we listen to many of them and react accordingly
         * We often group many of these events together where we wish the grid to behave identically for all changes
         * for each set - please provide a reason and use case
         **********************/
        /**
         * Use Case: AG Grid columns have changed
         * Action: Set Columns in store and filter grid
         */
        const columnEventsThatTriggersStateChange = [
            core_1.Events.EVENT_COLUMN_MOVED,
            core_1.Events.EVENT_GRID_COLUMNS_CHANGED,
            core_1.Events.EVENT_COLUMN_EVERYTHING_CHANGED,
            core_1.Events.EVENT_DISPLAYED_COLUMNS_CHANGED,
            core_1.Events.EVENT_COLUMN_VISIBLE,
            core_1.Events.EVENT_NEW_COLUMNS_LOADED,
        ];
        this.gridOptions.api.addGlobalListener((this.listenerGlobalColumnEventsThatTriggerStateChange = (type) => {
            if (columnEventsThatTriggersStateChange.indexOf(type) > -1) {
                this.debouncedSetColumnIntoStore();
            }
        }));
        /**
         * Use Case: User has started inline editing but its distabled in Action Row Options
         * Action: Stop editing
         */
        this.gridOptions.api.addEventListener(core_1.Events.EVENT_CELL_EDITING_STARTED, (this.listenerCellEditingStarted = () => {
            var _a;
            if ((_a = this.adaptableOptions.actionRowOptions) === null || _a === void 0 ? void 0 : _a.disableInlineEditing)
                this.gridOptions.api.stopEditing();
        }));
        /**
         * Use Case: initial data has been displayed in grid
         * Action1: Set the Layout
         * Action2: Ensure that we have set column data types
         * Note: Deals with scenario where the data is provided to AdapTable after grid has been setup
         */
        this.gridOptions.api.addEventListener(core_1.Events.EVENT_FIRST_DATA_RENDERED, (this.listenerFirstDataRendered = () => {
            this.checkColumnsDataTypeSet();
            this.api.calculatedColumnApi.refreshAggregatedCalculatedColumns();
            this.setLayout();
        }));
        /**
         * Use Case: Entered or Left Pivot Mode
         * Action 1: Autosize pivot columns when entering pivot mode (if autosize pivot in Layout is true)
         * Action 2: Set pivot mode on / off in api as necessary
         */
        this.gridOptions.api.addEventListener(core_1.Events.EVENT_COLUMN_PIVOT_MODE_CHANGED, (this.listenerPivotModeChanged = (params) => {
            if (params.type == 'columnPivotModeChanged' &&
                params.columnApi != null &&
                params.columnApi.columnController != null &&
                params.columnApi.columnController.pivotMode == true) {
                if (this.adaptableOptions.layoutOptions.autoSizeColumnsInPivotLayout == true) {
                    this.gridOptions.columnApi.autoSizeAllColumns();
                }
                this.api.internalApi.setPivotModeOn();
            }
            else {
                this.api.internalApi.setPivotModeOff();
            }
        }));
        /**
         * Use Case: A pivot column has changed
         * Action: Autosize pivot columns (if autosize pivot in Layout is true)
         */
        this.gridOptions.api.addEventListener(core_1.Events.EVENT_COLUMN_PIVOT_CHANGED, (this.listenerPivotChanged = (params) => {
            if (params.type == 'columnPivotChanged' &&
                params.columnApi != null &&
                params.columnApi.columnController != null &&
                params.columnApi.columnController.pivotMode == true) {
                if (this.adaptableOptions.layoutOptions.autoSizeColumnsInPivotLayout == true) {
                    this.gridOptions.columnApi.autoSizeAllColumns();
                }
            }
        }));
        /**
         * Use Case: Things have changed in the grid that require the Layout to be saved
         * Action: Save the Layout (on a debounce)
         */
        const columnEventsThatTriggersAutoLayoutSave = [
            core_1.Events.EVENT_COLUMN_PINNED,
            core_1.Events.EVENT_COLUMN_PIVOT_CHANGED,
            core_1.Events.EVENT_COLUMN_PIVOT_MODE_CHANGED,
            core_1.Events.EVENT_DISPLAYED_COLUMNS_CHANGED,
            core_1.Events.EVENT_SORT_CHANGED,
            core_1.Events.EVENT_COLUMN_ROW_GROUP_CHANGED,
            core_1.Events.EVENT_COLUMN_VALUE_CHANGED,
        ];
        // ADD filter event
        this.gridOptions.api.addGlobalListener((this.listenerGlobalColumnEventsThatTriggerAutoLayoutSave = (type) => {
            if (columnEventsThatTriggersAutoLayoutSave.indexOf(type) > -1) {
                this.debouncedSaveGridLayout();
            }
        }));
        // Save Layout if Display Row Groups is "dynamic"
        const rowGroupEventsThatTriggersAutoLayoutSave = [
            core_1.Events.EVENT_ROW_GROUP_OPENED,
            core_1.Events.EVENT_EXPAND_COLLAPSE_ALL,
        ];
        this.gridOptions.api.addGlobalListener((this.listenerGlobalRowGroupEventsThatTriggerAutoLayoutSave = (type) => {
            if (rowGroupEventsThatTriggersAutoLayoutSave.indexOf(type) > -1) {
                if (this.adaptableOptions.layoutOptions.displayRowGroups == 'dynamic') {
                    this.debouncedSaveGridLayout();
                }
            }
        }));
        /**
         * Use Case: Column Row Grouping changes and 'restoreUngroupedColumns' is true
         * Action: Make the column invisiblel
         */
        this.gridOptions.api.addEventListener(core_1.Events.EVENT_COLUMN_ROW_GROUP_CHANGED, (this.listenerColumnRowGroupChanged = (params) => {
            var _a, _b;
            if (this.api.internalApi.isGridInPivotMode()) {
                return;
            }
            if ((_b = (_a = this.adaptableOptions) === null || _a === void 0 ? void 0 : _a.groupingOptions) === null || _b === void 0 ? void 0 : _b.restoreUngroupedColumns) {
                this.persistColumnIndexBeforeGrouping(params);
            }
        }));
        /**
         * Use Case: A Column has finished being resized
         * Action 1: Save the Layout (on a debounce)
         * Action 2: Emit the internal ColumnResized event - used by Sparkline Column (in Charts)
         */
        this.gridOptions.api.addEventListener(core_1.Events.EVENT_COLUMN_RESIZED, (this.listenerColumnResized = (params) => {
            if (params.finished == true && params.type == 'columnResized' && params.column) {
                this.debouncedSaveGridLayout();
            }
        }));
        /**
         * Use Case: Row Selection has changed
         * Action: Set Selected Rows (on a debeounce)
         */
        const columnEventsThatTriggerSetRowSelection = [
            core_1.Events.EVENT_ROW_GROUP_OPENED,
            core_1.Events.EVENT_SELECTION_CHANGED,
            core_1.Events.EVENT_ROW_SELECTED,
        ];
        this.gridOptions.api.addGlobalListener((this.listenerGlobalSetRowSelection = (type) => {
            if (ArrayExtensions_1.ArrayExtensions.ContainsItem(columnEventsThatTriggerSetRowSelection, type)) {
                this.debouncedSetSelectedRows();
            }
        }));
        /**
         * Use Case: User has selected a range of cells
         * Action: Set Selected Cells (on a debounce)
         */
        this.gridOptions.api.addEventListener(core_1.Events.EVENT_RANGE_SELECTION_CHANGED, (this.listenerRangeSelectionChanged = (params) => {
            if (params.finished == true) {
                this.debouncedSetSelectedCells();
            }
        }));
        /**
         * Use Case: Sort has changed in the Grid
         * Action1: Update AdapTable Sort Info
         * Action2: Set Selected Cells (on a debounce)
         */
        this.gridOptions.api.addEventListener(core_1.Events.EVENT_SORT_CHANGED, (this.listenerSortChanged = () => {
            this.onSortChanged();
            this.debouncedSetSelectedCells();
            this._emit('SortChanged');
        }));
        const showGroupingTotalsAsHeader = this.adaptableOptions.groupingOptions.showGroupingTotalsAsHeader;
        /**
         * Use Case: Model has updated
         * Action: If user has set to see grouping totals as header create a pinned row (bit of a hack)
         */
        this.gridOptions.api.addEventListener(core_1.Events.EVENT_MODEL_UPDATED, (this.listenerModelUpdated = (params) => {
            if (showGroupingTotalsAsHeader) {
                if (params && params.api) {
                    const pinnedData = params.api.getPinnedTopRow(0);
                    const model = params.api.getModel();
                    const rootNode = model.getRootNode();
                    if (!pinnedData) {
                        params.api.setPinnedTopRowData([rootNode.aggData]);
                    }
                    else {
                        pinnedData.updateData(rootNode.aggData);
                    }
                }
            }
        }));
        const eventsThatTriggerChartingChanges = [
            core_1.Events.EVENT_CHART_CREATED,
            /** Chart Range selection has changed */
            core_1.Events.EVENT_CHART_RANGE_SELECTION_CHANGED,
            /** Chart Options have changed */
            core_1.Events.EVENT_CHART_OPTIONS_CHANGED,
            /** Chart was destroyed */
            core_1.Events.EVENT_CHART_DESTROYED,
        ];
        const chartingModule = this.ModuleService.getModuleById('Charting');
        if (chartingModule.isModuleAvailable()) {
            this.gridOptions.api.addGlobalListener((type, params) => {
                if (ArrayExtensions_1.ArrayExtensions.ContainsItem(eventsThatTriggerChartingChanges, type)) {
                    this.ChartingService.onChartModelChange(this.getChartModels(), type, params);
                }
            });
        }
        /**
         * Row and Cell listeners created in 2020
         * These have supplanted many of the events we previously had and simplified things
         */
        this.rowListeners = {
            dataChanged: (event) => {
                this.onRowDataChanged({
                    rowNode: event.node,
                    oldData: event.oldData,
                    newData: event.newData,
                });
            },
            cellChanged: (event) => {
                if (event.column) {
                    this.onCellDataChanged({
                        rowNode: event.node,
                        oldValue: event.oldValue,
                        newValue: event.newValue,
                        colId: event.column.colId,
                    });
                }
            },
        };
        // We plug our filter mechanism and if there is already something like external widgets... we save ref to the function
        this.setGridOptionsProperty('isExternalFilterPresent', (originalIsExternalFilterPresent) => {
            return (params) => {
                if (this.isDestroyed) {
                    return true;
                }
                const columnFilters = this.api.columnFilterApi.getActiveColumnFilters();
                const isColumnFiltersActive = ArrayExtensions_1.ArrayExtensions.IsNotNullOrEmpty(columnFilters);
                const isGridFilterActive = StringExtensions_1.StringExtensions.IsNotNullOrEmpty(this.api.gridFilterApi.getCurrentGridFilterExpression());
                return (isColumnFiltersActive ||
                    isGridFilterActive ||
                    // it means that originalIsExternalFilterPresent will be called so we reinit that collection
                    (originalIsExternalFilterPresent ? originalIsExternalFilterPresent(params) : false));
            };
        });
        this.setGridOptionsProperty('doesExternalFilterPass', (originalDoesExternalFilterPass) => {
            return (node) => {
                if (this.isDestroyed) {
                    return true;
                }
                // first we assess a Grid Filter (if its running locally)
                if (!this.isGroupRowNode(node)) {
                    const currentGridFilter = this.api.gridFilterApi.getCurrentGridFilterExpression();
                    if (currentGridFilter) {
                        const evaluateGridFilterOnClient = this.api.expressionApi.internalApi.evaluateExpressionInAdaptableQL('GridFilter', undefined, currentGridFilter);
                        if (evaluateGridFilterOnClient) {
                            const isCurrentGridFilterValid = this.api.expressionApi.isValidBooleanExpression(currentGridFilter, ModuleConstants_1.GridFilterModuleId, `Invalid Grid Filter '${currentGridFilter}'`);
                            if (!isCurrentGridFilterValid ||
                                !this.api.internalApi
                                    .getQueryLanguageService()
                                    .evaluateBooleanExpression(currentGridFilter, ModuleConstants_1.GridFilterModuleId, node)) {
                                return false;
                            }
                        }
                    }
                }
                const columnFilters = this.api.columnFilterApi.getActiveColumnFilters();
                try {
                    if (columnFilters.length > 0) {
                        for (const columnFilter of columnFilters) {
                            const evaluateFilterOnClient = this.api.expressionApi.internalApi.evaluatePredicatesInAdaptableQL('ColumnFilter', columnFilter, [columnFilter.Predicate]);
                            if (evaluateFilterOnClient) {
                                // we then assess filters (if running locally)
                                if (!this.api.columnFilterApi.internalApi.evaluateColumnFilter(columnFilter, node)) {
                                    return false;
                                }
                            }
                        }
                    }
                }
                catch (ex) {
                    this.logger.error(ex);
                    return false;
                }
                return originalDoesExternalFilterPass ? originalDoesExternalFilterPass(node) : true;
            };
        });
        this.setGridOptionsProperty('getMainMenuItems', (userPropertyValue) => {
            return (params) => {
                // couldnt find a way to listen for menu close. There is a Menu Item Select, but you can also close menu from filter and clicking outside menu....
                return this.agGridMenuHelper.buildColumnMenu(params, userPropertyValue);
            };
        });
        this.setGridOptionsProperty('getContextMenuItems', (userPropertyValue) => {
            return (params) => {
                return this.agGridMenuHelper.buildContextMenu(params, userPropertyValue);
            };
        });
        this.prepareGrid();
    }
    updateColumnFilterActiveState() {
        var _a;
        const columnFilters = this.api.columnFilterApi.getActiveColumnFilters();
        const activeFilters = (_a = columnFilters === null || columnFilters === void 0 ? void 0 : columnFilters.filter) === null || _a === void 0 ? void 0 : _a.call(columnFilters, (columnFilter) => this.api.columnFilterApi.isColumnFilterActive(columnFilter));
        const isFilterActive = ArrayExtensions_1.ArrayExtensions.IsNotNullOrEmpty(activeFilters);
        const columnsWithActiveFilters = {};
        if (isFilterActive) {
            // used in particular at init time to show the filter icon correctly
            for (const colFilter of activeFilters) {
                const agGridCol = this.gridOptions.columnApi.getColumn(colFilter.ColumnId);
                if (agGridCol) {
                    columnsWithActiveFilters[agGridCol.getColId()] = true;
                    if (!agGridCol.isFilterActive()) {
                        agGridCol.setFilterActive(true);
                    }
                }
            }
        }
        const agFilterModel = this.gridOptions.api.getFilterModel();
        (this.gridOptions.columnApi.getColumns() || []).forEach((col) => {
            /**
             * When AG Grid filters are used active state should not be removed for all columns.
             * Need to check if the column has a filter model.
             */
            const isColumnInFilterModel = agFilterModel && agFilterModel[col.getColId()];
            if (!isColumnInFilterModel &&
                !columnsWithActiveFilters[col.getColId()] &&
                col.isFilterActive()) {
                col.setFilterActive(false);
            }
        });
    }
    buildStandaloneColumnHeader(adaptableColumn) {
        return this.agGridMenuHelper.buildStandaloneColumnHeader(adaptableColumn);
    }
    prepareGrid() {
        const perfPrepareGrid = this.logger.beginPerf('prepareGrid()');
        // add any special renderers, display formats, custom sorts and apply the theme
        // then do any final rendering required, filter the grid and - to be on the safe set - redraw
        try {
            this.updateColDefsForSpecialColumns();
            this.applyCurrentTheme();
            this.applyFinalRendering();
            this.applyColumnFiltering();
        }
        catch (ex) {
            this.logger.consoleError('AgGrid setup failed');
            this.logger.consoleError(ex);
        }
        perfPrepareGrid.end();
    }
    setupColumns() {
        const perfSetupColumns = this.logger.beginPerf('setupColumns()');
        const cols = this.gridOptions.columnApi.getColumns();
        cols.map((c) => c.getColDef()).forEach((colDef) => assignColId(colDef, this.logger));
        // this needs to be here, before the other setup below
        // so the setup methods below reference the correct columns in adaptable store
        this.updateColumnsIntoStore();
        cols.forEach((col) => {
            const colDef = col.getColDef();
            const colId = col.getColId();
            const abColumn = this.api.columnApi.getColumnWithColumnId(colId);
            const colSetupInfo = {
                col,
                colDef,
                colId,
                abColumn,
            };
            this.setupColumnCellRenderer(colSetupInfo);
            this.setupColumnCellStyle(colSetupInfo);
            this.setupColumnCellClass(colSetupInfo);
            this.setupColumnTooltipValueGetter(colSetupInfo);
            this.setupColumnValueGetter(colSetupInfo);
            this.setupColumnFilter(colSetupInfo);
            this.setupColumnFloatingFilter(colSetupInfo);
            this.setupColumnValueFormatter(colSetupInfo);
            this.setupColumnEditable(colSetupInfo);
            this.setupColumnValueSetter(colSetupInfo);
            this.setupColumnComparator(colSetupInfo);
            this.setupColumnCellEditor(colSetupInfo);
            this.setupColumnHeader(colSetupInfo);
            this.setupColumnQuickFilerText(colSetupInfo);
            this.setupAllowedAggFuncs(colSetupInfo);
            this.setupColumnKeyCreator(colSetupInfo);
            // this is just to make sure that AG Grid does NOT infer the cellDataType
            // https://github.com/AdaptableTools/adaptable/issues/2230 should render it obsolete
            this.setupColumnCellDataType(colSetupInfo);
        });
        this.setupRowStyling();
        // setupAggregationColumnHeaders() is executed on every setLayout()
        // this is safe because setLayout() is guaranteed to run at startup
        // this is required because every setLayout() may override the property
        //this.setupAggregationColumnHeaders();
        this.redrawBody();
        this.redrawHeader();
        perfSetupColumns.end();
    }
    revertGridOptionsPropertiesToUserValue(propertyNames) {
        for (const propertyName of propertyNames) {
            // see this.setGridOptionsProperty(...)
            const userKey = `user.${propertyName}`;
            const userValue = this.gridOptionsPropertyCache.get(userKey);
            this.gridOptions[propertyName] = userValue;
        }
    }
    setGridOptionsProperty(propertyName, propertyGetter) {
        if (this.isDestroyed) {
            return;
        }
        const userKey = `user.${propertyName}`;
        const adaptableKey = `adaptable.${propertyName}`;
        const value = this.gridOptions[propertyName];
        const isUserDefined = value !== this.gridOptionsPropertyCache.get(adaptableKey);
        if (isUserDefined) {
            this.gridOptionsPropertyCache.set(userKey, value);
        }
        const userValue = this.gridOptionsPropertyCache.get(userKey);
        let adaptableValue = propertyGetter(userValue);
        // usualy it is a function, but not always, e.g. in the case of 'suppressAggFuncInHeader'
        if (typeof adaptableValue === 'function') {
            const previousValue = adaptableValue;
            adaptableValue = (...args) => {
                if (this.isDestroyed) {
                    return;
                }
                return previousValue(...args);
            };
        }
        if (adaptableValue != null) {
            this.gridOptionsPropertyCache.set(adaptableKey, adaptableValue);
        }
        this.gridOptions[propertyName] = adaptableValue !== null && adaptableValue !== void 0 ? adaptableValue : userValue;
    }
    setColDefProperty(col, propertyName, propertyGetter) {
        const colId = col.getColId();
        const colDef = col.getColDef();
        const colSetupInfo = {
            col,
            colDef,
            colId,
        };
        const userKey = `user.${colId}.${propertyName}`;
        const adaptableKey = `adaptable.${colId}.${propertyName}`;
        const value = colDef[propertyName];
        const isUserDefined = value !== this.colDefPropertyCache.get(adaptableKey);
        if (isUserDefined) {
            this.colDefPropertyCache.set(userKey, value);
        }
        const userValue = this.colDefPropertyCache.get(userKey);
        const adaptableValue = propertyGetter(userValue);
        if (adaptableValue != null) {
            this.colDefPropertyCache.set(adaptableKey, adaptableValue);
        }
        let theValue = adaptableValue !== null && adaptableValue !== void 0 ? adaptableValue : userValue;
        this.forPlugins((plugin) => {
            if (plugin.interceptSetupColumnProperty) {
                theValue = plugin.interceptSetupColumnProperty(colSetupInfo, propertyName, theValue, this.api);
            }
        });
        if (propertyName === 'aggFunc') {
            if (colDef[propertyName] !== (theValue !== null && theValue !== void 0 ? theValue : null)) {
                this.gridOptions.columnApi.setColumnAggFunc(colId, theValue !== null && theValue !== void 0 ? theValue : null);
            }
        }
        if (theValue === undefined && colDef[propertyName] === undefined) {
            // already undefined, so don't set an own property to the same undefined value
            return;
        }
        colDef[propertyName] = theValue;
    }
    getUserColDefProperty(columnId, propertyName) {
        const userKey = `user.${columnId}.${propertyName}`;
        return this.colDefPropertyCache.get(userKey);
    }
    setupColumnCellStyle({ col, colId, abColumn }) {
        this.setColDefProperty(col, 'cellStyle', (userCellStyle) => {
            const quickSearchStyle = this.getQuickSearchCellStyle();
            const hasQuickSearchStyle = quickSearchStyle != undefined;
            const cellStyle = (params) => {
                const gridCell = this.getGridCellFromRowNode(params.node, abColumn.columnId);
                const isQuickSearchActive = hasQuickSearchStyle && this.isQuickSearchActive(gridCell, params);
                const userDefined = typeof userCellStyle === 'function' ? userCellStyle(params) : userCellStyle;
                const result = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, userDefined), this.getReadOnlyCellStyle(gridCell, params)), this.getEditableCellStyle(gridCell, params)), this.getFormatColumnAndStyledColumnCellStyle(gridCell, params)), (isQuickSearchActive ? quickSearchStyle : {})), this.getAlertCellStyle(gridCell, params)), this.getFlashingCellStyle(gridCell, params)), this.getCellHighlightStyle(gridCell, params));
                return (0, StyleHelper_1.normalizeStyleForAgGrid)(result);
            };
            return cellStyle;
        });
    }
    setupColumnCellClass({ col, colId, abColumn }) {
        this.setColDefProperty(col, 'cellClass', (userCellClass) => {
            const formatColumns = this.api.formatColumnApi.internalApi.getFormatColumnWithStyleClassNameForColumn(abColumn);
            const quickSearchStyleClassName = this.api.quickSearchApi.getQuickSearchStyle().ClassName;
            const hasQuickSearchStyleClassName = StringExtensions_1.StringExtensions.IsNotNullOrEmpty(quickSearchStyleClassName);
            const cellClass = (params) => {
                const gridCell = this.getGridCellFromRowNode(params.node, abColumn.columnId);
                // if a Visual Data export is in progress, we are interested only in the Excel Style Class
                if (this.api.exportApi.internalApi.isVisualDataExportInProgress()) {
                    const userDefinedCellClass = typeof userCellClass === 'function' ? userCellClass(params) : userCellClass;
                    const cellClassKey = this.getExcelClassNameForCell(colId, gridCell.primaryKeyValue, userDefinedCellClass);
                    return this.api.internalApi
                        .getReportService()
                        .getExcelStyleIdForCellClassKey(cellClassKey);
                }
                const isQuickSearchActive = hasQuickSearchStyleClassName && this.isQuickSearchActive(gridCell, params);
                const editableClassName = this.getEditableCellClass(gridCell, params);
                const readonlyClassName = this.getReadonlyCellClass(gridCell, params);
                const highlightAlertClassName = this.getAlertCellClass(gridCell, params);
                const flashingClassName = this.getFlashingCellClass(gridCell, params);
                const styledColumn = this.api.styledColumnApi.getStyledColumnForColumnId(colId);
                const hasStyledColumn = !!styledColumn && !styledColumn.IsSuspended;
                const noteClassName = this.getNoteCellClassName(gridCell, params);
                const returnValue = [
                    typeof userCellClass === 'function' ? userCellClass(params) : userCellClass,
                    !hasStyledColumn && formatColumns.length
                        ? this.getFormatColumnCellClass(formatColumns, abColumn, params)
                        : null,
                    isQuickSearchActive ? quickSearchStyleClassName : null,
                    editableClassName,
                    readonlyClassName,
                    highlightAlertClassName,
                    flashingClassName,
                    noteClassName,
                ]
                    // we flatten the array because some rules ('userCellClass' etc)  might return a string[]
                    .flat()
                    .filter((x) => !!x);
                const result = returnValue.length ? returnValue : undefined;
                return result;
            };
            return cellClass;
        });
    }
    getExcelClassNameForCell(colId, primaryKeyValue, userDefinedCellClass) {
        let excelClassName = `--excel-cell-${colId}-${primaryKeyValue}`;
        if (excelClassName.indexOf(' ') > 0) {
            excelClassName = excelClassName.replace(/\s/g, '_');
        }
        return userDefinedCellClass != null
            ? `${excelClassName}-${Array.isArray(userDefinedCellClass)
                ? userDefinedCellClass.join('-')
                : userDefinedCellClass}`
            : excelClassName;
    }
    setupColumnCellEditor({ colId, col }) {
        const adaptableColumn = this.api.columnApi.getColumnWithColumnId(colId);
        const editLookUpItem = this.api.userInterfaceApi.getEditLookUpItemForColumn(adaptableColumn);
        const hasRichSelectCellEditor = this.isAgGridModulePresent(core_1.ModuleNames.RichSelectModule);
        this.setColDefProperty(col, 'cellEditor', () => {
            if (editLookUpItem) {
                return hasRichSelectCellEditor ? 'agRichSelectCellEditor' : 'agSelectCellEditor';
            }
        });
        this.setColDefProperty(col, 'cellEditorPopup', () => {
            // as specified in https://www.ag-grid.com/react-data-grid/provided-cell-editors/#rich-select-cell-editor
            // agRichSelectCellEditor should always set cellEditorPopup=true. Otherwise the editor will be clipped to the cell contents
            if (editLookUpItem && hasRichSelectCellEditor) {
                return true;
            }
        });
        this.setColDefProperty(col, 'cellEditorParams', (params) => {
            if (editLookUpItem) {
                return (params) => {
                    const gridCell = this.getGridCellFromRowNode(params === null || params === void 0 ? void 0 : params.node, colId);
                    return {
                        values: this.api.userInterfaceApi.getEditLookUpValuesForEditLookUpItem(editLookUpItem, gridCell),
                    };
                };
            }
        });
    }
    setupColumnCellRenderer({ col, colId, abColumn }) {
        this.setColDefProperty(col, 'cellRenderer', () => {
            const styledColumn = this.api.styledColumnApi.getStyledColumnForColumnId(abColumn.columnId);
            if (styledColumn && !styledColumn.IsSuspended) {
                if (styledColumn.PercentBarStyle) {
                    return this.agGridHelper.createPercentBarRendererComp(styledColumn, abColumn);
                }
                if (styledColumn.BadgeStyle) {
                    return this.agGridHelper.createBadgeRendererComp(styledColumn, abColumn);
                }
                if (styledColumn.SparkLineStyle) {
                    return 'agSparklineCellRenderer';
                }
            }
        });
        this.setColDefProperty(col, 'cellRendererParams', (userDefined) => {
            const styledColumn = this.api.styledColumnApi.getStyledColumnForColumnId(abColumn.columnId);
            if (styledColumn && !styledColumn.IsSuspended) {
                if (styledColumn.SparkLineStyle) {
                    const sparklineOptions = (0, merge_1.default)({}, userDefined === null || userDefined === void 0 ? void 0 : userDefined.sparklineOptions, styledColumn.SparkLineStyle.options);
                    return Object.assign(Object.assign({}, userDefined), { sparklineOptions });
                }
            }
        });
    }
    setupColumnTooltipValueGetter({ col, colId, abColumn }) {
        let hasTooptip = false;
        this.setColDefProperty(col, 'tooltipValueGetter', () => {
            const styledColumn = this.api.styledColumnApi.getStyledColumnForColumnId(colId);
            if (styledColumn &&
                !styledColumn.IsSuspended &&
                styledColumn.PercentBarStyle &&
                styledColumn.PercentBarStyle.ToolTipText) {
                hasTooptip = true;
                if (styledColumn === null || styledColumn === void 0 ? void 0 : styledColumn.PercentBarStyle) {
                    return (params) => {
                        const min = this.api.styledColumnApi.internalApi.getNumericStyleMinValue(styledColumn, abColumn, params.node, params.value);
                        const max = this.api.styledColumnApi.internalApi.getNumericStyleMaxValue(styledColumn, abColumn, params.node, params.value);
                        const textOptions = styledColumn.PercentBarStyle.ToolTipText;
                        let returnValue = '';
                        if (textOptions.includes('CellValue')) {
                            returnValue = params.value;
                        }
                        if (textOptions.includes('PercentageValue')) {
                            const clampedValue = Helper_1.Helper.clamp(params.value, min, max);
                            const percentageValue = ((clampedValue - min) / (max - min)) * 100;
                            returnValue += ' ' + `(${percentageValue.toFixed(0)}%)`;
                        }
                        return returnValue ? returnValue : params.value;
                    };
                }
            }
        });
    }
    setupColumnQuickFilerText({ col, abColumn }) {
        this.setColDefProperty(col, 'getQuickFilterText', (userGetQuickFilterText) => {
            if (userGetQuickFilterText) {
                return userGetQuickFilterText;
            }
            return (params) => {
                const visibleCoulmnsMap = this.api.layoutApi.getCurrentVisibleColumnIdsMap();
                const isVisible = visibleCoulmnsMap[abColumn.columnId];
                if (!isVisible) {
                    return '';
                }
                return this.getDisplayValueFromRowNode(params.node, abColumn.columnId);
            };
        });
    }
    setupAllowedAggFuncs({ col, abColumn }) {
        this.setColDefProperty(col, 'allowedAggFuncs', () => {
            return abColumn.availableAggregationFunctions;
        });
    }
    triggerSetupColumnKeyCreator(colId) {
        const col = this.gridOptions.columnApi.getColumn(colId);
        const colDef = col.getColDef();
        const abColumn = this.api.columnApi.getColumnWithColumnId(colId);
        const colSetupInfo = {
            col,
            colDef,
            colId,
            abColumn,
        };
        this.setupColumnKeyCreator(colSetupInfo);
    }
    setupColumnKeyCreator(columnSetupInfo) {
        const { col, abColumn } = columnSetupInfo;
        const adaptableOptions = this.adaptableOptions;
        const adaptableApi = this.api;
        this.setColDefProperty(col, 'keyCreator', (userPropertyValue) => {
            return (params) => {
                var _a;
                if (typeof userPropertyValue === 'function') {
                    return userPropertyValue(params);
                }
                const value = params.value;
                const unbalancedGroupsKey = (_a = adaptableOptions.groupingOptions) === null || _a === void 0 ? void 0 : _a.unbalancedGroupsKey;
                if (!unbalancedGroupsKey) {
                    return value;
                }
                let groupUnbalancedGroupsUnderKeyValue = typeof unbalancedGroupsKey === 'function'
                    ? unbalancedGroupsKey({
                        adaptableApi: adaptableApi,
                        userName: this.adaptableOptions.userName,
                        adaptableId: this.adaptableOptions.adaptableId,
                        adaptableColumn: abColumn,
                        params,
                    })
                    : unbalancedGroupsKey;
                return value === null || value === undefined ? groupUnbalancedGroupsUnderKeyValue : value;
            };
        });
    }
    setupColumnCellDataType(columnSetupInfo) {
        const { col } = columnSetupInfo;
        // AG Grid introduced since v30.x an inferred cellDataType
        // the problem is that it breaks the default value formatter and/or editor (especially for Date columns)
        this.setColDefProperty(col, 'cellDataType', () => {
            return false;
        });
    }
    setupColumnHeader({ col, abColumn }) {
        var _a, _b;
        const previousColumnHeader = (_a = col === null || col === void 0 ? void 0 : col.getColDef()) === null || _a === void 0 ? void 0 : _a.headerName;
        this.setColDefProperty(col, 'headerName', (userHeaderName) => {
            var _a;
            // set the default to the AG Grid provided values
            // from https://github.com/ag-grid/ag-grid/blob/v26.1.0/community-modules/core/src/ts/columns/columnModel.ts#L2515
            let resultHeaderName = userHeaderName !== null && userHeaderName !== void 0 ? userHeaderName : StringExtensions_1.StringExtensions.CamelCaseToHumanText(col.getColDef().field);
            const layoutCustomHeader = (_a = this.api.layoutApi.getCurrentLayout().ColumnHeadersMap) === null || _a === void 0 ? void 0 : _a[col.getColId()];
            if (layoutCustomHeader) {
                resultHeaderName = layoutCustomHeader;
            }
            // required here for the initial layout rendering
            // Removed by JW, 3 october 2023; i don't think we need it and it overrides stuff unnecessarily
            //  abColumn.friendlyName = resultHeaderName;
            return resultHeaderName;
        });
        const newColumnHeader = (_b = col === null || col === void 0 ? void 0 : col.getColDef()) === null || _b === void 0 ? void 0 : _b.headerName;
        return previousColumnHeader !== newColumnHeader;
    }
    setupColumnFilter({ col, colDef }) {
        this.setColDefProperty(col, 'filter', () => {
            if (!colDef.filter) {
                return;
            }
            if (!this.adaptableOptions.columnFilterOptions.useAdaptableColumnFiltering) {
                return;
            }
            this.gridOptions.api.destroyFilter(col);
            return (0, FilterWrapper_1.FilterWrapperFactory)(this);
        });
    }
    setupColumnFloatingFilter({ col, colDef }) {
        const isFloatingFilterDisabled = !colDef.floatingFilter ||
            !this.adaptableOptions.columnFilterOptions.useAdaptableColumnFiltering;
        this.setColDefProperty(col, 'floatingFilterComponent', () => {
            if (isFloatingFilterDisabled) {
                return;
            }
            return (0, FloatingFilterWrapper_1.FloatingFilterWrapperFactory)(this);
        });
        this.setColDefProperty(col, 'floatingFilterComponentParams', () => {
            if (isFloatingFilterDisabled) {
                return;
            }
            return {
                suppressFilterButton: true,
            };
        });
    }
    setupColumnValueFormatter({ col, abColumn }) {
        this.setColDefProperty(col, 'valueFormatter', (userPropertyValue) => {
            const activeFormatColumnsWithDisplayFormat = this.api.formatColumnApi.internalApi.getFormatColumnsWithDisplayFormatForColumn(abColumn);
            if (!activeFormatColumnsWithDisplayFormat.length) {
                return;
            }
            return (params) => {
                const { node, value } = params;
                const mostRelevantFormatColumn = this.api.formatColumnApi.internalApi.getMostRelevantFormatColumnForColumn(activeFormatColumnsWithDisplayFormat, abColumn, { node, value });
                if (!mostRelevantFormatColumn) {
                    // ALL FormatColumns are conditional and NONE of them are relevant for this row
                    return value;
                }
                const options = mostRelevantFormatColumn.DisplayFormat.Options;
                if (mostRelevantFormatColumn.DisplayFormat.Formatter === 'NumberFormatter') {
                    // change the Number format - if the scope allows it
                    if (this.api.scopeApi.isColumnInNumericScope(abColumn, mostRelevantFormatColumn.Scope)) {
                        return this.api.formatColumnApi.internalApi.getNumberFormattedValue(params.value, params.node, abColumn, options);
                    }
                }
                if (mostRelevantFormatColumn.DisplayFormat.Formatter === 'DateFormatter') {
                    // change the Date format - if the scope allows it
                    if (this.api.scopeApi.isColumnInDateScope(abColumn, mostRelevantFormatColumn.Scope)) {
                        return this.api.formatColumnApi.internalApi.getDateFormattedValue(params.value, params.node, abColumn, options);
                    }
                }
                if (mostRelevantFormatColumn.DisplayFormat.Formatter === 'StringFormatter') {
                    // change the String format - if the scope allows it
                    if (this.api.scopeApi.isColumnInStringsScope(abColumn, mostRelevantFormatColumn.Scope)) {
                        return this.api.formatColumnApi.internalApi.getStringFormattedValue(params.value, params.node, abColumn, options);
                    }
                }
                // should NEVER arrive at this line, but just to be sure
                return value;
            };
        });
    }
    setupColumnEditable({ col }) {
        this.setColDefProperty(col, 'editable', (userValue) => {
            // if AG Grid defines the column as NOT editable, we don't mess with it
            if (typeof userValue === 'boolean' && userValue === false) {
                return userValue;
            }
            const cellEditableFn = this.adaptableOptions.editOptions.isCellEditable;
            if (!cellEditableFn) {
                return userValue;
            }
            const editableCallback = (params) => {
                const gridCell = this.getGridCellFromRowNode(params.node, params.column.getColId());
                const cellEditableContext = {
                    gridCell,
                    adaptableApi: this.api,
                    userName: this.adaptableOptions.userName,
                    adaptableId: this.adaptableOptions.adaptableId,
                };
                return cellEditableFn(cellEditableContext);
            };
            return editableCallback;
        });
    }
    setupColumnValueSetter({ col, colId, abColumn }) {
        this.setColDefProperty(col, 'valueSetter', (userValueSetter) => {
            var _a;
            const preventEditAlertsForColumn = this.api.alertApi.internalApi
                .getAlertDefinitionsWithPreventEdit()
                .filter((alertDefinition) => {
                return this.api.scopeApi.isColumnInScope(abColumn, alertDefinition.Scope);
            });
            const noValidations = !preventEditAlertsForColumn.length && !((_a = this.adaptableOptions.editOptions) === null || _a === void 0 ? void 0 : _a.validateOnServer);
            if (noValidations) {
                return;
            }
            const valueSetter = (params) => {
                var _a;
                const field = params.column.getColDef().field;
                if (noValidations) {
                    //TODO also consider the case when userValueSetter is a string
                    if (typeof userValueSetter === 'function') {
                        return userValueSetter(params);
                    }
                    // we allowed it go reach this point
                    // just to run isCellEditable
                    // and since this has already run and we have no other validations
                    // just assign the new value and exit
                    if (field) {
                        params.data[field] = params.newValue;
                    }
                    return true;
                }
                const cellDataChangedInfo = this.api.internalApi.buildDataChangedInfo({
                    oldValue: params.oldValue,
                    newValue: params.newValue,
                    column: this.api.columnApi.getColumnWithColumnId(params.column.getColId()),
                    primaryKeyValue: this.getPrimaryKeyValueFromRowNode(params.node),
                    rowNode: params.node,
                    trigger: 'edit',
                });
                if (cellDataChangedInfo.oldValue === cellDataChangedInfo.newValue) {
                    return true;
                }
                /**
                 * Validate on the future row, with the new value.
                 * structuredClone fails, it contains functions.
                 */
                const newRow = Object.assign(Object.assign({}, params.node), { data: Object.assign({}, params.node.data) });
                newRow.data[field] = params.newValue;
                const cellDataChangeInfoForSyncValidation = Object.assign(Object.assign({}, cellDataChangedInfo), { rowNode: newRow });
                if (!this.ValidationService.performValidation(cellDataChangeInfoForSyncValidation)) {
                    return false;
                }
                const onServerValidationCompleted = () => { };
                if ((_a = this.adaptableOptions.editOptions) === null || _a === void 0 ? void 0 : _a.validateOnServer) {
                    this.ValidationService.performServerValidation(cellDataChangedInfo, {
                        onServerValidationCompleted,
                    })();
                }
                //TODO also consider the case when userValueSetter is a string
                if (typeof userValueSetter === 'function') {
                    return userValueSetter(params);
                }
                if (field) {
                    params.data[field] = params.newValue;
                }
                else {
                    throw `Cannot edit a column without a field - column id was ${colId}`;
                }
                return true;
            };
            return valueSetter;
        });
    }
    setupColumnComparator({ col, colId, abColumn }) {
        const customSort = this.api.customSortApi.getCustomSortForColumn(colId);
        const columnSortComparer = this.api.customSortApi.internalApi.getCustomSortComparer(abColumn);
        const comparatorGetter = (propName) => {
            return () => {
                return this.getActiveColumnComparator(colId, customSort, columnSortComparer);
            };
        };
        this.setColDefProperty(col, 'comparator', comparatorGetter('comparator'));
        this.setColDefProperty(col, 'pivotComparator', comparatorGetter('pivotComparator'));
    }
    applyCurrentTheme() {
        this.api.themeApi.applyCurrentTheme();
    }
    applyFinalRendering() {
        this.embedColumnMenu = this.isAgGridModulePresent(core_1.ModuleNames.MenuModule);
        if (this.gridOptions.treeData && this.gridOptions.treeData == true) {
            this.api.internalApi.setTreeModeOn();
        }
        else {
            this.api.internalApi.setTreeModeOff();
        }
        this.agGridHelper.checkShouldClearExistingFiltersOrSearches();
    }
    isAgGridModulePresent(moduleName) {
        const standardAgGridRegistrationCheck = core_1.ModuleRegistry.__isRegistered(moduleName, this.gridOptions.gridId);
        if (standardAgGridRegistrationCheck) {
            return true;
        }
        const allAgGridRegisteredModules = this.getAllAgGridRegisteredModules();
        return !!allAgGridRegisteredModules[moduleName];
    }
    getAllAgGridRegisteredModules() {
        // as of AG Grid 30.0.3 the ModuleRegistry gets an dynamic grid ID for the framework versions
        // because of that we have to check ALL grid IDs to see if the module is registered
        // TODO AFL: we should recheck this later, it seems AG Grid is already refactoring this faulty registration process
        // @ts-ignore
        const gridModulesMap = core_1.ModuleRegistry.gridModulesMap;
        let allModulesMap = {};
        Object.keys(gridModulesMap).forEach((gridId) => {
            allModulesMap = Object.assign(Object.assign({}, allModulesMap), gridModulesMap[gridId]);
        });
        return allModulesMap;
    }
    getAgGridRegisteredModules() {
        // see comment from getAllAgGridRegisteredModules()
        // return ModuleRegistry.__getRegisteredModules(this.gridOptions.gridId);
        return Object.values(this.getAllAgGridRegisteredModules());
    }
    getAgGridRegisteredModuleNames() {
        return this.getAgGridRegisteredModules().map((module) => module.moduleName);
    }
    onRowDataChanged({ rowNode, oldData, newData, }) {
        if (oldData == null || oldData == undefined) {
            return;
        }
        if (oldData == newData) {
            return;
        }
        const primaryKeyValue = this.getPrimaryKeyValueFromRowNode(rowNode);
        if (!primaryKeyValue) {
            return;
        }
        // rowNode = this.getRowNodeForPrimaryKey(primaryKeyValue);
        let cellDataChangedInfos = [];
        Object.keys(oldData).forEach((key) => {
            if (this.api.columnApi.isColumnInGrid(key)) {
                const oldValue = oldData[key];
                const newValue = newData[key];
                if (oldValue != newValue) {
                    const cellDataChangedInfo = this.api.internalApi.buildDataChangedInfo({
                        oldValue: oldValue,
                        newValue: newValue,
                        column: this.api.columnApi.getColumnWithColumnId(key),
                        primaryKeyValue: primaryKeyValue,
                        rowNode: rowNode,
                        trigger: 'tick',
                    });
                    if (this.isUndoChange(cellDataChangedInfo)) {
                        cellDataChangedInfo.trigger = 'undo';
                    }
                    cellDataChangedInfos.push(cellDataChangedInfo);
                }
            }
        });
        this.performPostEditChecks(cellDataChangedInfos);
    }
    onCellDataChanged({ rowNode, oldValue, newValue, colId, }) {
        if (oldValue == newValue) {
            return;
        }
        const abColumn = this.api.columnApi.getColumnWithColumnId(colId);
        if (!abColumn) {
            return;
        }
        if (this.isGroupRowNode(rowNode)) {
            const cellDataChangedInfo = this.api.internalApi.buildDataChangedInfo({
                oldValue: oldValue,
                newValue: newValue,
                column: abColumn,
                /**
                 * A grouped row does not have an underling data item.
                 * Because of this we use the rowNode.id as an identifier.
                 */
                primaryKeyValue: rowNode.id,
                rowNode: rowNode,
                trigger: 'aggChange',
            });
            this.DataService.CreateDataChangedEvent(cellDataChangedInfo);
            return;
        }
        const primaryKeyValue = this.getPrimaryKeyValueFromRowNode(rowNode);
        if (!primaryKeyValue) {
            return;
        }
        const cellDataChangedInfo = this.api.internalApi.buildDataChangedInfo({
            oldValue: oldValue,
            newValue: newValue,
            column: abColumn,
            primaryKeyValue: primaryKeyValue,
            rowNode: rowNode,
            trigger: 'edit',
        });
        if (this.isUndoChange(cellDataChangedInfo)) {
            cellDataChangedInfo.trigger = 'undo';
        }
        this.performPostEditChecks([cellDataChangedInfo]);
    }
    isUndoChange(dataChange) {
        // check if this is not a reverted change
        const undoChange = this.api.internalApi.getDataService().extractUndoChange(dataChange);
        return !!undoChange;
    }
    /**
     * There are a few things that we need to do AFTER we edit a cell and it makes sense to put them in one place
     */
    performPostEditChecks(cellDataChangedInfos) {
        const firstInfo = cellDataChangedInfos[0];
        if (!firstInfo || !firstInfo.rowNode) {
            return;
        }
        cellDataChangedInfos.forEach((cellDataChangedInfo) => {
            if (cellDataChangedInfo.trigger === 'undo') {
                this.logger.info(`Undo data change: PK(${cellDataChangedInfo.primaryKeyValue}) Col(${cellDataChangedInfo.column}) RevertedValue(${cellDataChangedInfo.oldValue}) OriginalValue(${cellDataChangedInfo.newValue})`);
            }
            if (cellDataChangedInfo.trigger === 'edit' || cellDataChangedInfo.trigger === 'undo') {
                this.checkChangedCellCurrentlySelected(cellDataChangedInfo);
                this.api.freeTextColumnApi.internalApi.handleFreeTextColumnDataChange(cellDataChangedInfo);
            }
            this.DataService.CreateDataChangedEvent(cellDataChangedInfo);
            this.resetMinMaxCachedValueForColumn(cellDataChangedInfo.column);
        });
        this.refreshCellsBasedOnCellDataChange(cellDataChangedInfos);
        firstInfo.trigger == 'tick' ? this.filterOnTickingDataChange() : this.filterOnEditDataChange();
    }
    refreshCellsBasedOnCellDataChange(cellDataChangedInfos) {
        const [firstInfo] = cellDataChangedInfos;
        // if node is visible then check if need to refresh other columns / whole row if the updating column is:
        // 1. referenced in Format Column Styles that have Expressions (refreshing whole row if Scope is All)
        // 2. referenced in Format Column styles that use Column Comparisons (which might also be calculated columns)
        if (this.isVisibleNode(firstInfo.rowNode)) {
            let dataChangedScope = {
                wholeRow: false,
                columnIds: new Set(),
            };
            this.getFormatColumnExpressionStylesChanges(dataChangedScope, cellDataChangedInfos);
            if (dataChangedScope.wholeRow === false) {
                this.getFormatColumnPredicateStyleChanges(dataChangedScope, cellDataChangedInfos);
            }
            if (dataChangedScope.wholeRow) {
                this.redrawRow(firstInfo.rowNode);
            }
            else {
                this.getStyledColumnComparisonChanges(dataChangedScope, cellDataChangedInfos);
                if (dataChangedScope.columnIds.size > 0) {
                    this.refreshCells([firstInfo.rowNode], Array.from(dataChangedScope.columnIds.values()), true);
                }
            }
        }
        this.refreshColumnForRelativeRangeStyledColumns(cellDataChangedInfos);
    }
    getFormatColumnExpressionStylesChanges(dataChangedScope, cellDataChangedInfos) {
        const formatColumnsWithExpression = [];
        formatColumnsWithExpression.push(...this.api.formatColumnApi.internalApi.getFormatColumnsWithExpression());
        if (ArrayExtensions_1.ArrayExtensions.IsNullOrEmpty(formatColumnsWithExpression)) {
            return;
        }
        cellDataChangedInfos.forEach((cellDataChangedInfo) => {
            if (!dataChangedScope.wholeRow) {
                formatColumnsWithExpression.forEach((styleModule) => {
                    if (!dataChangedScope.wholeRow) {
                        const columnIds = this.api.expressionApi.getColumnsFromExpression(styleModule.Rule.BooleanExpression);
                        if (columnIds.includes(cellDataChangedInfo.column.columnId)) {
                            if (this.api.scopeApi.scopeIsAll(styleModule.Scope)) {
                                dataChangedScope.wholeRow = true;
                                return;
                            }
                            else {
                                this.api.scopeApi
                                    .getColumnsForScope(styleModule.Scope)
                                    .map((c) => c.columnId)
                                    .forEach((colId) => {
                                    dataChangedScope.columnIds.add(colId);
                                });
                            }
                        }
                    }
                });
            }
        });
    }
    getFormatColumnPredicateStyleChanges(dataChangedScope, cellDataChangedInfos) {
        cellDataChangedInfos.forEach((cellDataChangeInfo) => {
            const dependentColumns = this.api.formatColumnApi.internalApi.getFormatColumnColumnsDependentOnColumnChange(cellDataChangeInfo.column);
            for (let colId of dependentColumns) {
                dataChangedScope.columnIds.add(colId);
            }
        });
    }
    getStyledColumnComparisonChanges(dataChangedScope, cellDataChangedInfos) {
        this.api.styledColumnApi.getStyledColumns().forEach((sc) => {
            let columnComparison = this.api.styledColumnApi.internalApi.getColumnComparisonForStyledColumn(sc);
            if (columnComparison) {
                let affectedColumnIds = this.api.styledColumnApi.internalApi.getColumnIdsFromColumnComparison(columnComparison);
                if (ArrayExtensions_1.ArrayExtensions.IsNotNullOrEmpty(affectedColumnIds)) {
                    cellDataChangedInfos.forEach((cellDataChangedInfo) => {
                        if (affectedColumnIds.includes(cellDataChangedInfo.column.columnId)) {
                            dataChangedScope.columnIds.add(sc.ColumnId);
                        }
                    });
                }
            }
        });
    }
    checkChangedCellCurrentlySelected(cellDataChangedInfo) {
        let selectedCellInfo = this.api.gridApi.getSelectedCellInfo();
        if (selectedCellInfo && ArrayExtensions_1.ArrayExtensions.IsNotNullOrEmpty(selectedCellInfo.gridCells)) {
            let matchingCell = selectedCellInfo.gridCells.find((gc) => gc.primaryKeyValue == cellDataChangedInfo.primaryKeyValue &&
                gc.column == cellDataChangedInfo.column);
            if (matchingCell) {
                this.setSelectedCells();
            }
        }
        let selectedRowInfo = this.api.gridApi.getSelectedRowInfo();
        if (selectedRowInfo && ArrayExtensions_1.ArrayExtensions.IsNotNullOrEmpty(selectedRowInfo.gridRows)) {
            let matchingRow = selectedRowInfo.gridRows.find((gr) => gr.primaryKeyValue == cellDataChangedInfo.primaryKeyValue);
            if (matchingRow) {
                this.setSelectedRows();
            }
        }
    }
    refreshColumnForRelativeRangeStyledColumns(cellDataChangedInfos) {
        const columnIdMap = new Set();
        cellDataChangedInfos.forEach((cellDataChangeInfo) => {
            const styledColumn = this.api.styledColumnApi.getActiveStyledColumnForColumn(cellDataChangeInfo.column);
            if (styledColumn &&
                this.api.styledColumnApi.internalApi.hasStyledColumnRelativeCellRange(styledColumn)) {
                columnIdMap.add(styledColumn.ColumnId);
            }
        });
        const columnIdsToUpdate = [...columnIdMap];
        if (columnIdsToUpdate.length) {
            this.refreshColumns(columnIdsToUpdate, true);
        }
    }
    getColumnSorts() {
        const columnSorts = [];
        const columnState = this.gridOptions.columnApi.getColumnState();
        columnState.forEach((colState) => {
            const { colId } = colState;
            if (colState.sort && colState.sortIndex != null) {
                columnSorts.push({
                    ColumnId: colId,
                    SortOrder: colState.sort === 'asc' ? 'Asc' : 'Desc',
                    SortIndex: colState.sortIndex,
                });
            }
        });
        columnSorts.sort((a, b) => a.SortIndex - b.SortIndex);
        return columnSorts.map((c) => {
            return {
                ColumnId: c.ColumnId,
                SortOrder: c.SortOrder,
            };
        });
    }
    onSortChanged() {
        const columnSorts = this.getColumnSorts();
        this.api.gridApi.setColumnSorts(columnSorts);
        this._emit('SortChanged', columnSorts);
    }
    expandAllRowGroups() {
        this.gridOptions.api.forEachNode((node) => {
            if (node.group) {
                node.expanded = true;
            }
        });
        this.gridOptions.api.onGroupExpandedOrCollapsed();
    }
    closeAllRowGroups() {
        this.gridOptions.api.forEachNode((node) => {
            if (node.group) {
                node.expanded = false;
            }
        });
        this.gridOptions.api.onGroupExpandedOrCollapsed();
    }
    expandRowGroupsForValues(columnValues) {
        if (ArrayExtensions_1.ArrayExtensions.IsNotNullOrEmpty(columnValues)) {
            const expandedKeys = columnValues.reduce((acc, key) => {
                acc[key] = true;
                return acc;
            }, {});
            this.gridOptions.api.forEachNode((node) => {
                if (node.group && !node.expanded) {
                    const nodePath = [];
                    let current = node;
                    while (current) {
                        nodePath.push(current.key);
                        current = current.parent;
                    }
                    const nodeKey = nodePath
                        .filter((x) => !!x)
                        .reverse()
                        .join(GROUP_PATH_SEPARATOR);
                    if (expandedKeys[nodeKey]) {
                        node.setExpanded(true);
                    }
                }
            });
            this.gridOptions.api.onGroupExpandedOrCollapsed();
        }
    }
    getAgGridColumnForColumnId(columnId) {
        return this.gridOptions.columnApi.getColumn(columnId);
    }
    getAgGridAllGridColumns() {
        return this.gridOptions.columnApi.getAllGridColumns();
    }
    setRowGroupColumns(columnIds) {
        this.gridOptions.columnApi.setRowGroupColumns(columnIds);
    }
    clearRowGroupColumns() {
        this.gridOptions.columnApi.removeRowGroupColumns(this.gridOptions.columnApi.getRowGroupColumns());
    }
    getExpandRowGroupsKeys() {
        let returnValues = [];
        if (this.api.layoutApi.internalApi.areExpandedRowGroupsSavedInLayouts()) {
            this.gridOptions.api.forEachNode((node) => {
                if (node.group && node.expanded) {
                    let current = node;
                    const path = [];
                    while (current) {
                        path.push(current.key);
                        current = current.parent;
                    }
                    returnValues.push(path
                        .filter((x) => !!x)
                        .reverse()
                        .join(GROUP_PATH_SEPARATOR));
                }
            });
        }
        return returnValues;
    }
    getRowCount() {
        return this.gridOptions.rowData
            ? this.getGridData().length
            : this.gridOptions.api.getDisplayedRowCount();
    }
    getColumnCount() {
        return this.gridOptions.columnApi.getColumns().length;
    }
    getVisibleRowCount() {
        return this.gridOptions.api.getDisplayedRowCount();
    }
    getVisibleColumnCount() {
        return this.gridOptions.columnApi.getColumns().filter((c) => c.isVisible()).length;
    }
    selectColumns(columnIds, config) {
        if (!(config === null || config === void 0 ? void 0 : config.keepExistingSelection)) {
            this.gridOptions.api.clearRangeSelection();
        }
        const rowCount = this.gridOptions.api.getDisplayedRowCount();
        columnIds.forEach((colId) => {
            const cellRangeParams = {
                rowStartIndex: 0,
                rowEndIndex: rowCount - 1,
                columnStart: colId,
                columnEnd: colId,
            };
            this.gridOptions.api.addCellRange(cellRangeParams);
        });
    }
    selectColumn(columnId, config) {
        if (!(config === null || config === void 0 ? void 0 : config.keepExistingSelection)) {
            this.gridOptions.api.clearRangeSelection();
        }
        const cellRangeParams = {
            rowStartIndex: 0,
            rowEndIndex: this.gridOptions.api.getDisplayedRowCount() - 1,
            columnStart: columnId,
            columnEnd: columnId,
        };
        this.gridOptions.api.addCellRange(cellRangeParams);
    }
    selectAll() {
        this.gridOptions.api.selectAll();
    }
    deselectAll() {
        // need to do both as first just clears selected rows and second clears ranges
        this.gridOptions.api.deselectAll();
        this.gridOptions.api.clearRangeSelection();
    }
    hideColumn(columnId) {
        let agGridColumn = this.gridOptions.columnApi.getColumn(columnId);
        if (agGridColumn) {
            this.gridOptions.columnApi.setColumnVisible(columnId, false);
            this.updateColumnsIntoStore();
        }
    }
    showColumn(columnId) {
        let agGridColumn = this.gridOptions.columnApi.getColumn(columnId);
        if (agGridColumn) {
            this.gridOptions.columnApi.setColumnVisible(columnId, true);
            this.updateColumnsIntoStore();
        }
    }
    autoSizeColumn(columnId) {
        var _a;
        (_a = this.gridOptions.columnApi) === null || _a === void 0 ? void 0 : _a.autoSizeColumn(columnId);
    }
    autoSizeColumns(columnIds) {
        var _a;
        (_a = this.gridOptions.columnApi) === null || _a === void 0 ? void 0 : _a.autoSizeColumns(columnIds);
    }
    autoSizeAllColumns() {
        var _a;
        (_a = this.gridOptions.columnApi) === null || _a === void 0 ? void 0 : _a.autoSizeAllColumns();
    }
    setColumnSort(columnSorts) {
        const columnSortsMap = columnSorts === null || columnSorts === void 0 ? void 0 : columnSorts.reduce((acc, columnSort, index) => {
            acc[columnSort.ColumnId] = Object.assign(Object.assign({}, columnSort), { SortIndex: index });
            return acc;
        }, {});
        if (!this.gridOptions.api) {
            return;
        }
        const newColumnState = this.gridOptions.columnApi.getColumnState().map((colState) => {
            const { colId } = colState;
            const state = Object.assign({}, colState);
            const colSort = columnSortsMap ? columnSortsMap[colId] : undefined;
            if (colSort) {
                state.sort = colSort.SortOrder === 'Asc' ? 'asc' : 'desc';
                state.sortIndex = colSort.SortIndex;
            }
            else {
                state.sort = null;
                state.sortIndex = null;
            }
            return state;
        });
        this.gridOptions.columnApi.applyColumnState({
            state: newColumnState,
            applyOrder: true,
        });
        this.gridOptions.api.onSortChanged();
    }
    clearColumnSort() {
        this.setColumnSort(null);
    }
    setGridData(dataSource) {
        if (!this.gridOptions.api) {
            return;
        }
        if (this.hasAutogeneratedPrimaryKey()) {
            this.addSyntheticPrimaryKey(dataSource);
        }
        this.gridOptions.api.setRowData(dataSource);
        this.updateColumnDataTypeIfRowDataIsEmpty();
        this.updateRowGroupsExpandedState();
        this.setupColumns();
    }
    getGridData() {
        var _a;
        const data = [];
        (_a = this.gridOptions) === null || _a === void 0 ? void 0 : _a.api.forEachNode((rowNode) => {
            if (!this.isGroupRowNode(rowNode)) {
                data.push(rowNode.data);
            }
        });
        return data;
    }
    getFilteredData() {
        var _a;
        const data = [];
        (_a = this.gridOptions) === null || _a === void 0 ? void 0 : _a.api.forEachNodeAfterFilter((rowNode) => {
            if (!this.isGroupRowNode(rowNode)) {
                data.push(rowNode.data);
            }
        });
        return data;
    }
    updateRows(dataRows, dataUpdateConfig) {
        if (this.hasAutogeneratedPrimaryKey()) {
            this.addSyntheticPrimaryKeyIfMissing(dataRows);
        }
        dataUpdateConfig = dataUpdateConfig || {};
        if (dataUpdateConfig.runAsync) {
            return new Promise((resolve) => {
                this.gridOptions.api.applyTransactionAsync({ update: dataRows }, (transaction) => {
                    if (typeof dataUpdateConfig.callback === 'function') {
                        dataUpdateConfig.callback(transaction);
                    }
                    resolve(transaction === null || transaction === void 0 ? void 0 : transaction.update);
                });
            });
        }
        else {
            const transaction = this.gridOptions.api.applyTransaction({
                update: dataRows,
            });
            return Promise.resolve(transaction === null || transaction === void 0 ? void 0 : transaction.update);
        }
    }
    addRows(dataRows, dataUpdateConfig) {
        if (this.hasAutogeneratedPrimaryKey()) {
            this.addSyntheticPrimaryKey(dataRows);
        }
        dataUpdateConfig = dataUpdateConfig || {};
        const newData = { add: dataRows };
        if (dataUpdateConfig.addIndex !== undefined) {
            newData.addIndex = dataUpdateConfig.addIndex;
        }
        if (dataUpdateConfig.runAsync) {
            return new Promise((resolve) => {
                this.gridOptions.api.applyTransactionAsync(newData, (transaction) => {
                    if (typeof dataUpdateConfig.callback === 'function') {
                        dataUpdateConfig.callback(transaction);
                    }
                    resolve(transaction === null || transaction === void 0 ? void 0 : transaction.add);
                    this.updateColumnDataTypeIfRowDataIsEmpty();
                    this.updateRowGroupsExpandedState();
                });
            });
        }
        else {
            const transaction = this.gridOptions.api.applyTransaction(newData);
            this.updateColumnDataTypeIfRowDataIsEmpty();
            this.updateRowGroupsExpandedState();
            return Promise.resolve(transaction === null || transaction === void 0 ? void 0 : transaction.add);
        }
    }
    addOrUpdateRows(dataRows, dataUpdateConfig) {
        const addDataRows = [];
        const updateDataRows = [];
        const primaryKey = this.adaptableOptions.primaryKey;
        dataRows.forEach((dataRow) => {
            const node = this.getRowNodeForPrimaryKey(dataRow[primaryKey]);
            if (node) {
                updateDataRows.push(dataRow);
            }
            else {
                addDataRows.push(dataRow);
            }
        });
        if (this.hasAutogeneratedPrimaryKey()) {
            this.addSyntheticPrimaryKeyIfMissing(addDataRows);
        }
        dataUpdateConfig = dataUpdateConfig || {};
        if (dataUpdateConfig.runAsync) {
            return new Promise((resolve) => {
                this.gridOptions.api.applyTransactionAsync({ update: updateDataRows, add: addDataRows, addIndex: dataUpdateConfig.addIndex }, (transaction) => {
                    if (typeof dataUpdateConfig.callback === 'function') {
                        dataUpdateConfig.callback(transaction);
                    }
                    resolve({
                        added: transaction === null || transaction === void 0 ? void 0 : transaction.add,
                        updated: transaction === null || transaction === void 0 ? void 0 : transaction.update,
                    });
                });
            });
        }
        else {
            const transaction = this.gridOptions.api.applyTransaction({
                update: updateDataRows,
                add: addDataRows,
                addIndex: dataUpdateConfig.addIndex,
            });
            return Promise.resolve({
                added: transaction === null || transaction === void 0 ? void 0 : transaction.add,
                updated: transaction === null || transaction === void 0 ? void 0 : transaction.update,
            });
        }
    }
    deleteRows(dataRows, dataUpdateConfig) {
        dataUpdateConfig = dataUpdateConfig || {};
        if (dataUpdateConfig.runAsync) {
            return new Promise((resolve) => {
                this.gridOptions.api.applyTransactionAsync({ remove: dataRows }, (transaction) => {
                    if (typeof dataUpdateConfig.callback === 'function') {
                        dataUpdateConfig.callback(transaction);
                    }
                    resolve(transaction === null || transaction === void 0 ? void 0 : transaction.remove);
                });
            });
        }
        else {
            const transaction = this.gridOptions.api.applyTransaction({
                remove: dataRows,
            });
            return Promise.resolve(transaction.remove);
        }
    }
    getFirstGroupedColumn() {
        let groupedColumns = this.gridOptions.columnApi.getRowGroupColumns();
        if (ArrayExtensions_1.ArrayExtensions.IsNotNullOrEmpty(groupedColumns)) {
            let groupedColumn = groupedColumns[0];
            return this.api.columnApi.getColumnWithColumnId(groupedColumn.getColId());
        }
    }
    checkColumnsDataTypeSet() {
        // check that we have no unknown columns - if we do then ok
        const colDefs = this.api.columnApi.getColumns();
        if (colDefs.some((colDef) => colDef.dataType === 'Unknown')) {
            this.updateColumnsIntoStore();
        }
    }
    updateColumnDataTypeIfRowDataIsEmpty() {
        var _a, _b;
        // gridOptions?.rowData is not updated when setting data via the api
        if (!this.isCheckedColumnDataType && !((_b = (_a = this.gridOptions) === null || _a === void 0 ? void 0 : _a.rowData) === null || _b === void 0 ? void 0 : _b.length)) {
            // the columns dataType is based on the rawData, when type is not set on the column
            // when data is loaded async, the dataType initially is dataType=unknown
            // to fix this, we need to update the columnDefs when we data is loaded
            this.checkColumnsDataTypeSet();
            // this influences the floating filter
            // need to trigger header redraw, if not, the columnDef update is not picked-up
            this.redrawHeader();
            // ensures the check is performed only once
            this.isCheckedColumnDataType = true;
        }
    }
    runAdaptableComparerFunction(columnId, columnValues) {
        let adaptable = this;
        return function compareItemsOfCustomSort(valueA, valueB, nodeA, nodeB) {
            var _a, _b;
            let firstElementValueString = (_a = (nodeA && adaptable.getDisplayValueFromRowNode(nodeA, columnId))) !== null && _a !== void 0 ? _a : valueA;
            let secondElementValueString = (_b = (nodeB && adaptable.getDisplayValueFromRowNode(nodeB, columnId))) !== null && _b !== void 0 ? _b : valueB;
            let indexFirstElement = columnValues.indexOf(firstElementValueString);
            let containsFirstElement = indexFirstElement >= 0;
            let indexSecondElement = columnValues.indexOf(secondElementValueString);
            let containsSecondElement = indexSecondElement >= 0;
            //if none of the element are in the list we jsut return normal compare
            if (!containsFirstElement && !containsSecondElement) {
                if (valueA == valueB) {
                    return 0;
                }
                return valueA < valueB ? -1 : 1;
            }
            //if first item not in the list make sure we put it after the second item
            if (!containsFirstElement) {
                return 1;
            }
            //if second item not in the list make sure we put it after the first item
            if (!containsSecondElement) {
                return -1;
            }
            //return the comparison from the list if the two items are in the list
            return indexFirstElement - indexSecondElement;
        };
    }
    isGridSelectable() {
        return (this.gridOptions.rowSelection === 'single' || this.gridOptions.rowSelection === 'multiple');
    }
    isGridRangeSelectable() {
        let isRangeSelectionModuleRegistered = this.isAgGridModulePresent(core_1.ModuleNames.RangeSelectionModule);
        return isRangeSelectionModuleRegistered && this.gridOptions.enableRangeSelection === true;
    }
    isGridGroupable() {
        const isTreeLayout = this.api.internalApi.isGridInTreeMode();
        return !isTreeLayout;
    }
    isGridGroupingActive() {
        let isGroupedActive = false;
        this.gridOptions.api.forEachNode((node) => {
            if (!isGroupedActive) {
                if (node.group) {
                    isGroupedActive = true;
                }
            }
        });
        return isGroupedActive;
    }
    isGridPivotable() {
        const isTreeLayout = this.api.internalApi.isGridInTreeMode();
        return !isTreeLayout;
    }
    isQuickFilterAvailable() {
        return this.hasFloatingFilterOnAtLeastOneColumn() === true;
    }
    setAgGridQuickSearch(searchText) {
        this.gridOptions.api.setQuickFilter(searchText);
    }
    hasFloatingFilterOnAtLeastOneColumn(defs = this.gridOptions.columnDefs) {
        if (this.gridOptions.defaultColDef && this.gridOptions.defaultColDef.floatingFilter) {
            return true;
        }
        let col;
        for (col of defs) {
            if (col.floatingFilter) {
                return true;
            }
            if (col.children) {
                if (this.hasFloatingFilterOnAtLeastOneColumn(col.children)) {
                    return true;
                }
            }
        }
        return false;
    }
    showQuickFilter() {
        const height = this.api.optionsApi.getColumnFilterOptions().quickFilterOptions.quickFilterHeight;
        this.gridOptions.api.setFloatingFiltersHeight(height);
    }
    hideQuickFilter() {
        this.gridOptions.api.setFloatingFiltersHeight(0);
    }
    getAgGridColumnType(columnId) {
        var _a;
        const { type } = (_a = this.gridOptions.api.getColumnDef(columnId)) !== null && _a !== void 0 ? _a : {};
        return type || '';
    }
    getActiveColumnComparator(columnId, customSort, customSortComparer) {
        if ((!customSort || (customSort === null || customSort === void 0 ? void 0 : customSort.IsSuspended)) && !customSortComparer) {
            // defaults to AG-Grid column definition comparator if no CustomSort is defined&active
            const colDefComparator = this.getUserColDefProperty(columnId, 'comparator');
            return colDefComparator;
        }
        // CustomSort Comparer function takes precedence over CustomSort SortedValues
        const comparerFunction = customSortComparer
            ? customSortComparer.comparer
            : this.runAdaptableComparerFunction(customSort.ColumnId, customSort.SortedValues);
        return comparerFunction;
    }
    getAgGridLightThemeName() {
        const container = this.getAgGridContainerElement();
        if (container && container.classList) {
            // we detect the ag theme class
            const classList = container.classList;
            for (let i = 0, len = classList.length; i < len; i++) {
                const cls = classList[i];
                if (cls.indexOf('ag-theme-') === 0) {
                    // even if dark theme is included, we compute the light theme name out of it
                    return cls.replace('-dark', '');
                }
            }
        }
        return this.agGridHelper.getAgGridLightThemeName();
    }
    getAgGridCurrentThemeName() {
        const container = this.getAgGridContainerElement();
        if (container && container.classList) {
            // we detect the ag theme class
            const classList = container.classList;
            for (let i = 0, len = classList.length; i < len; i++) {
                const cls = classList[i];
                if (cls.indexOf('ag-theme-') === 0) {
                    return cls;
                }
            }
        }
        return this.getAgGridLightThemeName();
    }
    applyAdaptableTheme(theme) {
        const themeName = typeof theme === 'string' ? theme : theme.Name;
        const isSystemTheme = this.api.themeApi.internalApi.isSystemTheme(themeName);
        const themeClassNamesToRemove = [];
        const themesToRemove = [];
        const allThemes = this.api.themeApi.getThemes().map((t) => {
            // we mutate the theme later,
            // and since we don't want the mutation to end up in state
            // we better clone it here
            return Object.assign({}, t);
        });
        const allThemesMap = allThemes.reduce((acc, theme) => {
            acc[theme.Name] = theme;
            return acc;
        }, {});
        const themeObject = allThemesMap[themeName];
        // REMOVE PREVIOUS THEME
        // const themePrefix = 'ab--theme-'
        const el = document.documentElement;
        el.classList.forEach((cssClassName) => {
            const index = cssClassName.indexOf(GeneralConstants.THEME_STYLE);
            if (index === 0) {
                themeClassNamesToRemove.push(cssClassName);
                const themeName = cssClassName.substring(GeneralConstants.THEME_STYLE.length);
                if (allThemesMap[themeName]) {
                    themesToRemove.push(allThemesMap[themeName]);
                }
            }
        });
        themeClassNamesToRemove.forEach((cssClassName) => el.classList.remove(cssClassName));
        // remove infinite table classnames
        themesToRemove.forEach((theme) => {
            el.classList.remove(`infinite-${theme.Name}`);
        });
        // VARIANT
        let variantTheme = '';
        if (!isSystemTheme && themeObject.Variant) {
            variantTheme = themeObject.Variant;
        }
        // APPLY NEW THEME
        const newTheme = allThemesMap[themeName];
        const getClassName = (theme) => GeneralConstants.THEME_STYLE + theme;
        el.classList.add(getClassName(themeName));
        if (variantTheme) {
            el.classList.add(getClassName(variantTheme));
        }
        if (isSystemTheme) {
            // add infinite table classname for theme
            el.classList.add(`infinite-${themeName}`);
        }
        else if (variantTheme) {
            el.classList.add(`infinite-${variantTheme}`);
        }
        // AG THEME CLASS NAME
        const container = this.getAgGridContainerElement();
        const getAgGridLightThemeName = () => this.getAgGridLightThemeName();
        const getAgGridDarkThemeName = () => getAgGridLightThemeName() + '-dark';
        if (newTheme && (isSystemTheme || variantTheme)) {
            if ((variantTheme || themeName) === GeneralConstants_1.LIGHT_THEME) {
                newTheme.AgGridClassName = newTheme.AgGridClassName || getAgGridLightThemeName();
            }
            if ((variantTheme || themeName) === GeneralConstants_1.DARK_THEME) {
                newTheme.AgGridClassName = newTheme.AgGridClassName || getAgGridDarkThemeName();
            }
        }
        if (!newTheme.AgGridClassName) {
            // default AG Grid to its light theme
            newTheme.AgGridClassName = getAgGridLightThemeName();
        }
        if (container != null) {
            if (themesToRemove.length) {
                themesToRemove.forEach((theme) => {
                    if (theme.AgGridClassName) {
                        container.classList.remove(theme.AgGridClassName);
                    }
                });
            }
            // also remove all AG Grid theme class names
            const agGridClassNamesToRemove = [];
            container.classList.forEach((x) => {
                if (x && x.indexOf('ag-theme-') === 0) {
                    agGridClassNamesToRemove.push(x);
                }
            });
            agGridClassNamesToRemove.forEach((x) => container.classList.remove(x));
            if (newTheme && newTheme.AgGridClassName) {
                container.classList.add(newTheme.AgGridClassName);
            }
            container.classList.add('ab-Grid');
            if (this.adaptableOptions.columnFilterOptions.indicateFilteredColumns) {
                container.classList.add('ab-Grid--indicate-filtered-columns');
            }
        }
        // MAC LIKE SCROLLBARS
        if (this.adaptableOptions.userInterfaceOptions &&
            this.adaptableOptions.userInterfaceOptions.useCustomMacLikeScrollbars &&
            (0, getScrollbarSize_1.default)() > 0) {
            el.classList.add('ab--custom-mac-like-scrollbars');
        }
        else {
            el.classList.remove('ab--custom-mac-like-scrollbars');
        }
    }
    setupRowStyling() {
        // Set any Row Styles (i.e. items without a classname)
        this.setGridOptionsProperty('getRowStyle', (userGetRowStyle) => {
            return (params) => {
                const result = Object.assign(Object.assign(Object.assign({}, userGetRowStyle === null || userGetRowStyle === void 0 ? void 0 : userGetRowStyle(params)), this.getRowHighlightStyle(params)), this.getAlertRowStyle(params));
                return result;
            };
        });
        // Now set up Row Classes
        this.setGridOptionsProperty('getRowClass', (userGetRowClass) => {
            return (params) => {
                const alertHighlightClassName = this.getAlertRowClass(params);
                const highlightClassName = this.getRowHighlightClass(params);
                const returnValue = [
                    typeof userGetRowClass === 'function' ? userGetRowClass(params) : userGetRowClass,
                    highlightClassName,
                    alertHighlightClassName,
                ]
                    // we flatten it because 'userGetRowClass' might return a string[]
                    .flat()
                    .filter((x) => !!x);
                return (returnValue === null || returnValue === void 0 ? void 0 : returnValue.length) ? returnValue : undefined;
            };
        });
    }
    setupColumnHeaderAggregations(currentLayout) {
        const previousValue = this.gridOptions.suppressAggFuncInHeader;
        this.setGridOptionsProperty('suppressAggFuncInHeader', (userSuppressAggFuncInHeader) => {
            if (currentLayout.SuppressAggFuncInHeader !== userSuppressAggFuncInHeader) {
                return currentLayout.SuppressAggFuncInHeader;
            }
        });
        const newValue = this.gridOptions.suppressAggFuncInHeader;
        return previousValue !== newValue;
    }
    getGridOptionsApi() {
        if (!this.gridOptions.api) {
            this.logger.consoleError('There is a problem with your instance of AG Grid - it has no gridApi object.  Please contact Support.');
            return;
        }
        return this.gridOptions.api;
    }
    canGenerateCharts() {
        return (this.isAgGridModulePresent(core_1.ModuleNames.GridChartsModule) && this.gridOptions.enableCharts);
    }
    canDisplaySparklines() {
        return this.isAgGridModulePresent(core_1.ModuleNames.SparklinesModule);
    }
    canExportToExcel() {
        return this.isAgGridModulePresent(core_1.ModuleNames.ExcelExportModule);
    }
    exportToExcel(reportData, fileName) {
        const columnDefs = reportData.columns.map((column) => ({
            field: column.columnId,
            headerName: column.friendlyName,
        }));
        const NESTED_COL_SEPARATOR = '.';
        const nestedColumns = reportData.columns.filter((column) => column.columnId.includes(NESTED_COL_SEPARATOR));
        if (nestedColumns.length) {
            // the rowData of nested fields (containing dots) was flattened during the report data extraction and we need to un-flatten it back
            // ex.
            // current state: { field: 'outerKey.innerKey', value: 2}
            // original (desired) state: { field: 'outerKey.innerKey', value: {outerKey: {innerKey: 2}}}
            reportData.rows.forEach((rowDataEntry) => nestedColumns.forEach((nestedColumn) => {
                const cellExportValue = rowDataEntry[nestedColumn.columnId];
                let intermediaryValue = rowDataEntry;
                // augment the rowDataEntry with the new object
                nestedColumn.columnId
                    .split(NESTED_COL_SEPARATOR)
                    .forEach((nestedKey, index, nestedCols) => {
                    // we reached the leaf node, so we set the cell value and break
                    if (index === nestedCols.length - 1) {
                        intermediaryValue[nestedKey] = cellExportValue;
                        return;
                    }
                    if (intermediaryValue[nestedKey] == undefined) {
                        intermediaryValue[nestedKey] = {};
                    } // else means that the object already has a property with this key
                    intermediaryValue = intermediaryValue[nestedKey];
                });
                // delete the obsolete flattened property
                delete rowDataEntry[nestedColumn.columnId];
            }));
        }
        const gridOptions = {
            columnDefs,
            rowData: reportData.rows,
        };
        let gridParams = { modules: this.getAgGridRegisteredModules() };
        const grid = new core_1.Grid(document.createElement('div'), gridOptions, gridParams);
        gridOptions.api.exportDataAsExcel({
            sheetName: 'Sheet 1',
            fileName: fileName,
        });
        grid.destroy();
    }
    setExcelStylesForExport() {
        // we memoize as much as possible, as this is called quite A LOT
        const adaptableColumnMap = {};
        const getAdaptableColumnWithColumnId = (columnId) => {
            const memoizedColumn = adaptableColumnMap[columnId];
            if (memoizedColumn) {
                return memoizedColumn;
            }
            const abColumn = this.api.columnApi.getColumnWithColumnId(columnId);
            adaptableColumnMap[columnId] = abColumn;
            return abColumn;
        };
        const formatColumnsWithDisplayFormatForColumn = {};
        const getFormatColumnsWithDisplayFormatForColumn = (columnId) => {
            const memoizedFormatColumns = formatColumnsWithDisplayFormatForColumn[columnId];
            if (memoizedFormatColumns) {
                return memoizedFormatColumns;
            }
            const abColumn = getAdaptableColumnWithColumnId(columnId);
            const formatColumns = this.api.formatColumnApi.internalApi.getFormatColumnsWithDisplayFormatForColumn(abColumn);
            formatColumnsWithDisplayFormatForColumn[columnId] = formatColumns;
            return formatColumns;
        };
        const isDateCellExportedAsFormattedValue = this.api.exportApi.internalApi.isDateCellExportedAsFormattedValue();
        this.setGridOptionsProperty('excelStyles', (userExcelStyles = []) => {
            const displayedColumns = this.gridOptions.columnApi.getAllDisplayedColumns();
            const colDefs = displayedColumns.map((column) => {
                return column.getColDef();
            });
            const forAllVisibleRowNodesDoConfig = { includeGroupRows: true };
            this.forAllVisibleRowNodesDo((node, rowIndex) => {
                const rowParams = {
                    node,
                    data: node.data,
                    rowIndex,
                    $scope: {},
                    api: this.gridOptions.api,
                    columnApi: this.gridOptions.columnApi,
                    context: {},
                };
                const rowStyle = this.gridOptions.getRowStyle(rowParams);
                displayedColumns.forEach((column, columnIndex) => {
                    var _a, _b, _c;
                    const colDef = colDefs[columnIndex];
                    let cellClassParams;
                    const getLazyCellClassParams = () => {
                        if (!cellClassParams) {
                            cellClassParams = {
                                colDef,
                                node,
                                column,
                                data: node.data,
                                value: this.getRawValueFromRowNode(node, column.getId()),
                                rowIndex,
                                api: this.gridOptions.api,
                                columnApi: this.gridOptions.columnApi,
                                context: {},
                            };
                        }
                        return cellClassParams;
                    };
                    const cellStyle = typeof colDef.cellStyle === 'function'
                        ? colDef.cellStyle(getLazyCellClassParams())
                        : {};
                    const excelStyles = [];
                    // add user defined excel styles
                    let userColDefCellClass = this.getUserColDefProperty(column.getColId(), 'cellClass');
                    const userDefinedCellClass = typeof userColDefCellClass === 'function'
                        ? userColDefCellClass(getLazyCellClassParams())
                        : userColDefCellClass;
                    const userDefinedExcelStyle = userDefinedCellClass &&
                        userExcelStyles.find((excelStyle) => {
                            var _a;
                            return typeof userDefinedCellClass === 'string'
                                ? userDefinedCellClass === excelStyle.id
                                : (_a = userDefinedCellClass === null || userDefinedCellClass === void 0 ? void 0 : userDefinedCellClass.includes) === null || _a === void 0 ? void 0 : _a.call(userDefinedCellClass, excelStyle.id);
                        });
                    if (userDefinedExcelStyle) {
                        excelStyles.push(userDefinedExcelStyle);
                    }
                    // add adaptable derived styles (format column etc.)
                    const adaptableStyle = Object.assign(Object.assign({}, rowStyle), Object.keys(cellStyle).reduce((result, key) => {
                        if (cellStyle[key] !== null) {
                            result[key] = cellStyle[key];
                        }
                        return result;
                    }, {}));
                    const sanitizedAdaptableStyle = (0, StyleHelper_1.sanitizeStyle)(adaptableStyle);
                    if (Object.values(sanitizedAdaptableStyle).some((style) => style != null)) {
                        excelStyles.push(this.convertCSSToExcelStyle(sanitizedAdaptableStyle));
                    }
                    const excelDataType = this.api.exportApi.internalApi.getExcelDataType(colDef === null || colDef === void 0 ? void 0 : colDef.type);
                    const rawValue = this.getRawValueFromRowNode(node, column.getId());
                    // don't add the cell style if it has no adaptable custom styles
                    if (!excelStyles.length &&
                        // if this is a formatted Date value, we still need to add the AG GRID specific type & numberFormat below
                        !(excelDataType === 'DateTime' && isDateCellExportedAsFormattedValue)) {
                        return;
                    }
                    const cellClassId = this.getExcelClassNameForCell(column.getId(), this.getPrimaryKeyValueFromRowNode(node), userDefinedCellClass);
                    const finalCellExcelStyle = Object.assign({}, ...excelStyles);
                    if (excelDataType === 'DateTime' && isDateCellExportedAsFormattedValue) {
                        let dateFormatPattern = this.api.optionsApi.getExportOptions().exportDateFormat;
                        const abColumn = getAdaptableColumnWithColumnId(column.getColId());
                        if (!dateFormatPattern) {
                            const mostRelevantFormatColumn = this.api.formatColumnApi.internalApi.getMostRelevantFormatColumnForColumn(getFormatColumnsWithDisplayFormatForColumn(column.getColId()), abColumn, { node, value: rawValue });
                            dateFormatPattern =
                                ((_a = mostRelevantFormatColumn === null || mostRelevantFormatColumn === void 0 ? void 0 : mostRelevantFormatColumn.DisplayFormat) === null || _a === void 0 ? void 0 : _a.Formatter) === 'DateFormatter' &&
                                    ((_c = (_b = mostRelevantFormatColumn === null || mostRelevantFormatColumn === void 0 ? void 0 : mostRelevantFormatColumn.DisplayFormat) === null || _b === void 0 ? void 0 : _b.Options) === null || _c === void 0 ? void 0 : _c.Pattern);
                        }
                        if (dateFormatPattern) {
                            const normalisedValue = this.getNormalisedValueFromRawValue(rawValue, abColumn);
                            if (normalisedValue) {
                                // we have to pass the date in the ISO format to Excel
                                // see https://www.ag-grid.com/javascript-data-grid/excel-export-data-types/#dates
                                // we can NOT use Date.toISOString() because we don't want the timezone corrections to kick in
                                const isoFormattedValue = (0, FormatHelper_1.DateFormatter)(normalisedValue, {
                                    Pattern: `yyyy-MM-dd'T'HH:mm:ss.SSS`,
                                });
                                if (isoFormattedValue) {
                                    finalCellExcelStyle.dataType = 'DateTime';
                                    finalCellExcelStyle.numberFormat = { format: dateFormatPattern };
                                    // create a new cell key to ensure any user provided className does not interfere
                                    const cellKey = this.getExcelClassNameForCell(column.getColId(), this.getPrimaryKeyValueFromRowNode(node));
                                    // we need to register so that later the cellProcessor will put the isoFormattedValue through (thus giving the formatting responsability to Excel)
                                    this.api.internalApi
                                        .getReportService()
                                        .registerExcelStyleWithFormattedDate(cellKey, isoFormattedValue);
                                }
                            }
                        }
                    }
                    this.api.internalApi
                        .getReportService()
                        .registerExcelStyle(finalCellExcelStyle, cellClassId);
                });
            }, forAllVisibleRowNodesDoConfig);
            return this.api.internalApi.getReportService().getRegisteredExcelStyles();
        });
    }
    exportVisualDataToExcel() {
        // add adaptable style props to user defined props
        this.setExcelStylesForExport();
        this.gridOptions.api.exportDataAsExcel({
            sheetName: 'Sheet 1',
            fileName: this.ReportService.getReportFileName(this.adaptableOptions.adaptableId, 'Excel'),
            // delegate the cell value processing to Adaptable
            processCellCallback: ({ node, column, value }) => {
                var _a;
                const columnId = column.getColId();
                if ((node === null || node === void 0 ? void 0 : node.group) &&
                    (this.api.columnApi.isAutoRowGroupColumn(columnId) ||
                        // we would still need to process the cell if this is a group row with an aggregated value
                        ((_a = node === null || node === void 0 ? void 0 : node.aggData) === null || _a === void 0 ? void 0 : _a[columnId]) == undefined)) {
                    // skip processing of row groups, this was already handled in processRowGroupCallback()
                    return value;
                }
                return this.processCellForExcelExport(node, columnId);
            },
            processRowGroupCallback: (params) => {
                var _a;
                // recreating the standard AG Grid styling for row groups: 'Parent -> Child'
                // additionally the values are formatted
                let rowGroupNode = params.node;
                const isFooterRow = rowGroupNode.footer;
                const rowGroupSummary = [(_a = this.processRowGroupForExcelExport(rowGroupNode)) !== null && _a !== void 0 ? _a : ''];
                while (rowGroupNode.parent) {
                    rowGroupNode = rowGroupNode.parent;
                    const formattedParentNode = this.processRowGroupForExcelExport(rowGroupNode);
                    if (formattedParentNode) {
                        rowGroupSummary.push(formattedParentNode);
                    }
                }
                let summary = rowGroupSummary.reverse().join(' -> ');
                if (isFooterRow) {
                    summary = `Total: ${summary}`;
                }
                return summary;
            },
        });
        // keep only the user defined props
        this.setGridOptionsProperty('excelStyles', (userExcelStyles) => {
            return userExcelStyles;
        });
    }
    convertAdaptableStyleToCSS(style) {
        return (0, StyleHelper_1.convertAdaptableStyleToCSS)(style);
    }
    convertCSSToExcelStyle(style) {
        const getHexColor = (color) => {
            const preparedColor = (0, StyleHelper_1.getVariableColor)(color);
            const t = tinycolor(preparedColor);
            const a = t.getAlpha();
            return tinycolor.mix(tinycolor('white'), t, a * 100).toHexString();
        };
        let result = {};
        if (style.backgroundColor != null) {
            result.interior = {
                color: getHexColor(style.backgroundColor),
                pattern: 'Solid',
            };
        }
        if (style.borderColor != null) {
            const excelBorder = {
                color: style.borderColor,
                lineStyle: 'Continuous',
                weight: 1,
            };
            result.borders = {
                borderBottom: excelBorder,
                borderLeft: excelBorder,
                borderRight: excelBorder,
                borderTop: excelBorder,
            };
        }
        if (style.textAlign) {
            result.alignment = {
                horizontal: StringExtensions_1.StringExtensions.CapitaliseFirstLetter(style.textAlign),
            };
        }
        if (style.color != null) {
            if (!result.font) {
                result.font = {};
            }
            result.font = {
                color: getHexColor(style.color),
            };
        }
        if (style.fontStyle === 'italic') {
            if (!result.font) {
                result.font = {};
            }
            result.font.italic = true;
        }
        if (style.fontWeight != null &&
            (style.fontWeight === 'bold' || Number(style.fontWeight) >= 600)) {
            if (!result.font) {
                result.font = {};
            }
            result.font.bold = true;
        }
        if (style.fontSize != null) {
            if (!result.font) {
                result.font = {};
            }
            result.font.size = (0, StyleHelper_1.convertCSSAbsoluteFontSizeToPt)(style.fontSize);
        }
        return result;
    }
    processCellForExcelExport(rowNode, columnId) {
        if (this.api.exportApi.internalApi.isVisualDataExportInProgress()) {
            const cellKey = this.getExcelClassNameForCell(columnId, this.getPrimaryKeyValueFromRowNode(rowNode));
            const isoFormattedDate = this.ReportService.getExcelStyleWithFormattedDate(cellKey);
            if (isoFormattedDate) {
                // this is a Date cell which will be formatted by Excel
                return isoFormattedDate;
            }
        }
        return this.ReportService.getCellExportValueFromRowNode(rowNode, columnId);
    }
    processRowGroupForExcelExport(rowNode) {
        var _a, _b, _c, _d, _e;
        const columnId = (_c = (_a = rowNode.field) !== null && _a !== void 0 ? _a : (_b = rowNode.rowGroupColumn) === null || _b === void 0 ? void 0 : _b.getColId()) !== null && _c !== void 0 ? _c : (_e = (_d = rowNode.rowGroupColumn) === null || _d === void 0 ? void 0 : _d.getColDef()) === null || _e === void 0 ? void 0 : _e.field;
        if (!columnId || !rowNode.key) {
            return;
        }
        let rawValue = rowNode.key;
        if (this.api.columnApi.getColumnDataTypeForColumnId(columnId) === 'Date' &&
            typeof rawValue === 'string' &&
            // rawValue is composed only of digits
            /^\d+$/.test(rawValue)) {
            // AG-Grid converts the value to string, we have to reconvert it back
            const dateRawValue = parseInt(rawValue);
            if (dateRawValue != undefined) {
                // @ts-ignore
                rawValue = dateRawValue;
            }
        }
        return this.ReportService.getCellExportValueFromRawValue(rowNode, rawValue, columnId);
    }
    hasAutogeneratedPrimaryKey() {
        return this.adaptableOptions.autogeneratePrimaryKey;
    }
    addSyntheticPrimaryKey(rowData = []) {
        for (let i = 0; i < rowData.length; i++) {
            rowData[i][GeneralConstants_1.AUTOGENERATED_PK_COLUMN] = (0, Uuid_1.createUuid)();
        }
    }
    // add a synthetic PK only if missing (useful in case of updating row data)
    addSyntheticPrimaryKeyIfMissing(rowData = []) {
        this.adaptableOptions.primaryKey = GeneralConstants_1.AUTOGENERATED_PK_COLUMN;
        for (let i = 0; i < rowData.length; i++) {
            if (!rowData[i][GeneralConstants_1.AUTOGENERATED_PK_COLUMN]) {
                rowData[i][GeneralConstants_1.AUTOGENERATED_PK_COLUMN] = (0, Uuid_1.createUuid)();
            }
        }
    }
    initLicenseService() {
        const globalObject = typeof globalThis !== 'undefined' ? globalThis : window;
        const licenseKey = globalObject.ADAPTABLE_LICENSE_KEY || this.api.optionsApi.getLicenseKey();
        return new LicenseService_1.LicenseService(this, licenseKey, {
            publishedAt: publishTimestamp_1.default,
        });
    }
    showCharts(chartsDefinitions, chartContainer) {
        return chartsDefinitions.map((chartDefinition) => this.showChart(chartDefinition, chartContainer));
    }
    showChart(chartDefinition, container) {
        if (!this.gridOptions.api) {
            this.logger.consoleError('Adaptable must be instantiated before calling showChart');
            return null;
        }
        /**
         * Ag-grid always creates new charts behind the sences.
         * So we need to update the model inside adaptable state.
         * This is important to be done as soon as possible so it is not considered new.
         */
        const chartRef = this.gridOptions.api.restoreChart(chartDefinition.Model, container);
        const chartModel = this.getChartModels().find((chartModel) => chartModel.chartId === chartRef.chartId);
        // Update the definition in state so it is not considered new
        this.api.chartingApi.editChartDefinition(Object.assign(Object.assign({}, chartDefinition), { Model: chartModel }));
        return chartRef;
    }
    updateChart(chart) {
        const upgradableProperties = {
            // Only Range charts are supported to be created at run time
            // the other two are pivot & cross-filter
            type: 'rangeChartUpdate',
            chartId: chart.Model.chartId,
            unlinkChart: Boolean(chart.Model.unlinkChart),
            suppressChartRanges: Boolean(chart.Model.suppressChartRanges),
            aggFunc: chart.Model.aggFunc,
        };
        this.gridOptions.api.updateChart(upgradableProperties);
    }
    getChartRef(chartId) {
        return this.gridOptions.api.getChartRef(chartId);
    }
    getChartModels() {
        if (!this.gridOptions.api) {
            this.logger.consoleError('Adaptable must be instantiated before calling getChartModels');
            return [];
        }
        return this.gridOptions.api.getChartModels();
    }
    getAgGridRowModelType() {
        var _a;
        // it seems that this can be null so we need explicitly to return "clientSide" in this case
        // need to check that for ServerSideRowModel it is ALWAYS returned...
        return (_a = this.gridOptions.rowModelType) !== null && _a !== void 0 ? _a : 'clientSide';
    }
    getActiveAdaptableAggFuncForCol(columnId) {
        if (!columnId) {
            return null;
        }
        const currentLayout = this.api.layoutApi.getCurrentLayout();
        const aggregationFunctionsColumnsMap = currentLayout.AggregationColumns || {};
        const adaptableAggFunc = aggregationFunctionsColumnsMap[columnId];
        if (typeof adaptableAggFunc === 'object' &&
            'type' in adaptableAggFunc &&
            adaptableAggFunc.type === 'weightedAverage') {
            return adaptableAggFunc;
        }
        return null;
    }
    registerAdaptableAggFuncs() {
        this.setGridOptionsProperty('aggFuncs', (originalAggFuncs) => {
            const aggregationFunctions = originalAggFuncs || {};
            aggregationFunctions[AggregationColumns_1.WEIGHTED_AVERAGE_AGG_FN_NAME] = (params) => {
                const columnId = params.column.getColId();
                const adaptableAggFunc = this.getActiveAdaptableAggFuncForCol(columnId);
                if (!adaptableAggFunc) {
                    return undefined;
                }
                if (adaptableAggFunc.type === 'weightedAverage') {
                    return (0, weightedAverage_1.weightedAverage)(params, params.colDef.colId, adaptableAggFunc.weightedColumnId);
                }
                return undefined;
            };
            return aggregationFunctions;
        });
    }
    /**
     * When reading the state from the grid, we have to make sure 'avg' is not overriden with the 'avg' string.
     */
    mapAggregationColumnsFromGrid(aggFuncFromGrid, currentLayout) {
        return Object.entries(aggFuncFromGrid).reduce((acc, [colId, agg]) => {
            const adaptableAggFunc = this.getActiveAdaptableAggFuncForCol(colId);
            if ((adaptableAggFunc === null || adaptableAggFunc === void 0 ? void 0 : adaptableAggFunc.type) === 'weightedAverage' && agg === AggregationColumns_1.WEIGHTED_AVERAGE_AGG_FN_NAME) {
                // do not override the agg func
                acc[colId] = currentLayout.AggregationColumns[colId];
            }
            else {
                acc[colId] = agg;
            }
            return acc;
        }, {});
    }
    resetMinMaxCachedValueForColumn(column) {
        if (!column) {
            this.columnMinMaxValuesCache[column.columnId] = {};
        }
        if (this.columnMinMaxValuesCache[column.columnId]) {
            this.columnMinMaxValuesCache[column.columnId] = undefined;
        }
    }
    getMinMaxCachedValueForColumn(column, minMax) {
        var _a;
        const { columnId, dataType } = column;
        if (dataType !== 'Number') {
            return undefined;
        }
        let value = (_a = this.columnMinMaxValuesCache[columnId]) === null || _a === void 0 ? void 0 : _a[minMax];
        if (value !== undefined) {
            return value;
        }
        const distinctRawValues = this.api.gridApi.internalApi
            .getUnsortedDistinctRawValuesForColumn(columnId)
            .map((item) => item.rawValue);
        value = minMax === 'min' ? Math.min(...distinctRawValues) : Math.max(...distinctRawValues);
        this.columnMinMaxValuesCache[columnId] = Object.assign(Object.assign({}, this.columnMinMaxValuesCache[columnId]), { [minMax]: value });
        return value;
    }
    persistColumnIndexBeforeGrouping(params) {
        const layout = this.api.layoutApi.getCurrentLayout();
        const columnGroupsInLayout = layout.RowGroupedColumns;
        const columnGroupsInGrid = params.columns.map((col) => col.getColId());
        // what is new
        const newGroups = columnGroupsInGrid.filter((colId) => !columnGroupsInLayout.includes(colId));
        newGroups.forEach((colId) => {
            const columnIndex = layout.Columns.filter((colId) => !(this.api.columnApi.isAutoRowGroupColumn(colId) ||
                this.api.columnApi.isAutoPivotColumn(colId))).findIndex((columnIdInLayout) => columnIdInLayout === colId);
            // user may group after a column not in layout
            if (columnIndex !== -1) {
                this.api.internalApi.persistPreviousGroupedColumnsIndex(layout.Uuid, colId, columnIndex);
            }
        });
    }
    /**
     * Restores the order previous grouping order.
  
     */
    restoreUnGroupColumnOrder({ newGroupColumns, columnOrder, }) {
        var _a;
        const newColumnOrder = [...columnOrder];
        const currentLayout = this.api.layoutApi.getCurrentLayout();
        const previousGroupedColumnsIndex = this.api.internalApi.getPreviousGroupedColumnsIndex(currentLayout.Uuid);
        const isUnGroup = newGroupColumns.length < ((_a = currentLayout === null || currentLayout === void 0 ? void 0 : currentLayout.RowGroupedColumns) === null || _a === void 0 ? void 0 : _a.length);
        if (!isUnGroup) {
            return columnOrder;
        }
        if (!previousGroupedColumnsIndex) {
            return columnOrder;
        }
        for (let [colId, previousIndex] of Object.entries(previousGroupedColumnsIndex)) {
            const isStillGrouped = newGroupColumns.includes(colId);
            const isAlreadyInGrid = currentLayout.Columns.includes(colId); // was not just added by ungrouping
            if (isStillGrouped) {
                continue;
            }
            if (isAlreadyInGrid) {
                // no longer grouped, but already in grid, this means it was already in grid before ungrouping
                // in this case the state can be cleared
                this.api.internalApi.persistPreviousGroupedColumnsIndex(currentLayout.Uuid, colId, null);
                continue;
            }
            // need to ajust index based if grouped
            const numberOfGroupedColumns = newColumnOrder.filter((colId) => this.api.columnApi.isAutoRowGroupColumn(colId)).length;
            const adjustedPreviousIndex = previousIndex + numberOfGroupedColumns;
            const indexInGrid = newColumnOrder.indexOf(colId);
            const hasDifferentPositionAsPreviousLayout = adjustedPreviousIndex !== null && // if null, the reorder was already applied
                indexInGrid > 0 && // needs to be in grid
                adjustedPreviousIndex !== indexInGrid;
            const isPreviousPositionInRange = adjustedPreviousIndex < newColumnOrder.length;
            if (hasDifferentPositionAsPreviousLayout && isPreviousPositionInRange) {
                newColumnOrder.splice(indexInGrid, 1);
                newColumnOrder.splice(adjustedPreviousIndex, 0, colId);
            }
            this.api.internalApi.persistPreviousGroupedColumnsIndex(currentLayout.Uuid, colId, null);
        }
        return newColumnOrder;
    }
}
exports.Adaptable = Adaptable;
class AdaptableNoCodeWizard {
    /**
     * @param adaptableOptions
     */
    constructor(adaptableOptions, extraOptions = {}, agGridOptons) {
        const defaultInit = async ({ gridOptions, adaptableOptions, agGridModules, }) => {
            return await Adaptable.init(adaptableOptions, { gridOptions, modules: agGridModules });
        };
        this.adaptableOptions = adaptableOptions;
        this.init = extraOptions.onInit || defaultInit;
        this.extraOptions = extraOptions;
        this.agGridModules = core_1.ModuleRegistry.__getRegisteredModules(agGridOptons === null || agGridOptons === void 0 ? void 0 : agGridOptons.gridOptions.gridId);
        this.render();
    }
    render(container) {
        var _a;
        let id = DefaultAdaptableOptions_1.DefaultAdaptableOptions.containerOptions.adaptableContainer || 'adaptable';
        if (!container) {
            let containerOptionsContainer = (_a = this.adaptableOptions.containerOptions) === null || _a === void 0 ? void 0 : _a.adaptableContainer;
            if (containerOptionsContainer) {
                if (typeof containerOptionsContainer === 'string') {
                    id = containerOptionsContainer !== null && containerOptionsContainer !== void 0 ? containerOptionsContainer : id;
                }
                else {
                    container = containerOptionsContainer;
                }
            }
        }
        container = container || document.getElementById(id);
        if (!container) {
            throw new Error('Cannot find container in which to render Adaptable No Code Wizard');
        }
        // this allows people to customize the wizard dimensions & styling
        // when it's visible
        container.classList.add('adaptable--in-wizard');
        const unmount = (0, renderReactRoot_1.renderReactRoot)(React.createElement(AdaptableWizardView_1.default, Object.assign(Object.assign({ adaptableOptions: this.adaptableOptions }, this.extraOptions), { onInit: (adaptableOptions, gridOptions) => {
                container.classList.remove('adaptable--in-wizard');
                unmount();
                this.init({
                    adaptableOptions,
                    gridOptions: gridOptions,
                    agGridModules: [...this.agGridModules],
                });
            } })), container);
    }
}
exports.AdaptableNoCodeWizard = AdaptableNoCodeWizard;
